import type { IconSvgObject } from './types';

/**
 * Dynamic Icon Loader - stroke-rounded (5439 icons)
 * 
 * Load icons dynamically by name without importing all icons upfront.
 * Uses static import maps for maximum bundler compatibility.
 * 
 * @example
 * import { loadIcon } from '@hugeicons-pro/core-stroke-rounded/loader';
 * 
 * const icon = await loadIcon('Home01Icon');
 * // Use icon with @hugeicons/react or any other renderer
 */

// Static import map for code-splitting (Vite/Webpack)
const imports: Record<string, () => Promise<{ default: IconSvgObject }>> = {
  FirstBracketCircleIcon: () => import('./FirstBracketCircleIcon.js'),
  FirstBracketSquareIcon: () => import('./FirstBracketSquareIcon.js'),
  FirstBracketIcon: () => import('./FirstBracketIcon.js'),
  TwentyFourHoursClockIcon: () => import('./TwentyFourHoursClockIcon.js'),
  SecondBracketCircleIcon: () => import('./SecondBracketCircleIcon.js'),
  SecondBracketSquareIcon: () => import('./SecondBracketSquareIcon.js'),
  SecondBracketIcon: () => import('./SecondBracketIcon.js'),
  ThreeDViewIcon: () => import('./ThreeDViewIcon.js'),
  ThreeDMoveIcon: () => import('./ThreeDMoveIcon.js'),
  ThreeDPrinterIcon: () => import('./ThreeDPrinterIcon.js'),
  ThreeDRotateIcon: () => import('./ThreeDRotateIcon.js'),
  ThreeDScaleIcon: () => import('./ThreeDScaleIcon.js'),
  ThirdBracketCircleIcon: () => import('./ThirdBracketCircleIcon.js'),
  ThirdBracketSquareIcon: () => import('./ThirdBracketSquareIcon.js'),
  ThirdBracketIcon: () => import('./ThirdBracketIcon.js'),
  FourKIcon: () => import('./FourKIcon.js'),
  SevenZ01Icon: () => import('./SevenZ01Icon.js'),
  SevenZ02Icon: () => import('./SevenZ02Icon.js'),
  AArrowDownIcon: () => import('./AArrowDownIcon.js'),
  AArrowUpIcon: () => import('./AArrowUpIcon.js'),
  ALargeSmallIcon: () => import('./ALargeSmallIcon.js'),
  AbacusIcon: () => import('./AbacusIcon.js'),
  AbsoluteIcon: () => import('./AbsoluteIcon.js'),
  AccelerationIcon: () => import('./AccelerationIcon.js'),
  AccessIcon: () => import('./AccessIcon.js'),
  AccessibilityIcon: () => import('./AccessibilityIcon.js'),
  AccidentIcon: () => import('./AccidentIcon.js'),
  AccountRecoveryIcon: () => import('./AccountRecoveryIcon.js'),
  AccountSetting01Icon: () => import('./AccountSetting01Icon.js'),
  AccountSetting02Icon: () => import('./AccountSetting02Icon.js'),
  AccountSetting03Icon: () => import('./AccountSetting03Icon.js'),
  Activity01Icon: () => import('./Activity01Icon.js'),
  Activity02Icon: () => import('./Activity02Icon.js'),
  Activity03Icon: () => import('./Activity03Icon.js'),
  Activity04Icon: () => import('./Activity04Icon.js'),
  Activity05Icon: () => import('./Activity05Icon.js'),
  ActivityCircleIcon: () => import('./ActivityCircleIcon.js'),
  ActivitySparkIcon: () => import('./ActivitySparkIcon.js'),
  AcuteIcon: () => import('./AcuteIcon.js'),
  Add01Icon: () => import('./Add01Icon.js'),
  Add02Icon: () => import('./Add02Icon.js'),
  AddCircleHalfDotIcon: () => import('./AddCircleHalfDotIcon.js'),
  AddCircleIcon: () => import('./AddCircleIcon.js'),
  AddFemaleIcon: () => import('./AddFemaleIcon.js'),
  AddInvoiceIcon: () => import('./AddInvoiceIcon.js'),
  AddMaleIcon: () => import('./AddMaleIcon.js'),
  AddMoneyCircleIcon: () => import('./AddMoneyCircleIcon.js'),
  AddSquareIcon: () => import('./AddSquareIcon.js'),
  AddTeam02Icon: () => import('./AddTeam02Icon.js'),
  AddTeamIcon: () => import('./AddTeamIcon.js'),
  AddToListIcon: () => import('./AddToListIcon.js'),
  AddressBookIcon: () => import('./AddressBookIcon.js'),
  AdjustPositionIcon: () => import('./AdjustPositionIcon.js'),
  AdobeAfterEffectIcon: () => import('./AdobeAfterEffectIcon.js'),
  AdobeIllustratorIcon: () => import('./AdobeIllustratorIcon.js'),
  AdobeIndesignIcon: () => import('./AdobeIndesignIcon.js'),
  AdobePhotoshopIcon: () => import('./AdobePhotoshopIcon.js'),
  AdobePremierIcon: () => import('./AdobePremierIcon.js'),
  AdobeXdIcon: () => import('./AdobeXdIcon.js'),
  AdventureIcon: () => import('./AdventureIcon.js'),
  AdvertisimentIcon: () => import('./AdvertisimentIcon.js'),
  AdzanIcon: () => import('./AdzanIcon.js'),
  AffiliateIcon: () => import('./AffiliateIcon.js'),
  Agreement01Icon: () => import('./Agreement01Icon.js'),
  Agreement02Icon: () => import('./Agreement02Icon.js'),
  Agreement03Icon: () => import('./Agreement03Icon.js'),
  AiAudioIcon: () => import('./AiAudioIcon.js'),
  AiBeautifyIcon: () => import('./AiBeautifyIcon.js'),
  AiBookIcon: () => import('./AiBookIcon.js'),
  AiBrain01Icon: () => import('./AiBrain01Icon.js'),
  AiBrain02Icon: () => import('./AiBrain02Icon.js'),
  AiBrain03Icon: () => import('./AiBrain03Icon.js'),
  AiBrain04Icon: () => import('./AiBrain04Icon.js'),
  AiBrain05Icon: () => import('./AiBrain05Icon.js'),
  AiBrowserIcon: () => import('./AiBrowserIcon.js'),
  AiChat01Icon: () => import('./AiChat01Icon.js'),
  AiChat02Icon: () => import('./AiChat02Icon.js'),
  AiChemistry01Icon: () => import('./AiChemistry01Icon.js'),
  AiChemistry02Icon: () => import('./AiChemistry02Icon.js'),
  AiChemistry03Icon: () => import('./AiChemistry03Icon.js'),
  AiChipIcon: () => import('./AiChipIcon.js'),
  AiCloud01Icon: () => import('./AiCloud01Icon.js'),
  AiCloud02Icon: () => import('./AiCloud02Icon.js'),
  AiCloudIcon: () => import('./AiCloudIcon.js'),
  AiComputerIcon: () => import('./AiComputerIcon.js'),
  AiContentGenerator01Icon: () => import('./AiContentGenerator01Icon.js'),
  AiContentGenerator02Icon: () => import('./AiContentGenerator02Icon.js'),
  AiDnaIcon: () => import('./AiDnaIcon.js'),
  AiEditingIcon: () => import('./AiEditingIcon.js'),
  AiEraserIcon: () => import('./AiEraserIcon.js'),
  AiFileIcon: () => import('./AiFileIcon.js'),
  AiFolder01Icon: () => import('./AiFolder01Icon.js'),
  AiFolder02Icon: () => import('./AiFolder02Icon.js'),
  AiGameIcon: () => import('./AiGameIcon.js'),
  AiGenerativeIcon: () => import('./AiGenerativeIcon.js'),
  AiIdeaIcon: () => import('./AiIdeaIcon.js'),
  AiImageIcon: () => import('./AiImageIcon.js'),
  AiInnovation01Icon: () => import('./AiInnovation01Icon.js'),
  AiInnovation02Icon: () => import('./AiInnovation02Icon.js'),
  AiInnovation03Icon: () => import('./AiInnovation03Icon.js'),
  AiLaptopIcon: () => import('./AiLaptopIcon.js'),
  AiLearningIcon: () => import('./AiLearningIcon.js'),
  AiLockIcon: () => import('./AiLockIcon.js'),
  AiMagicIcon: () => import('./AiMagicIcon.js'),
  AiMail01Icon: () => import('./AiMail01Icon.js'),
  AiMail02Icon: () => import('./AiMail02Icon.js'),
  AiMailIcon: () => import('./AiMailIcon.js'),
  AiMicIcon: () => import('./AiMicIcon.js'),
  AiNetworkIcon: () => import('./AiNetworkIcon.js'),
  AiPhone01Icon: () => import('./AiPhone01Icon.js'),
  AiPhone02Icon: () => import('./AiPhone02Icon.js'),
  AiProgrammingIcon: () => import('./AiProgrammingIcon.js'),
  AiScanIcon: () => import('./AiScanIcon.js'),
  AiSchedulingIcon: () => import('./AiSchedulingIcon.js'),
  AiSearch02Icon: () => import('./AiSearch02Icon.js'),
  AiSearchIcon: () => import('./AiSearchIcon.js'),
  AiSecurity01Icon: () => import('./AiSecurity01Icon.js'),
  AiSecurity02Icon: () => import('./AiSecurity02Icon.js'),
  AiSecurity03Icon: () => import('./AiSecurity03Icon.js'),
  AiSettingIcon: () => import('./AiSettingIcon.js'),
  AiSheetsIcon: () => import('./AiSheetsIcon.js'),
  AiSmartwatchIcon: () => import('./AiSmartwatchIcon.js'),
  AiUserIcon: () => import('./AiUserIcon.js'),
  AiVideoIcon: () => import('./AiVideoIcon.js'),
  AiViewIcon: () => import('./AiViewIcon.js'),
  AiVisionRecognitionIcon: () => import('./AiVisionRecognitionIcon.js'),
  AiVoiceGeneratorIcon: () => import('./AiVoiceGeneratorIcon.js'),
  AiVoiceIcon: () => import('./AiVoiceIcon.js'),
  AiWebBrowsingIcon: () => import('./AiWebBrowsingIcon.js'),
  AidsIcon: () => import('./AidsIcon.js'),
  AirVentIcon: () => import('./AirVentIcon.js'),
  AirbnbIcon: () => import('./AirbnbIcon.js'),
  AircraftGameIcon: () => import('./AircraftGameIcon.js'),
  AirdropIcon: () => import('./AirdropIcon.js'),
  Airplane01Icon: () => import('./Airplane01Icon.js'),
  Airplane02Icon: () => import('./Airplane02Icon.js'),
  AirplaneLanding01Icon: () => import('./AirplaneLanding01Icon.js'),
  AirplaneLanding02Icon: () => import('./AirplaneLanding02Icon.js'),
  AirplaneModeOffIcon: () => import('./AirplaneModeOffIcon.js'),
  AirplaneModeIcon: () => import('./AirplaneModeIcon.js'),
  AirplaneSeat02Icon: () => import('./AirplaneSeat02Icon.js'),
  AirplaneSeatIcon: () => import('./AirplaneSeatIcon.js'),
  AirplaneTakeOff01Icon: () => import('./AirplaneTakeOff01Icon.js'),
  AirplaneTakeOff02Icon: () => import('./AirplaneTakeOff02Icon.js'),
  AirplayLineIcon: () => import('./AirplayLineIcon.js'),
  AirplayIcon: () => import('./AirplayIcon.js'),
  Airpod01Icon: () => import('./Airpod01Icon.js'),
  Airpod02Icon: () => import('./Airpod02Icon.js'),
  Airpod03Icon: () => import('./Airpod03Icon.js'),
  Airport02Icon: () => import('./Airport02Icon.js'),
  AirportIcon: () => import('./AirportIcon.js'),
  AirportTowerIcon: () => import('./AirportTowerIcon.js'),
  AlAqsaMosqueIcon: () => import('./AlAqsaMosqueIcon.js'),
  AlarmClockCheckIcon: () => import('./AlarmClockCheckIcon.js'),
  AlarmClockMinusIcon: () => import('./AlarmClockMinusIcon.js'),
  AlarmClockOffIcon: () => import('./AlarmClockOffIcon.js'),
  AlarmClockPlusIcon: () => import('./AlarmClockPlusIcon.js'),
  AlarmClockIcon: () => import('./AlarmClockIcon.js'),
  AlarmSmokeIcon: () => import('./AlarmSmokeIcon.js'),
  Album01Icon: () => import('./Album01Icon.js'),
  Album02Icon: () => import('./Album02Icon.js'),
  AlbumNotFound01Icon: () => import('./AlbumNotFound01Icon.js'),
  AlbumNotFound02Icon: () => import('./AlbumNotFound02Icon.js'),
  AlbumIcon: () => import('./AlbumIcon.js'),
  Alert01Icon: () => import('./Alert01Icon.js'),
  Alert02Icon: () => import('./Alert02Icon.js'),
  AlertCircleIcon: () => import('./AlertCircleIcon.js'),
  AlertDiamondIcon: () => import('./AlertDiamondIcon.js'),
  AlertSquareIcon: () => import('./AlertSquareIcon.js'),
  AlgorithmIcon: () => import('./AlgorithmIcon.js'),
  Alien01Icon: () => import('./Alien01Icon.js'),
  Alien02Icon: () => import('./Alien02Icon.js'),
  AlignBottomIcon: () => import('./AlignBottomIcon.js'),
  AlignBoxBottomCenterIcon: () => import('./AlignBoxBottomCenterIcon.js'),
  AlignBoxBottomLeftIcon: () => import('./AlignBoxBottomLeftIcon.js'),
  AlignBoxBottomRightIcon: () => import('./AlignBoxBottomRightIcon.js'),
  AlignBoxMiddleCenterIcon: () => import('./AlignBoxMiddleCenterIcon.js'),
  AlignBoxMiddleLeftIcon: () => import('./AlignBoxMiddleLeftIcon.js'),
  AlignBoxMiddleRightIcon: () => import('./AlignBoxMiddleRightIcon.js'),
  AlignBoxTopCenterIcon: () => import('./AlignBoxTopCenterIcon.js'),
  AlignBoxTopLeftIcon: () => import('./AlignBoxTopLeftIcon.js'),
  AlignBoxTopRightIcon: () => import('./AlignBoxTopRightIcon.js'),
  AlignEndHorizontalIcon: () => import('./AlignEndHorizontalIcon.js'),
  AlignEndVerticalIcon: () => import('./AlignEndVerticalIcon.js'),
  AlignHorizontalCenterIcon: () => import('./AlignHorizontalCenterIcon.js'),
  AlignHorizontalDistributeCenterIcon: () => import('./AlignHorizontalDistributeCenterIcon.js'),
  AlignHorizontalDistributeEndIcon: () => import('./AlignHorizontalDistributeEndIcon.js'),
  AlignHorizontalDistributeStartIcon: () => import('./AlignHorizontalDistributeStartIcon.js'),
  AlignHorizontalJustifyCenterIcon: () => import('./AlignHorizontalJustifyCenterIcon.js'),
  AlignHorizontalJustifyEndIcon: () => import('./AlignHorizontalJustifyEndIcon.js'),
  AlignHorizontalJustifyStartIcon: () => import('./AlignHorizontalJustifyStartIcon.js'),
  AlignHorizontalSpaceAroundIcon: () => import('./AlignHorizontalSpaceAroundIcon.js'),
  AlignHorizontalSpaceBetweenIcon: () => import('./AlignHorizontalSpaceBetweenIcon.js'),
  AlignKeyObjectIcon: () => import('./AlignKeyObjectIcon.js'),
  AlignLeftIcon: () => import('./AlignLeftIcon.js'),
  AlignRightIcon: () => import('./AlignRightIcon.js'),
  AlignSelectionIcon: () => import('./AlignSelectionIcon.js'),
  AlignStartHorizontalIcon: () => import('./AlignStartHorizontalIcon.js'),
  AlignStartVerticalIcon: () => import('./AlignStartVerticalIcon.js'),
  AlignTopIcon: () => import('./AlignTopIcon.js'),
  AlignVerticalCenterIcon: () => import('./AlignVerticalCenterIcon.js'),
  AlignVerticalDistributeCenterIcon: () => import('./AlignVerticalDistributeCenterIcon.js'),
  AlignVerticalDistributeEndIcon: () => import('./AlignVerticalDistributeEndIcon.js'),
  AlignVerticalDistributeStartIcon: () => import('./AlignVerticalDistributeStartIcon.js'),
  AlignVerticalJustifyCenterIcon: () => import('./AlignVerticalJustifyCenterIcon.js'),
  AlignVerticalJustifyEndIcon: () => import('./AlignVerticalJustifyEndIcon.js'),
  AlignVerticalJustifyStartIcon: () => import('./AlignVerticalJustifyStartIcon.js'),
  AlignVerticalSpaceAroundIcon: () => import('./AlignVerticalSpaceAroundIcon.js'),
  AlignVerticalSpaceBetweenIcon: () => import('./AlignVerticalSpaceBetweenIcon.js'),
  AllBookmarkIcon: () => import('./AllBookmarkIcon.js'),
  AllahIcon: () => import('./AllahIcon.js'),
  AlmsIcon: () => import('./AlmsIcon.js'),
  AlphaCircleIcon: () => import('./AlphaCircleIcon.js'),
  AlphaSquareIcon: () => import('./AlphaSquareIcon.js'),
  AlphaIcon: () => import('./AlphaIcon.js'),
  AlphabetArabicIcon: () => import('./AlphabetArabicIcon.js'),
  AlphabetBanglaIcon: () => import('./AlphabetBanglaIcon.js'),
  AlphabetChineseIcon: () => import('./AlphabetChineseIcon.js'),
  AlphabetGreekIcon: () => import('./AlphabetGreekIcon.js'),
  AlphabetHebrewIcon: () => import('./AlphabetHebrewIcon.js'),
  AlphabetHindiIcon: () => import('./AlphabetHindiIcon.js'),
  AlphabetJapaneseIcon: () => import('./AlphabetJapaneseIcon.js'),
  AlphabetKoreanIcon: () => import('./AlphabetKoreanIcon.js'),
  AlphabetThaiIcon: () => import('./AlphabetThaiIcon.js'),
  AmazonIcon: () => import('./AmazonIcon.js'),
  AmbulanceIcon: () => import('./AmbulanceIcon.js'),
  AmericanFootballIcon: () => import('./AmericanFootballIcon.js'),
  AmieIcon: () => import('./AmieIcon.js'),
  AmmoIcon: () => import('./AmmoIcon.js'),
  AmpersandIcon: () => import('./AmpersandIcon.js'),
  AmpersandsIcon: () => import('./AmpersandsIcon.js'),
  AmphoraIcon: () => import('./AmphoraIcon.js'),
  AmpouleIcon: () => import('./AmpouleIcon.js'),
  AnalysisTextLinkIcon: () => import('./AnalysisTextLinkIcon.js'),
  Analytics01Icon: () => import('./Analytics01Icon.js'),
  Analytics02Icon: () => import('./Analytics02Icon.js'),
  Analytics03Icon: () => import('./Analytics03Icon.js'),
  AnalyticsDownIcon: () => import('./AnalyticsDownIcon.js'),
  AnalyticsUpIcon: () => import('./AnalyticsUpIcon.js'),
  AnchorPointIcon: () => import('./AnchorPointIcon.js'),
  AnchorIcon: () => import('./AnchorIcon.js'),
  AndroidIcon: () => import('./AndroidIcon.js'),
  AngelIcon: () => import('./AngelIcon.js'),
  Angle01Icon: () => import('./Angle01Icon.js'),
  AngleIcon: () => import('./AngleIcon.js'),
  AngryBirdIcon: () => import('./AngryBirdIcon.js'),
  AngryIcon: () => import('./AngryIcon.js'),
  AnnoyedIcon: () => import('./AnnoyedIcon.js'),
  AnonymousIcon: () => import('./AnonymousIcon.js'),
  AntennaIcon: () => import('./AntennaIcon.js'),
  AnvilIcon: () => import('./AnvilIcon.js'),
  ApartmentIcon: () => import('./ApartmentIcon.js'),
  ApertureIcon: () => import('./ApertureIcon.js'),
  ApiGatewayIcon: () => import('./ApiGatewayIcon.js'),
  ApiIcon: () => import('./ApiIcon.js'),
  AppStoreIcon: () => import('./AppStoreIcon.js'),
  AppWindowMacIcon: () => import('./AppWindowMacIcon.js'),
  AppWindowIcon: () => import('./AppWindowIcon.js'),
  Apple01Icon: () => import('./Apple01Icon.js'),
  AppleFinderIcon: () => import('./AppleFinderIcon.js'),
  AppleIntelligenceIcon: () => import('./AppleIntelligenceIcon.js'),
  AppleMusicIcon: () => import('./AppleMusicIcon.js'),
  AppleNewsIcon: () => import('./AppleNewsIcon.js'),
  ApplePieIcon: () => import('./ApplePieIcon.js'),
  AppleReminderIcon: () => import('./AppleReminderIcon.js'),
  AppleStocksIcon: () => import('./AppleStocksIcon.js'),
  AppleIcon: () => import('./AppleIcon.js'),
  AppleVisionProIcon: () => import('./AppleVisionProIcon.js'),
  Appointment01Icon: () => import('./Appointment01Icon.js'),
  Appointment02Icon: () => import('./Appointment02Icon.js'),
  ApproximatelyEqualCircleIcon: () => import('./ApproximatelyEqualCircleIcon.js'),
  ApproximatelyEqualSquareIcon: () => import('./ApproximatelyEqualSquareIcon.js'),
  ApproximatelyEqualIcon: () => import('./ApproximatelyEqualIcon.js'),
  ApricotIcon: () => import('./ApricotIcon.js'),
  ApronIcon: () => import('./ApronIcon.js'),
  ArcBrowserIcon: () => import('./ArcBrowserIcon.js'),
  ArcherIcon: () => import('./ArcherIcon.js'),
  Archive01Icon: () => import('./Archive01Icon.js'),
  Archive02Icon: () => import('./Archive02Icon.js'),
  Archive03Icon: () => import('./Archive03Icon.js'),
  Archive04Icon: () => import('./Archive04Icon.js'),
  ArchiveArrowDownIcon: () => import('./ArchiveArrowDownIcon.js'),
  ArchiveArrowUpIcon: () => import('./ArchiveArrowUpIcon.js'),
  ArchiveOff03Icon: () => import('./ArchiveOff03Icon.js'),
  ArchiveOff04Icon: () => import('./ArchiveOff04Icon.js'),
  ArchiveRestoreIcon: () => import('./ArchiveRestoreIcon.js'),
  ArchiveIcon: () => import('./ArchiveIcon.js'),
  ArchiveXIcon: () => import('./ArchiveXIcon.js'),
  ArmchairIcon: () => import('./ArmchairIcon.js'),
  ArmoredBootIcon: () => import('./ArmoredBootIcon.js'),
  ArrangeByLettersAZIcon: () => import('./ArrangeByLettersAZIcon.js'),
  ArrangeByLettersZAIcon: () => import('./ArrangeByLettersZAIcon.js'),
  ArrangeByNumbersOneNineIcon: () => import('./ArrangeByNumbersOneNineIcon.js'),
  ArrangeByNumbersNineOneIcon: () => import('./ArrangeByNumbersNineOneIcon.js'),
  ArrangeIcon: () => import('./ArrangeIcon.js'),
  ArrowAllDirectionIcon: () => import('./ArrowAllDirectionIcon.js'),
  ArrowBigDownDashIcon: () => import('./ArrowBigDownDashIcon.js'),
  ArrowBigLeftDashIcon: () => import('./ArrowBigLeftDashIcon.js'),
  ArrowBigRightDashIcon: () => import('./ArrowBigRightDashIcon.js'),
  ArrowBigUpDashIcon: () => import('./ArrowBigUpDashIcon.js'),
  ArrowDataTransferDiagonalIcon: () => import('./ArrowDataTransferDiagonalIcon.js'),
  ArrowDataTransferHorizontalIcon: () => import('./ArrowDataTransferHorizontalIcon.js'),
  ArrowDataTransferVerticalIcon: () => import('./ArrowDataTransferVerticalIcon.js'),
  ArrowDiagonalIcon: () => import('./ArrowDiagonalIcon.js'),
  ArrowDownZeroOneIcon: () => import('./ArrowDownZeroOneIcon.js'),
  ArrowDown01Icon: () => import('./ArrowDown01Icon.js'),
  ArrowDown02Icon: () => import('./ArrowDown02Icon.js'),
  ArrowDown03Icon: () => import('./ArrowDown03Icon.js'),
  ArrowDown04Icon: () => import('./ArrowDown04Icon.js'),
  ArrowDown05Icon: () => import('./ArrowDown05Icon.js'),
  ArrowDownOneZeroIcon: () => import('./ArrowDownOneZeroIcon.js'),
  ArrowDownAZIcon: () => import('./ArrowDownAZIcon.js'),
  ArrowDownAzIcon: () => import('./ArrowDownAzIcon.js'),
  ArrowDownBigIcon: () => import('./ArrowDownBigIcon.js'),
  ArrowDownDoubleIcon: () => import('./ArrowDownDoubleIcon.js'),
  ArrowDownFromLineIcon: () => import('./ArrowDownFromLineIcon.js'),
  ArrowDownLeft01Icon: () => import('./ArrowDownLeft01Icon.js'),
  ArrowDownLeft02Icon: () => import('./ArrowDownLeft02Icon.js'),
  ArrowDownNarrowWideIcon: () => import('./ArrowDownNarrowWideIcon.js'),
  ArrowDownRight01Icon: () => import('./ArrowDownRight01Icon.js'),
  ArrowDownRight02Icon: () => import('./ArrowDownRight02Icon.js'),
  ArrowDownToDotIcon: () => import('./ArrowDownToDotIcon.js'),
  ArrowDownToLineIcon: () => import('./ArrowDownToLineIcon.js'),
  ArrowDownWideNarrowIcon: () => import('./ArrowDownWideNarrowIcon.js'),
  ArrowDownZaIcon: () => import('./ArrowDownZaIcon.js'),
  ArrowExpand01Icon: () => import('./ArrowExpand01Icon.js'),
  ArrowExpand02Icon: () => import('./ArrowExpand02Icon.js'),
  ArrowExpandDiagonal01Icon: () => import('./ArrowExpandDiagonal01Icon.js'),
  ArrowExpandDiagonal02Icon: () => import('./ArrowExpandDiagonal02Icon.js'),
  ArrowExpandIcon: () => import('./ArrowExpandIcon.js'),
  ArrowHorizontalIcon: () => import('./ArrowHorizontalIcon.js'),
  ArrowLeft01Icon: () => import('./ArrowLeft01Icon.js'),
  ArrowLeft02Icon: () => import('./ArrowLeft02Icon.js'),
  ArrowLeft03Icon: () => import('./ArrowLeft03Icon.js'),
  ArrowLeft04Icon: () => import('./ArrowLeft04Icon.js'),
  ArrowLeft05Icon: () => import('./ArrowLeft05Icon.js'),
  ArrowLeftBigIcon: () => import('./ArrowLeftBigIcon.js'),
  ArrowLeftDoubleIcon: () => import('./ArrowLeftDoubleIcon.js'),
  ArrowLeftFromLineIcon: () => import('./ArrowLeftFromLineIcon.js'),
  ArrowLeftRightIcon: () => import('./ArrowLeftRightIcon.js'),
  ArrowLeftToLineIcon: () => import('./ArrowLeftToLineIcon.js'),
  ArrowMoveDownLeftIcon: () => import('./ArrowMoveDownLeftIcon.js'),
  ArrowMoveDownRightIcon: () => import('./ArrowMoveDownRightIcon.js'),
  ArrowMoveLeftDownIcon: () => import('./ArrowMoveLeftDownIcon.js'),
  ArrowMoveRightDownIcon: () => import('./ArrowMoveRightDownIcon.js'),
  ArrowMoveUpLeftIcon: () => import('./ArrowMoveUpLeftIcon.js'),
  ArrowMoveUpRightIcon: () => import('./ArrowMoveUpRightIcon.js'),
  ArrowReloadHorizontalIcon: () => import('./ArrowReloadHorizontalIcon.js'),
  ArrowReloadVerticalIcon: () => import('./ArrowReloadVerticalIcon.js'),
  ArrowRight01Icon: () => import('./ArrowRight01Icon.js'),
  ArrowRight02Icon: () => import('./ArrowRight02Icon.js'),
  ArrowRight03Icon: () => import('./ArrowRight03Icon.js'),
  ArrowRight04Icon: () => import('./ArrowRight04Icon.js'),
  ArrowRight05Icon: () => import('./ArrowRight05Icon.js'),
  ArrowRightBigIcon: () => import('./ArrowRightBigIcon.js'),
  ArrowRightDoubleIcon: () => import('./ArrowRightDoubleIcon.js'),
  ArrowRightFromLineIcon: () => import('./ArrowRightFromLineIcon.js'),
  ArrowRightToLineIcon: () => import('./ArrowRightToLineIcon.js'),
  ArrowShrink01Icon: () => import('./ArrowShrink01Icon.js'),
  ArrowShrink02Icon: () => import('./ArrowShrink02Icon.js'),
  ArrowShrinkIcon: () => import('./ArrowShrinkIcon.js'),
  ArrowTurnBackwardIcon: () => import('./ArrowTurnBackwardIcon.js'),
  ArrowTurnDownIcon: () => import('./ArrowTurnDownIcon.js'),
  ArrowTurnForwardIcon: () => import('./ArrowTurnForwardIcon.js'),
  ArrowTurnUpIcon: () => import('./ArrowTurnUpIcon.js'),
  ArrowUpZeroOneIcon: () => import('./ArrowUpZeroOneIcon.js'),
  ArrowUp01Icon: () => import('./ArrowUp01Icon.js'),
  ArrowUp02Icon: () => import('./ArrowUp02Icon.js'),
  ArrowUp03Icon: () => import('./ArrowUp03Icon.js'),
  ArrowUp04Icon: () => import('./ArrowUp04Icon.js'),
  ArrowUp05Icon: () => import('./ArrowUp05Icon.js'),
  ArrowUpOneZeroIcon: () => import('./ArrowUpOneZeroIcon.js'),
  ArrowUpAzIcon: () => import('./ArrowUpAzIcon.js'),
  ArrowUpBigIcon: () => import('./ArrowUpBigIcon.js'),
  ArrowUpDoubleIcon: () => import('./ArrowUpDoubleIcon.js'),
  ArrowUpDownIcon: () => import('./ArrowUpDownIcon.js'),
  ArrowUpFromDotIcon: () => import('./ArrowUpFromDotIcon.js'),
  ArrowUpFromLineIcon: () => import('./ArrowUpFromLineIcon.js'),
  ArrowUpLeft01Icon: () => import('./ArrowUpLeft01Icon.js'),
  ArrowUpLeft02Icon: () => import('./ArrowUpLeft02Icon.js'),
  ArrowUpNarrowWideIcon: () => import('./ArrowUpNarrowWideIcon.js'),
  ArrowUpRight01Icon: () => import('./ArrowUpRight01Icon.js'),
  ArrowUpRight02Icon: () => import('./ArrowUpRight02Icon.js'),
  ArrowUpRight03Icon: () => import('./ArrowUpRight03Icon.js'),
  ArrowUpRightStackIcon: () => import('./ArrowUpRightStackIcon.js'),
  ArrowUpToLineIcon: () => import('./ArrowUpToLineIcon.js'),
  ArrowUpWideNarrowIcon: () => import('./ArrowUpWideNarrowIcon.js'),
  ArrowUpZAIcon: () => import('./ArrowUpZAIcon.js'),
  ArrowUpZaIcon: () => import('./ArrowUpZaIcon.js'),
  ArrowVerticalIcon: () => import('./ArrowVerticalIcon.js'),
  ArrowsUpFromLineIcon: () => import('./ArrowsUpFromLineIcon.js'),
  ArtboardIcon: () => import('./ArtboardIcon.js'),
  ArtboardToolIcon: () => import('./ArtboardToolIcon.js'),
  ArtificialIntelligence01Icon: () => import('./ArtificialIntelligence01Icon.js'),
  ArtificialIntelligence02Icon: () => import('./ArtificialIntelligence02Icon.js'),
  ArtificialIntelligence03Icon: () => import('./ArtificialIntelligence03Icon.js'),
  ArtificialIntelligence04Icon: () => import('./ArtificialIntelligence04Icon.js'),
  ArtificialIntelligence05Icon: () => import('./ArtificialIntelligence05Icon.js'),
  ArtificialIntelligence06Icon: () => import('./ArtificialIntelligence06Icon.js'),
  ArtificialIntelligence07Icon: () => import('./ArtificialIntelligence07Icon.js'),
  ArtificialIntelligence08Icon: () => import('./ArtificialIntelligence08Icon.js'),
  AspectRatioIcon: () => import('./AspectRatioIcon.js'),
  AssignmentsIcon: () => import('./AssignmentsIcon.js'),
  Asterisk02Icon: () => import('./Asterisk02Icon.js'),
  AsteriskIcon: () => import('./AsteriskIcon.js'),
  Asteroid01Icon: () => import('./Asteroid01Icon.js'),
  Asteroid02Icon: () => import('./Asteroid02Icon.js'),
  Astronaut01Icon: () => import('./Astronaut01Icon.js'),
  Astronaut02Icon: () => import('./Astronaut02Icon.js'),
  AtSignIcon: () => import('./AtSignIcon.js'),
  AtIcon: () => import('./AtIcon.js'),
  Atm01Icon: () => import('./Atm01Icon.js'),
  Atm02Icon: () => import('./Atm02Icon.js'),
  Atom01Icon: () => import('./Atom01Icon.js'),
  Atom02Icon: () => import('./Atom02Icon.js'),
  AtomicPowerIcon: () => import('./AtomicPowerIcon.js'),
  Attachment01Icon: () => import('./Attachment01Icon.js'),
  Attachment02Icon: () => import('./Attachment02Icon.js'),
  AttachmentCircleIcon: () => import('./AttachmentCircleIcon.js'),
  AttachmentSquareIcon: () => import('./AttachmentSquareIcon.js'),
  AttachmentIcon: () => import('./AttachmentIcon.js'),
  AuctionIcon: () => import('./AuctionIcon.js'),
  AudioBook01Icon: () => import('./AudioBook01Icon.js'),
  AudioBook02Icon: () => import('./AudioBook02Icon.js'),
  AudioBook03Icon: () => import('./AudioBook03Icon.js'),
  AudioBook04Icon: () => import('./AudioBook04Icon.js'),
  AudioLinesIcon: () => import('./AudioLinesIcon.js'),
  AudioWave01Icon: () => import('./AudioWave01Icon.js'),
  AudioWave02Icon: () => import('./AudioWave02Icon.js'),
  AudioWaveformIcon: () => import('./AudioWaveformIcon.js'),
  Audit01Icon: () => import('./Audit01Icon.js'),
  Audit02Icon: () => import('./Audit02Icon.js'),
  AugmentedRealityArIcon: () => import('./AugmentedRealityArIcon.js'),
  AuthorizedIcon: () => import('./AuthorizedIcon.js'),
  AutoConversationsIcon: () => import('./AutoConversationsIcon.js'),
  AutomotiveBattery01Icon: () => import('./AutomotiveBattery01Icon.js'),
  AutomotiveBattery02Icon: () => import('./AutomotiveBattery02Icon.js'),
  AvalancheIcon: () => import('./AvalancheIcon.js'),
  AvocadoIcon: () => import('./AvocadoIcon.js'),
  Award01Icon: () => import('./Award01Icon.js'),
  Award02Icon: () => import('./Award02Icon.js'),
  Award03Icon: () => import('./Award03Icon.js'),
  Award04Icon: () => import('./Award04Icon.js'),
  Award05Icon: () => import('./Award05Icon.js'),
  AwsLambdaIcon: () => import('./AwsLambdaIcon.js'),
  AxeIcon: () => import('./AxeIcon.js'),
  AxisThreeDIcon: () => import('./AxisThreeDIcon.js'),
  Baby01Icon: () => import('./Baby01Icon.js'),
  Baby02Icon: () => import('./Baby02Icon.js'),
  BabyBed01Icon: () => import('./BabyBed01Icon.js'),
  BabyBed02Icon: () => import('./BabyBed02Icon.js'),
  BabyBottleIcon: () => import('./BabyBottleIcon.js'),
  BabyBoyDressIcon: () => import('./BabyBoyDressIcon.js'),
  BabyGirlDressIcon: () => import('./BabyGirlDressIcon.js'),
  BackMuscleBodyIcon: () => import('./BackMuscleBodyIcon.js'),
  BackgroundIcon: () => import('./BackgroundIcon.js'),
  Backpack01Icon: () => import('./Backpack01Icon.js'),
  Backpack02Icon: () => import('./Backpack02Icon.js'),
  Backpack03Icon: () => import('./Backpack03Icon.js'),
  Backward01Icon: () => import('./Backward01Icon.js'),
  Backward02Icon: () => import('./Backward02Icon.js'),
  BacteriaIcon: () => import('./BacteriaIcon.js'),
  BadgeAlertIcon: () => import('./BadgeAlertIcon.js'),
  BadgeCentIcon: () => import('./BadgeCentIcon.js'),
  BadgeCheckIcon: () => import('./BadgeCheckIcon.js'),
  BadgeDollarSignIcon: () => import('./BadgeDollarSignIcon.js'),
  BadgeEuroIcon: () => import('./BadgeEuroIcon.js'),
  BadgeIndianRupeeIcon: () => import('./BadgeIndianRupeeIcon.js'),
  BadgeInfoIcon: () => import('./BadgeInfoIcon.js'),
  BadgeJapaneseYenIcon: () => import('./BadgeJapaneseYenIcon.js'),
  BadgeMinusIcon: () => import('./BadgeMinusIcon.js'),
  BadgePercentIcon: () => import('./BadgePercentIcon.js'),
  BadgePlusIcon: () => import('./BadgePlusIcon.js'),
  BadgePoundSterlingIcon: () => import('./BadgePoundSterlingIcon.js'),
  BadgeQuestionMarkIcon: () => import('./BadgeQuestionMarkIcon.js'),
  BadgeRussianRubleIcon: () => import('./BadgeRussianRubleIcon.js'),
  BadgeIcon: () => import('./BadgeIcon.js'),
  BadgeSwissFrancIcon: () => import('./BadgeSwissFrancIcon.js'),
  BadgeTurkishLiraIcon: () => import('./BadgeTurkishLiraIcon.js'),
  BadgeXIcon: () => import('./BadgeXIcon.js'),
  BadmintonShuttleIcon: () => import('./BadmintonShuttleIcon.js'),
  BadmintonIcon: () => import('./BadmintonIcon.js'),
  BaggageClaimIcon: () => import('./BaggageClaimIcon.js'),
  BalanceScaleIcon: () => import('./BalanceScaleIcon.js'),
  BalloonIcon: () => import('./BalloonIcon.js'),
  BalloonsIcon: () => import('./BalloonsIcon.js'),
  BanIcon: () => import('./BanIcon.js'),
  BananaIcon: () => import('./BananaIcon.js'),
  BandageIcon: () => import('./BandageIcon.js'),
  BankIcon: () => import('./BankIcon.js'),
  BanknoteArrowDownIcon: () => import('./BanknoteArrowDownIcon.js'),
  BanknoteArrowUpIcon: () => import('./BanknoteArrowUpIcon.js'),
  BanknoteIcon: () => import('./BanknoteIcon.js'),
  BanknoteXIcon: () => import('./BanknoteXIcon.js'),
  BarChartHorizontalIcon: () => import('./BarChartHorizontalIcon.js'),
  BarChartIcon: () => import('./BarChartIcon.js'),
  BarCode01Icon: () => import('./BarCode01Icon.js'),
  BarCode02Icon: () => import('./BarCode02Icon.js'),
  BarcodeScanIcon: () => import('./BarcodeScanIcon.js'),
  BarcodeIcon: () => import('./BarcodeIcon.js'),
  BarnsIcon: () => import('./BarnsIcon.js'),
  BarrelIcon: () => import('./BarrelIcon.js'),
  BaseballBatIcon: () => import('./BaseballBatIcon.js'),
  BaseballHelmetIcon: () => import('./BaseballHelmetIcon.js'),
  BaseballIcon: () => import('./BaseballIcon.js'),
  BaselineIcon: () => import('./BaselineIcon.js'),
  BashIcon: () => import('./BashIcon.js'),
  Basketball01Icon: () => import('./Basketball01Icon.js'),
  Basketball02Icon: () => import('./Basketball02Icon.js'),
  BasketballHoopIcon: () => import('./BasketballHoopIcon.js'),
  BathIcon: () => import('./BathIcon.js'),
  Bathtub01Icon: () => import('./Bathtub01Icon.js'),
  Bathtub02Icon: () => import('./Bathtub02Icon.js'),
  BatteriesEnergyIcon: () => import('./BatteriesEnergyIcon.js'),
  BatteryCharging01Icon: () => import('./BatteryCharging01Icon.js'),
  BatteryCharging02Icon: () => import('./BatteryCharging02Icon.js'),
  BatteryEcoChargingIcon: () => import('./BatteryEcoChargingIcon.js'),
  BatteryEmptyIcon: () => import('./BatteryEmptyIcon.js'),
  BatteryFullIcon: () => import('./BatteryFullIcon.js'),
  BatteryLowIcon: () => import('./BatteryLowIcon.js'),
  BatteryMedium01Icon: () => import('./BatteryMedium01Icon.js'),
  BatteryMedium02Icon: () => import('./BatteryMedium02Icon.js'),
  BatteryPlusIcon: () => import('./BatteryPlusIcon.js'),
  BatteryIcon: () => import('./BatteryIcon.js'),
  BatteryWarningIcon: () => import('./BatteryWarningIcon.js'),
  BbqGrillIcon: () => import('./BbqGrillIcon.js'),
  Beach02Icon: () => import('./Beach02Icon.js'),
  BeachIcon: () => import('./BeachIcon.js'),
  BeakerIcon: () => import('./BeakerIcon.js'),
  BeanOffIcon: () => import('./BeanOffIcon.js'),
  BeanIcon: () => import('./BeanIcon.js'),
  BeaterIcon: () => import('./BeaterIcon.js'),
  BeboIcon: () => import('./BeboIcon.js'),
  BedBunkIcon: () => import('./BedBunkIcon.js'),
  BedDoubleIcon: () => import('./BedDoubleIcon.js'),
  BedSingle01Icon: () => import('./BedSingle01Icon.js'),
  BedSingle02Icon: () => import('./BedSingle02Icon.js'),
  BedIcon: () => import('./BedIcon.js'),
  Bedug01Icon: () => import('./Bedug01Icon.js'),
  Bedug02Icon: () => import('./Bedug02Icon.js'),
  BeefOffIcon: () => import('./BeefOffIcon.js'),
  BeefIcon: () => import('./BeefIcon.js'),
  BeerOffIcon: () => import('./BeerOffIcon.js'),
  BeerIcon: () => import('./BeerIcon.js'),
  Behance01Icon: () => import('./Behance01Icon.js'),
  Behance02Icon: () => import('./Behance02Icon.js'),
  BellDotIcon: () => import('./BellDotIcon.js'),
  BellElectricIcon: () => import('./BellElectricIcon.js'),
  BellMinusIcon: () => import('./BellMinusIcon.js'),
  BellOffIcon: () => import('./BellOffIcon.js'),
  BellPlusIcon: () => import('./BellPlusIcon.js'),
  BellRingIcon: () => import('./BellRingIcon.js'),
  BellIcon: () => import('./BellIcon.js'),
  BeltIcon: () => import('./BeltIcon.js'),
  BendToolIcon: () => import('./BendToolIcon.js'),
  BerlinIcon: () => import('./BerlinIcon.js'),
  BerlinTowerIcon: () => import('./BerlinTowerIcon.js'),
  BetaIcon: () => import('./BetaIcon.js'),
  BetweenHorizontalEndIcon: () => import('./BetweenHorizontalEndIcon.js'),
  BetweenHorizontalStartIcon: () => import('./BetweenHorizontalStartIcon.js'),
  BetweenVerticalEndIcon: () => import('./BetweenVerticalEndIcon.js'),
  BetweenVerticalStartIcon: () => import('./BetweenVerticalStartIcon.js'),
  BicepsFlexedIcon: () => import('./BicepsFlexedIcon.js'),
  Bicycle01Icon: () => import('./Bicycle01Icon.js'),
  BicycleIcon: () => import('./BicycleIcon.js'),
  BikeIcon: () => import('./BikeIcon.js'),
  Billiard01Icon: () => import('./Billiard01Icon.js'),
  Billiard02Icon: () => import('./Billiard02Icon.js'),
  BinaryCodeIcon: () => import('./BinaryCodeIcon.js'),
  BinaryIcon: () => import('./BinaryIcon.js'),
  BingIcon: () => import('./BingIcon.js'),
  BinocularsIcon: () => import('./BinocularsIcon.js'),
  BioEnergyIcon: () => import('./BioEnergyIcon.js'),
  BiohazardIcon: () => import('./BiohazardIcon.js'),
  BiomassEnergyIcon: () => import('./BiomassEnergyIcon.js'),
  BiometricAccessIcon: () => import('./BiometricAccessIcon.js'),
  BiometricDeviceIcon: () => import('./BiometricDeviceIcon.js'),
  BirdIcon: () => import('./BirdIcon.js'),
  BirdhouseIcon: () => import('./BirdhouseIcon.js'),
  BirthdayCakeIcon: () => import('./BirthdayCakeIcon.js'),
  BiscuitIcon: () => import('./BiscuitIcon.js'),
  Bitcoin01Icon: () => import('./Bitcoin01Icon.js'),
  Bitcoin02Icon: () => import('./Bitcoin02Icon.js'),
  Bitcoin03Icon: () => import('./Bitcoin03Icon.js'),
  Bitcoin04Icon: () => import('./Bitcoin04Icon.js'),
  BitcoinBagIcon: () => import('./BitcoinBagIcon.js'),
  BitcoinCircleIcon: () => import('./BitcoinCircleIcon.js'),
  BitcoinCloudIcon: () => import('./BitcoinCloudIcon.js'),
  BitcoinCpuIcon: () => import('./BitcoinCpuIcon.js'),
  BitcoinCreditCardIcon: () => import('./BitcoinCreditCardIcon.js'),
  BitcoinDatabaseIcon: () => import('./BitcoinDatabaseIcon.js'),
  BitcoinDown01Icon: () => import('./BitcoinDown01Icon.js'),
  BitcoinDown02Icon: () => import('./BitcoinDown02Icon.js'),
  BitcoinEllipseIcon: () => import('./BitcoinEllipseIcon.js'),
  BitcoinEyeIcon: () => import('./BitcoinEyeIcon.js'),
  BitcoinFilterIcon: () => import('./BitcoinFilterIcon.js'),
  BitcoinFlashdiskIcon: () => import('./BitcoinFlashdiskIcon.js'),
  BitcoinGraphIcon: () => import('./BitcoinGraphIcon.js'),
  BitcoinInvoiceIcon: () => import('./BitcoinInvoiceIcon.js'),
  BitcoinKeyIcon: () => import('./BitcoinKeyIcon.js'),
  BitcoinLocationIcon: () => import('./BitcoinLocationIcon.js'),
  BitcoinLockIcon: () => import('./BitcoinLockIcon.js'),
  BitcoinMailIcon: () => import('./BitcoinMailIcon.js'),
  BitcoinMindIcon: () => import('./BitcoinMindIcon.js'),
  BitcoinMoney01Icon: () => import('./BitcoinMoney01Icon.js'),
  BitcoinMoney02Icon: () => import('./BitcoinMoney02Icon.js'),
  BitcoinPieChartIcon: () => import('./BitcoinPieChartIcon.js'),
  BitcoinPiggyBankIcon: () => import('./BitcoinPiggyBankIcon.js'),
  BitcoinPresentationIcon: () => import('./BitcoinPresentationIcon.js'),
  BitcoinReceiptIcon: () => import('./BitcoinReceiptIcon.js'),
  BitcoinReceiveIcon: () => import('./BitcoinReceiveIcon.js'),
  BitcoinRectangleIcon: () => import('./BitcoinRectangleIcon.js'),
  BitcoinSafeIcon: () => import('./BitcoinSafeIcon.js'),
  BitcoinSearchIcon: () => import('./BitcoinSearchIcon.js'),
  BitcoinSendIcon: () => import('./BitcoinSendIcon.js'),
  BitcoinSettingIcon: () => import('./BitcoinSettingIcon.js'),
  BitcoinShieldIcon: () => import('./BitcoinShieldIcon.js'),
  BitcoinShoppingIcon: () => import('./BitcoinShoppingIcon.js'),
  BitcoinSmartphone01Icon: () => import('./BitcoinSmartphone01Icon.js'),
  BitcoinSmartphone02Icon: () => import('./BitcoinSmartphone02Icon.js'),
  BitcoinSquareIcon: () => import('./BitcoinSquareIcon.js'),
  BitcoinStoreIcon: () => import('./BitcoinStoreIcon.js'),
  BitcoinIcon: () => import('./BitcoinIcon.js'),
  BitcoinTagIcon: () => import('./BitcoinTagIcon.js'),
  BitcoinTargetIcon: () => import('./BitcoinTargetIcon.js'),
  BitcoinTransactionIcon: () => import('./BitcoinTransactionIcon.js'),
  BitcoinUp01Icon: () => import('./BitcoinUp01Icon.js'),
  BitcoinUp02Icon: () => import('./BitcoinUp02Icon.js'),
  BitcoinWalletIcon: () => import('./BitcoinWalletIcon.js'),
  BitcoinWithdrawIcon: () => import('./BitcoinWithdrawIcon.js'),
  BlackHole01Icon: () => import('./BlackHole01Icon.js'),
  BlackHoleIcon: () => import('./BlackHoleIcon.js'),
  BlendIcon: () => import('./BlendIcon.js'),
  BlenderIcon: () => import('./BlenderIcon.js'),
  BlindsIcon: () => import('./BlindsIcon.js'),
  BlockGameIcon: () => import('./BlockGameIcon.js'),
  Blockchain01Icon: () => import('./Blockchain01Icon.js'),
  Blockchain02Icon: () => import('./Blockchain02Icon.js'),
  Blockchain03Icon: () => import('./Blockchain03Icon.js'),
  Blockchain04Icon: () => import('./Blockchain04Icon.js'),
  Blockchain05Icon: () => import('./Blockchain05Icon.js'),
  Blockchain06Icon: () => import('./Blockchain06Icon.js'),
  Blockchain07Icon: () => import('./Blockchain07Icon.js'),
  BlockedIcon: () => import('./BlockedIcon.js'),
  BlocksIcon: () => import('./BlocksIcon.js'),
  BloggerIcon: () => import('./BloggerIcon.js'),
  BloodBagIcon: () => import('./BloodBagIcon.js'),
  BloodBottleIcon: () => import('./BloodBottleIcon.js'),
  BloodPressureIcon: () => import('./BloodPressureIcon.js'),
  BloodIcon: () => import('./BloodIcon.js'),
  BloodTypeIcon: () => import('./BloodTypeIcon.js'),
  BlueskyIcon: () => import('./BlueskyIcon.js'),
  BluetoothCircleIcon: () => import('./BluetoothCircleIcon.js'),
  BluetoothConnectedIcon: () => import('./BluetoothConnectedIcon.js'),
  BluetoothNotConnectedIcon: () => import('./BluetoothNotConnectedIcon.js'),
  BluetoothOffIcon: () => import('./BluetoothOffIcon.js'),
  BluetoothSearchIcon: () => import('./BluetoothSearchIcon.js'),
  BluetoothSearchingIcon: () => import('./BluetoothSearchingIcon.js'),
  BluetoothSquareIcon: () => import('./BluetoothSquareIcon.js'),
  BluetoothIcon: () => import('./BluetoothIcon.js'),
  BlurIcon: () => import('./BlurIcon.js'),
  BlushBrush01Icon: () => import('./BlushBrush01Icon.js'),
  BlushBrush02Icon: () => import('./BlushBrush02Icon.js'),
  BoardMathIcon: () => import('./BoardMathIcon.js'),
  BoatIcon: () => import('./BoatIcon.js'),
  BodyArmorIcon: () => import('./BodyArmorIcon.js'),
  BodyPartLegIcon: () => import('./BodyPartLegIcon.js'),
  BodyPartMuscleIcon: () => import('./BodyPartMuscleIcon.js'),
  BodyPartSixPackIcon: () => import('./BodyPartSixPackIcon.js'),
  BodySoapIcon: () => import('./BodySoapIcon.js'),
  BodyWeightIcon: () => import('./BodyWeightIcon.js'),
  BoldIcon: () => import('./BoldIcon.js'),
  BoltIcon: () => import('./BoltIcon.js'),
  BombIcon: () => import('./BombIcon.js'),
  Bone01Icon: () => import('./Bone01Icon.js'),
  Bone02Icon: () => import('./Bone02Icon.js'),
  Book01Icon: () => import('./Book01Icon.js'),
  Book02Icon: () => import('./Book02Icon.js'),
  Book03Icon: () => import('./Book03Icon.js'),
  Book04Icon: () => import('./Book04Icon.js'),
  BookAIcon: () => import('./BookAIcon.js'),
  BookAlertIcon: () => import('./BookAlertIcon.js'),
  BookBookmark01Icon: () => import('./BookBookmark01Icon.js'),
  BookBookmark02Icon: () => import('./BookBookmark02Icon.js'),
  BookCheckIcon: () => import('./BookCheckIcon.js'),
  BookCopyIcon: () => import('./BookCopyIcon.js'),
  BookDashedIcon: () => import('./BookDashedIcon.js'),
  BookDownIcon: () => import('./BookDownIcon.js'),
  BookDownloadIcon: () => import('./BookDownloadIcon.js'),
  BookEditIcon: () => import('./BookEditIcon.js'),
  BookHeadphonesIcon: () => import('./BookHeadphonesIcon.js'),
  BookHeartIcon: () => import('./BookHeartIcon.js'),
  BookImageIcon: () => import('./BookImageIcon.js'),
  BookKeyIcon: () => import('./BookKeyIcon.js'),
  BookLockIcon: () => import('./BookLockIcon.js'),
  BookMarkedIcon: () => import('./BookMarkedIcon.js'),
  BookMinusIcon: () => import('./BookMinusIcon.js'),
  BookOpen01Icon: () => import('./BookOpen01Icon.js'),
  BookOpen02Icon: () => import('./BookOpen02Icon.js'),
  BookOpenCheckIcon: () => import('./BookOpenCheckIcon.js'),
  BookOpenTextIcon: () => import('./BookOpenTextIcon.js'),
  BookPlusIcon: () => import('./BookPlusIcon.js'),
  BookSearchIcon: () => import('./BookSearchIcon.js'),
  BookTextIcon: () => import('./BookTextIcon.js'),
  BookTypeIcon: () => import('./BookTypeIcon.js'),
  BookUpTwoIcon: () => import('./BookUpTwoIcon.js'),
  BookUploadIcon: () => import('./BookUploadIcon.js'),
  BookUserIcon: () => import('./BookUserIcon.js'),
  BookXIcon: () => import('./BookXIcon.js'),
  Bookmark01Icon: () => import('./Bookmark01Icon.js'),
  Bookmark02Icon: () => import('./Bookmark02Icon.js'),
  Bookmark03Icon: () => import('./Bookmark03Icon.js'),
  BookmarkAdd01Icon: () => import('./BookmarkAdd01Icon.js'),
  BookmarkAdd02Icon: () => import('./BookmarkAdd02Icon.js'),
  BookmarkBlock01Icon: () => import('./BookmarkBlock01Icon.js'),
  BookmarkBlock02Icon: () => import('./BookmarkBlock02Icon.js'),
  BookmarkCheck01Icon: () => import('./BookmarkCheck01Icon.js'),
  BookmarkCheck02Icon: () => import('./BookmarkCheck02Icon.js'),
  BookmarkMinus01Icon: () => import('./BookmarkMinus01Icon.js'),
  BookmarkMinus02Icon: () => import('./BookmarkMinus02Icon.js'),
  BookmarkOff01Icon: () => import('./BookmarkOff01Icon.js'),
  BookmarkOff02Icon: () => import('./BookmarkOff02Icon.js'),
  BookmarkPlusIcon: () => import('./BookmarkPlusIcon.js'),
  BookmarkRemove01Icon: () => import('./BookmarkRemove01Icon.js'),
  BookmarkRemove02Icon: () => import('./BookmarkRemove02Icon.js'),
  BookmarkXIcon: () => import('./BookmarkXIcon.js'),
  Books01Icon: () => import('./Books01Icon.js'),
  Books02Icon: () => import('./Books02Icon.js'),
  Bookshelf01Icon: () => import('./Bookshelf01Icon.js'),
  Bookshelf02Icon: () => import('./Bookshelf02Icon.js'),
  Bookshelf03Icon: () => import('./Bookshelf03Icon.js'),
  BoomBoxIcon: () => import('./BoomBoxIcon.js'),
  BootstrapIcon: () => import('./BootstrapIcon.js'),
  BorderAll01Icon: () => import('./BorderAll01Icon.js'),
  BorderAll02Icon: () => import('./BorderAll02Icon.js'),
  BorderBottom01Icon: () => import('./BorderBottom01Icon.js'),
  BorderBottom02Icon: () => import('./BorderBottom02Icon.js'),
  BorderFullIcon: () => import('./BorderFullIcon.js'),
  BorderHorizontalIcon: () => import('./BorderHorizontalIcon.js'),
  BorderInnerIcon: () => import('./BorderInnerIcon.js'),
  BorderLeft01Icon: () => import('./BorderLeft01Icon.js'),
  BorderLeft02Icon: () => import('./BorderLeft02Icon.js'),
  BorderNone01Icon: () => import('./BorderNone01Icon.js'),
  BorderNone02Icon: () => import('./BorderNone02Icon.js'),
  BorderRight01Icon: () => import('./BorderRight01Icon.js'),
  BorderRight02Icon: () => import('./BorderRight02Icon.js'),
  BorderTop01Icon: () => import('./BorderTop01Icon.js'),
  BorderTop02Icon: () => import('./BorderTop02Icon.js'),
  BorderVerticalIcon: () => import('./BorderVerticalIcon.js'),
  BorobudurIcon: () => import('./BorobudurIcon.js'),
  BotMessageSquareIcon: () => import('./BotMessageSquareIcon.js'),
  BotOffIcon: () => import('./BotOffIcon.js'),
  BotIcon: () => import('./BotIcon.js'),
  BounceLeftIcon: () => import('./BounceLeftIcon.js'),
  BounceRightIcon: () => import('./BounceRightIcon.js'),
  BoundingBoxIcon: () => import('./BoundingBoxIcon.js'),
  BowArrowIcon: () => import('./BowArrowIcon.js'),
  BowTieIcon: () => import('./BowTieIcon.js'),
  BowlingBallIcon: () => import('./BowlingBallIcon.js'),
  BowlingPinsIcon: () => import('./BowlingPinsIcon.js'),
  BowlingIcon: () => import('./BowlingIcon.js'),
  BoxIcon: () => import('./BoxIcon.js'),
  BoxerIcon: () => import('./BoxerIcon.js'),
  BoxesIcon: () => import('./BoxesIcon.js'),
  BoxingBagIcon: () => import('./BoxingBagIcon.js'),
  BoxingGlove01Icon: () => import('./BoxingGlove01Icon.js'),
  BoxingGloveIcon: () => import('./BoxingGloveIcon.js'),
  BracesIcon: () => import('./BracesIcon.js'),
  BracketsIcon: () => import('./BracketsIcon.js'),
  Brain01Icon: () => import('./Brain01Icon.js'),
  Brain02Icon: () => import('./Brain02Icon.js'),
  Brain03Icon: () => import('./Brain03Icon.js'),
  BrainCircuitIcon: () => import('./BrainCircuitIcon.js'),
  BrainCogIcon: () => import('./BrainCogIcon.js'),
  BrainIcon: () => import('./BrainIcon.js'),
  BrandfetchIcon: () => import('./BrandfetchIcon.js'),
  Bread01Icon: () => import('./Bread01Icon.js'),
  Bread02Icon: () => import('./Bread02Icon.js'),
  Bread03Icon: () => import('./Bread03Icon.js'),
  Bread04Icon: () => import('./Bread04Icon.js'),
  BreastPumpIcon: () => import('./BreastPumpIcon.js'),
  BrickWallFireIcon: () => import('./BrickWallFireIcon.js'),
  BrickWallShieldIcon: () => import('./BrickWallShieldIcon.js'),
  BrickWallIcon: () => import('./BrickWallIcon.js'),
  BridgeIcon: () => import('./BridgeIcon.js'),
  Briefcase01Icon: () => import('./Briefcase01Icon.js'),
  Briefcase02Icon: () => import('./Briefcase02Icon.js'),
  Briefcase03Icon: () => import('./Briefcase03Icon.js'),
  Briefcase04Icon: () => import('./Briefcase04Icon.js'),
  Briefcase05Icon: () => import('./Briefcase05Icon.js'),
  Briefcase06Icon: () => import('./Briefcase06Icon.js'),
  Briefcase07Icon: () => import('./Briefcase07Icon.js'),
  Briefcase08Icon: () => import('./Briefcase08Icon.js'),
  Briefcase09Icon: () => import('./Briefcase09Icon.js'),
  BriefcaseBusinessIcon: () => import('./BriefcaseBusinessIcon.js'),
  BriefcaseConveyorBeltIcon: () => import('./BriefcaseConveyorBeltIcon.js'),
  BriefcaseDollarIcon: () => import('./BriefcaseDollarIcon.js'),
  BriefcaseMedicalIcon: () => import('./BriefcaseMedicalIcon.js'),
  BringToFrontIcon: () => import('./BringToFrontIcon.js'),
  BroccoliIcon: () => import('./BroccoliIcon.js'),
  BrochureIcon: () => import('./BrochureIcon.js'),
  BrokenBoneIcon: () => import('./BrokenBoneIcon.js'),
  BrowserIcon: () => import('./BrowserIcon.js'),
  BrushCleaningIcon: () => import('./BrushCleaningIcon.js'),
  BrushIcon: () => import('./BrushIcon.js'),
  BubbleChatAddIcon: () => import('./BubbleChatAddIcon.js'),
  BubbleChatBlockedIcon: () => import('./BubbleChatBlockedIcon.js'),
  BubbleChatCancelIcon: () => import('./BubbleChatCancelIcon.js'),
  BubbleChatDelayIcon: () => import('./BubbleChatDelayIcon.js'),
  BubbleChatDoneIcon: () => import('./BubbleChatDoneIcon.js'),
  BubbleChatDownload01Icon: () => import('./BubbleChatDownload01Icon.js'),
  BubbleChatDownload02Icon: () => import('./BubbleChatDownload02Icon.js'),
  BubbleChatEditIcon: () => import('./BubbleChatEditIcon.js'),
  BubbleChatFavouriteIcon: () => import('./BubbleChatFavouriteIcon.js'),
  BubbleChatIncomeIcon: () => import('./BubbleChatIncomeIcon.js'),
  BubbleChatLockIcon: () => import('./BubbleChatLockIcon.js'),
  BubbleChatNotificationIcon: () => import('./BubbleChatNotificationIcon.js'),
  BubbleChatOutcomeIcon: () => import('./BubbleChatOutcomeIcon.js'),
  BubbleChatPreviewIcon: () => import('./BubbleChatPreviewIcon.js'),
  BubbleChatQuestionIcon: () => import('./BubbleChatQuestionIcon.js'),
  BubbleChatSearchIcon: () => import('./BubbleChatSearchIcon.js'),
  BubbleChatSecureIcon: () => import('./BubbleChatSecureIcon.js'),
  BubbleChatSpark01Icon: () => import('./BubbleChatSpark01Icon.js'),
  BubbleChatSparkIcon: () => import('./BubbleChatSparkIcon.js'),
  BubbleChatIcon: () => import('./BubbleChatIcon.js'),
  BubbleChatTemporaryIcon: () => import('./BubbleChatTemporaryIcon.js'),
  BubbleChatTranslateIcon: () => import('./BubbleChatTranslateIcon.js'),
  BubbleChatUnlockIcon: () => import('./BubbleChatUnlockIcon.js'),
  BubbleChatUploadIcon: () => import('./BubbleChatUploadIcon.js'),
  BubbleChatUserIcon: () => import('./BubbleChatUserIcon.js'),
  BubbleTea01Icon: () => import('./BubbleTea01Icon.js'),
  BubbleTea02Icon: () => import('./BubbleTea02Icon.js'),
  BubblesIcon: () => import('./BubblesIcon.js'),
  BucketIcon: () => import('./BucketIcon.js'),
  Bug01Icon: () => import('./Bug01Icon.js'),
  Bug02Icon: () => import('./Bug02Icon.js'),
  Building01Icon: () => import('./Building01Icon.js'),
  Building02Icon: () => import('./Building02Icon.js'),
  Building03Icon: () => import('./Building03Icon.js'),
  Building04Icon: () => import('./Building04Icon.js'),
  Building05Icon: () => import('./Building05Icon.js'),
  Building06Icon: () => import('./Building06Icon.js'),
  BulbChargingIcon: () => import('./BulbChargingIcon.js'),
  BulbIcon: () => import('./BulbIcon.js'),
  BulletIcon: () => import('./BulletIcon.js'),
  BulletproofVestIcon: () => import('./BulletproofVestIcon.js'),
  BurjAlArabIcon: () => import('./BurjAlArabIcon.js'),
  BurningCdIcon: () => import('./BurningCdIcon.js'),
  Bus01Icon: () => import('./Bus01Icon.js'),
  Bus02Icon: () => import('./Bus02Icon.js'),
  Bus03Icon: () => import('./Bus03Icon.js'),
  BusFrontIcon: () => import('./BusFrontIcon.js'),
  CIcon: () => import('./CIcon.js'),
  CProgrammingIcon: () => import('./CProgrammingIcon.js'),
  Cabinet01Icon: () => import('./Cabinet01Icon.js'),
  Cabinet02Icon: () => import('./Cabinet02Icon.js'),
  Cabinet03Icon: () => import('./Cabinet03Icon.js'),
  Cabinet04Icon: () => import('./Cabinet04Icon.js'),
  CableCarIcon: () => import('./CableCarIcon.js'),
  CableIcon: () => import('./CableIcon.js'),
  CactusIcon: () => import('./CactusIcon.js'),
  CaduceusIcon: () => import('./CaduceusIcon.js'),
  CafeIcon: () => import('./CafeIcon.js'),
  CakeSliceIcon: () => import('./CakeSliceIcon.js'),
  CakeIcon: () => import('./CakeIcon.js'),
  CalculateIcon: () => import('./CalculateIcon.js'),
  Calculator01Icon: () => import('./Calculator01Icon.js'),
  CalculatorIcon: () => import('./CalculatorIcon.js'),
  Calendar01Icon: () => import('./Calendar01Icon.js'),
  Calendar02Icon: () => import('./Calendar02Icon.js'),
  Calendar03Icon: () => import('./Calendar03Icon.js'),
  Calendar04Icon: () => import('./Calendar04Icon.js'),
  Calendar05Icon: () => import('./Calendar05Icon.js'),
  CalendarAdd01Icon: () => import('./CalendarAdd01Icon.js'),
  CalendarAdd02Icon: () => import('./CalendarAdd02Icon.js'),
  CalendarAnalysisIcon: () => import('./CalendarAnalysisIcon.js'),
  CalendarArrowDownIcon: () => import('./CalendarArrowDownIcon.js'),
  CalendarArrowUpIcon: () => import('./CalendarArrowUpIcon.js'),
  CalendarBlock01Icon: () => import('./CalendarBlock01Icon.js'),
  CalendarBlock02Icon: () => import('./CalendarBlock02Icon.js'),
  CalendarCheckIn01Icon: () => import('./CalendarCheckIn01Icon.js'),
  CalendarCheckIn02Icon: () => import('./CalendarCheckIn02Icon.js'),
  CalendarCheckOut01Icon: () => import('./CalendarCheckOut01Icon.js'),
  CalendarCheckOut02Icon: () => import('./CalendarCheckOut02Icon.js'),
  CalendarCheckIcon: () => import('./CalendarCheckIcon.js'),
  CalendarClockIcon: () => import('./CalendarClockIcon.js'),
  CalendarCogIcon: () => import('./CalendarCogIcon.js'),
  CalendarDaysIcon: () => import('./CalendarDaysIcon.js'),
  CalendarDownload01Icon: () => import('./CalendarDownload01Icon.js'),
  CalendarDownload02Icon: () => import('./CalendarDownload02Icon.js'),
  CalendarFavorite01Icon: () => import('./CalendarFavorite01Icon.js'),
  CalendarFavorite02Icon: () => import('./CalendarFavorite02Icon.js'),
  CalendarFoldIcon: () => import('./CalendarFoldIcon.js'),
  CalendarHeartIcon: () => import('./CalendarHeartIcon.js'),
  CalendarLock01Icon: () => import('./CalendarLock01Icon.js'),
  CalendarLock02Icon: () => import('./CalendarLock02Icon.js'),
  CalendarLove01Icon: () => import('./CalendarLove01Icon.js'),
  CalendarLove02Icon: () => import('./CalendarLove02Icon.js'),
  CalendarMinus01Icon: () => import('./CalendarMinus01Icon.js'),
  CalendarMinus02Icon: () => import('./CalendarMinus02Icon.js'),
  CalendarMortarboardIcon: () => import('./CalendarMortarboardIcon.js'),
  CalendarOffIcon: () => import('./CalendarOffIcon.js'),
  CalendarPlusIcon: () => import('./CalendarPlusIcon.js'),
  CalendarRangeIcon: () => import('./CalendarRangeIcon.js'),
  CalendarRemove01Icon: () => import('./CalendarRemove01Icon.js'),
  CalendarRemove02Icon: () => import('./CalendarRemove02Icon.js'),
  CalendarSearchIcon: () => import('./CalendarSearchIcon.js'),
  CalendarSetting01Icon: () => import('./CalendarSetting01Icon.js'),
  CalendarSetting02Icon: () => import('./CalendarSetting02Icon.js'),
  CalendarSyncIcon: () => import('./CalendarSyncIcon.js'),
  CalendarUpload01Icon: () => import('./CalendarUpload01Icon.js'),
  CalendarUpload02Icon: () => import('./CalendarUpload02Icon.js'),
  CalendarUserIcon: () => import('./CalendarUserIcon.js'),
  CalendarXIcon: () => import('./CalendarXIcon.js'),
  CalendarsIcon: () => import('./CalendarsIcon.js'),
  Call02Icon: () => import('./Call02Icon.js'),
  CallAdd02Icon: () => import('./CallAdd02Icon.js'),
  CallAddIcon: () => import('./CallAddIcon.js'),
  CallBlocked02Icon: () => import('./CallBlocked02Icon.js'),
  CallBlockedIcon: () => import('./CallBlockedIcon.js'),
  CallDisabled02Icon: () => import('./CallDisabled02Icon.js'),
  CallDisabledIcon: () => import('./CallDisabledIcon.js'),
  CallDone02Icon: () => import('./CallDone02Icon.js'),
  CallDoneIcon: () => import('./CallDoneIcon.js'),
  CallEnd01Icon: () => import('./CallEnd01Icon.js'),
  CallEnd02Icon: () => import('./CallEnd02Icon.js'),
  CallEnd03Icon: () => import('./CallEnd03Icon.js'),
  CallEnd04Icon: () => import('./CallEnd04Icon.js'),
  CallIncoming01Icon: () => import('./CallIncoming01Icon.js'),
  CallIncoming02Icon: () => import('./CallIncoming02Icon.js'),
  CallIncoming03Icon: () => import('./CallIncoming03Icon.js'),
  CallIncoming04Icon: () => import('./CallIncoming04Icon.js'),
  CallInternal02Icon: () => import('./CallInternal02Icon.js'),
  CallInternalIcon: () => import('./CallInternalIcon.js'),
  CallLocked02Icon: () => import('./CallLocked02Icon.js'),
  CallLockedIcon: () => import('./CallLockedIcon.js'),
  CallMinus02Icon: () => import('./CallMinus02Icon.js'),
  CallMinusIcon: () => import('./CallMinusIcon.js'),
  CallMissed01Icon: () => import('./CallMissed01Icon.js'),
  CallMissed02Icon: () => import('./CallMissed02Icon.js'),
  CallMissed03Icon: () => import('./CallMissed03Icon.js'),
  CallMissed04Icon: () => import('./CallMissed04Icon.js'),
  CallOutgoing01Icon: () => import('./CallOutgoing01Icon.js'),
  CallOutgoing02Icon: () => import('./CallOutgoing02Icon.js'),
  CallOutgoing03Icon: () => import('./CallOutgoing03Icon.js'),
  CallOutgoing04Icon: () => import('./CallOutgoing04Icon.js'),
  CallPaused02Icon: () => import('./CallPaused02Icon.js'),
  CallPausedIcon: () => import('./CallPausedIcon.js'),
  CallReceived02Icon: () => import('./CallReceived02Icon.js'),
  CallReceivedIcon: () => import('./CallReceivedIcon.js'),
  CallRinging01Icon: () => import('./CallRinging01Icon.js'),
  CallRinging02Icon: () => import('./CallRinging02Icon.js'),
  CallRinging03Icon: () => import('./CallRinging03Icon.js'),
  CallRinging04Icon: () => import('./CallRinging04Icon.js'),
  CallSpark01Icon: () => import('./CallSpark01Icon.js'),
  CallSpark02Icon: () => import('./CallSpark02Icon.js'),
  CallIcon: () => import('./CallIcon.js'),
  CallUnlocked02Icon: () => import('./CallUnlocked02Icon.js'),
  CallUnlockedIcon: () => import('./CallUnlockedIcon.js'),
  Calling02Icon: () => import('./Calling02Icon.js'),
  CallingIcon: () => import('./CallingIcon.js'),
  CamelIcon: () => import('./CamelIcon.js'),
  Camera01Icon: () => import('./Camera01Icon.js'),
  Camera02Icon: () => import('./Camera02Icon.js'),
  Camera03Icon: () => import('./Camera03Icon.js'),
  CameraAdd01Icon: () => import('./CameraAdd01Icon.js'),
  CameraAdd02Icon: () => import('./CameraAdd02Icon.js'),
  CameraAdd03Icon: () => import('./CameraAdd03Icon.js'),
  CameraAiIcon: () => import('./CameraAiIcon.js'),
  CameraAutomatically01Icon: () => import('./CameraAutomatically01Icon.js'),
  CameraAutomatically02Icon: () => import('./CameraAutomatically02Icon.js'),
  CameraLensIcon: () => import('./CameraLensIcon.js'),
  CameraMicrophone01Icon: () => import('./CameraMicrophone01Icon.js'),
  CameraMicrophone02Icon: () => import('./CameraMicrophone02Icon.js'),
  CameraNightMode01Icon: () => import('./CameraNightMode01Icon.js'),
  CameraNightMode02Icon: () => import('./CameraNightMode02Icon.js'),
  CameraOff01Icon: () => import('./CameraOff01Icon.js'),
  CameraOff02Icon: () => import('./CameraOff02Icon.js'),
  CameraRotated01Icon: () => import('./CameraRotated01Icon.js'),
  CameraRotated02Icon: () => import('./CameraRotated02Icon.js'),
  CameraSmile01Icon: () => import('./CameraSmile01Icon.js'),
  CameraSmile02Icon: () => import('./CameraSmile02Icon.js'),
  CameraTripodIcon: () => import('./CameraTripodIcon.js'),
  CameraVideoIcon: () => import('./CameraVideoIcon.js'),
  CamperIcon: () => import('./CamperIcon.js'),
  CampfireIcon: () => import('./CampfireIcon.js'),
  Cancel01Icon: () => import('./Cancel01Icon.js'),
  Cancel02Icon: () => import('./Cancel02Icon.js'),
  CancelCircleHalfDotIcon: () => import('./CancelCircleHalfDotIcon.js'),
  CancelCircleIcon: () => import('./CancelCircleIcon.js'),
  CancelSquareIcon: () => import('./CancelSquareIcon.js'),
  Candelier01Icon: () => import('./Candelier01Icon.js'),
  Candelier02Icon: () => import('./Candelier02Icon.js'),
  CandyCaneIcon: () => import('./CandyCaneIcon.js'),
  CandyOffIcon: () => import('./CandyOffIcon.js'),
  CandyIcon: () => import('./CandyIcon.js'),
  CannabisOffIcon: () => import('./CannabisOffIcon.js'),
  CannabisIcon: () => import('./CannabisIcon.js'),
  CanvasIcon: () => import('./CanvasIcon.js'),
  CapProjectingIcon: () => import('./CapProjectingIcon.js'),
  CapRoundIcon: () => import('./CapRoundIcon.js'),
  CapStraightIcon: () => import('./CapStraightIcon.js'),
  CapIcon: () => import('./CapIcon.js'),
  CapcutRectangleIcon: () => import('./CapcutRectangleIcon.js'),
  CapcutIcon: () => import('./CapcutIcon.js'),
  CaptionsOffIcon: () => import('./CaptionsOffIcon.js'),
  CaptionsIcon: () => import('./CaptionsIcon.js'),
  Car01Icon: () => import('./Car01Icon.js'),
  Car02Icon: () => import('./Car02Icon.js'),
  Car03Icon: () => import('./Car03Icon.js'),
  Car04Icon: () => import('./Car04Icon.js'),
  Car05Icon: () => import('./Car05Icon.js'),
  CarAlertIcon: () => import('./CarAlertIcon.js'),
  CarFrontIcon: () => import('./CarFrontIcon.js'),
  CarParking01Icon: () => import('./CarParking01Icon.js'),
  CarParking02Icon: () => import('./CarParking02Icon.js'),
  CarSignalIcon: () => import('./CarSignalIcon.js'),
  CarTaxiFrontIcon: () => import('./CarTaxiFrontIcon.js'),
  CarTimeIcon: () => import('./CarTimeIcon.js'),
  CaravanIcon: () => import('./CaravanIcon.js'),
  CardExchange01Icon: () => import('./CardExchange01Icon.js'),
  CardExchange02Icon: () => import('./CardExchange02Icon.js'),
  CardSimIcon: () => import('./CardSimIcon.js'),
  CardiganIcon: () => import('./CardiganIcon.js'),
  Cardiogram01Icon: () => import('./Cardiogram01Icon.js'),
  Cardiogram02Icon: () => import('./Cardiogram02Icon.js'),
  Cards01Icon: () => import('./Cards01Icon.js'),
  Cards02Icon: () => import('./Cards02Icon.js'),
  CargoShipIcon: () => import('./CargoShipIcon.js'),
  CarouselHorizontal02Icon: () => import('./CarouselHorizontal02Icon.js'),
  CarouselHorizontalIcon: () => import('./CarouselHorizontalIcon.js'),
  CarouselVerticalIcon: () => import('./CarouselVerticalIcon.js'),
  CarrotIcon: () => import('./CarrotIcon.js'),
  CaseLowerIcon: () => import('./CaseLowerIcon.js'),
  CaseSensitiveIcon: () => import('./CaseSensitiveIcon.js'),
  CaseUpperIcon: () => import('./CaseUpperIcon.js'),
  Cash01Icon: () => import('./Cash01Icon.js'),
  Cash02Icon: () => import('./Cash02Icon.js'),
  CashbackBitcoinIcon: () => import('./CashbackBitcoinIcon.js'),
  CashbackEuroIcon: () => import('./CashbackEuroIcon.js'),
  CashbackPoundIcon: () => import('./CashbackPoundIcon.js'),
  CashbackIcon: () => import('./CashbackIcon.js'),
  CashbackYenIcon: () => import('./CashbackYenIcon.js'),
  Cashier02Icon: () => import('./Cashier02Icon.js'),
  CashierIcon: () => import('./CashierIcon.js'),
  CassetteTapeIcon: () => import('./CassetteTapeIcon.js'),
  CastIcon: () => import('./CastIcon.js'),
  CastboxIcon: () => import('./CastboxIcon.js'),
  Castle01Icon: () => import('./Castle01Icon.js'),
  Castle02Icon: () => import('./Castle02Icon.js'),
  CastleIcon: () => import('./CastleIcon.js'),
  CatIcon: () => import('./CatIcon.js'),
  CatalogueIcon: () => import('./CatalogueIcon.js'),
  CayanTowerIcon: () => import('./CayanTowerIcon.js'),
  CctvCameraIcon: () => import('./CctvCameraIcon.js'),
  CctvOffIcon: () => import('./CctvOffIcon.js'),
  CctvIcon: () => import('./CctvIcon.js'),
  CdIcon: () => import('./CdIcon.js'),
  CellsIcon: () => import('./CellsIcon.js'),
  CellularNetworkOfflineIcon: () => import('./CellularNetworkOfflineIcon.js'),
  CellularNetworkIcon: () => import('./CellularNetworkIcon.js'),
  CelsiusIcon: () => import('./CelsiusIcon.js'),
  CenterFocusIcon: () => import('./CenterFocusIcon.js'),
  CentralShaheedMinarIcon: () => import('./CentralShaheedMinarIcon.js'),
  CentralizedIcon: () => import('./CentralizedIcon.js'),
  Certificate01Icon: () => import('./Certificate01Icon.js'),
  Certificate02Icon: () => import('./Certificate02Icon.js'),
  Chair01Icon: () => import('./Chair01Icon.js'),
  Chair02Icon: () => import('./Chair02Icon.js'),
  Chair03Icon: () => import('./Chair03Icon.js'),
  Chair04Icon: () => import('./Chair04Icon.js'),
  Chair05Icon: () => import('./Chair05Icon.js'),
  ChairBarberIcon: () => import('./ChairBarberIcon.js'),
  ChampionIcon: () => import('./ChampionIcon.js'),
  ChangeScreenModeIcon: () => import('./ChangeScreenModeIcon.js'),
  CharacterPhoneticIcon: () => import('./CharacterPhoneticIcon.js'),
  CharityIcon: () => import('./CharityIcon.js'),
  Chart01Icon: () => import('./Chart01Icon.js'),
  Chart02Icon: () => import('./Chart02Icon.js'),
  Chart03Icon: () => import('./Chart03Icon.js'),
  ChartAnalysisIcon: () => import('./ChartAnalysisIcon.js'),
  ChartAreaIcon: () => import('./ChartAreaIcon.js'),
  ChartAverageIcon: () => import('./ChartAverageIcon.js'),
  ChartBarBigIcon: () => import('./ChartBarBigIcon.js'),
  ChartBarDecreasingIcon: () => import('./ChartBarDecreasingIcon.js'),
  ChartBarIncreasingIcon: () => import('./ChartBarIncreasingIcon.js'),
  ChartBarLineIcon: () => import('./ChartBarLineIcon.js'),
  ChartBarStackedIcon: () => import('./ChartBarStackedIcon.js'),
  ChartBreakoutCircleIcon: () => import('./ChartBreakoutCircleIcon.js'),
  ChartBreakoutSquareIcon: () => import('./ChartBreakoutSquareIcon.js'),
  ChartBubble01Icon: () => import('./ChartBubble01Icon.js'),
  ChartBubble02Icon: () => import('./ChartBubble02Icon.js'),
  ChartCandleIcon: () => import('./ChartCandleIcon.js'),
  ChartCandlestickIcon: () => import('./ChartCandlestickIcon.js'),
  ChartColumnBigIcon: () => import('./ChartColumnBigIcon.js'),
  ChartColumnDecreasingIcon: () => import('./ChartColumnDecreasingIcon.js'),
  ChartColumnIncreasingIcon: () => import('./ChartColumnIncreasingIcon.js'),
  ChartColumnStackedIcon: () => import('./ChartColumnStackedIcon.js'),
  ChartColumnIcon: () => import('./ChartColumnIcon.js'),
  ChartDecreaseIcon: () => import('./ChartDecreaseIcon.js'),
  ChartDownIcon: () => import('./ChartDownIcon.js'),
  ChartEvaluationIcon: () => import('./ChartEvaluationIcon.js'),
  ChartGanttIcon: () => import('./ChartGanttIcon.js'),
  ChartHighLowIcon: () => import('./ChartHighLowIcon.js'),
  ChartHistogramIcon: () => import('./ChartHistogramIcon.js'),
  ChartIncreaseIcon: () => import('./ChartIncreaseIcon.js'),
  ChartLineData01Icon: () => import('./ChartLineData01Icon.js'),
  ChartLineData02Icon: () => import('./ChartLineData02Icon.js'),
  ChartLineData03Icon: () => import('./ChartLineData03Icon.js'),
  ChartLineIcon: () => import('./ChartLineIcon.js'),
  ChartMaximumIcon: () => import('./ChartMaximumIcon.js'),
  ChartMediumIcon: () => import('./ChartMediumIcon.js'),
  ChartMinimumIcon: () => import('./ChartMinimumIcon.js'),
  ChartNetworkIcon: () => import('./ChartNetworkIcon.js'),
  ChartNoAxesColumnDecreasingIcon: () => import('./ChartNoAxesColumnDecreasingIcon.js'),
  ChartNoAxesColumnIncreasingIcon: () => import('./ChartNoAxesColumnIncreasingIcon.js'),
  ChartNoAxesColumnIcon: () => import('./ChartNoAxesColumnIcon.js'),
  ChartNoAxesCombinedIcon: () => import('./ChartNoAxesCombinedIcon.js'),
  ChartNoAxesGanttIcon: () => import('./ChartNoAxesGanttIcon.js'),
  ChartRadarIcon: () => import('./ChartRadarIcon.js'),
  ChartRelationshipIcon: () => import('./ChartRelationshipIcon.js'),
  ChartRingIcon: () => import('./ChartRingIcon.js'),
  ChartRoseIcon: () => import('./ChartRoseIcon.js'),
  ChartScatterIcon: () => import('./ChartScatterIcon.js'),
  ChartSplineIcon: () => import('./ChartSplineIcon.js'),
  ChartIcon: () => import('./ChartIcon.js'),
  ChartUpIcon: () => import('./ChartUpIcon.js'),
  Chat01Icon: () => import('./Chat01Icon.js'),
  ChatAdd01Icon: () => import('./ChatAdd01Icon.js'),
  ChatAddIcon: () => import('./ChatAddIcon.js'),
  ChatBlocked01Icon: () => import('./ChatBlocked01Icon.js'),
  ChatBlockedIcon: () => import('./ChatBlockedIcon.js'),
  ChatBotIcon: () => import('./ChatBotIcon.js'),
  ChatCancel01Icon: () => import('./ChatCancel01Icon.js'),
  ChatCancelIcon: () => import('./ChatCancelIcon.js'),
  ChatDelay01Icon: () => import('./ChatDelay01Icon.js'),
  ChatDelayIcon: () => import('./ChatDelayIcon.js'),
  ChatDone01Icon: () => import('./ChatDone01Icon.js'),
  ChatDoneIcon: () => import('./ChatDoneIcon.js'),
  ChatDownload01Icon: () => import('./ChatDownload01Icon.js'),
  ChatDownloadIcon: () => import('./ChatDownloadIcon.js'),
  ChatEdit01Icon: () => import('./ChatEdit01Icon.js'),
  ChatEditIcon: () => import('./ChatEditIcon.js'),
  ChatFavourite01Icon: () => import('./ChatFavourite01Icon.js'),
  ChatFavouriteIcon: () => import('./ChatFavouriteIcon.js'),
  ChatFeedback01Icon: () => import('./ChatFeedback01Icon.js'),
  ChatFeedbackIcon: () => import('./ChatFeedbackIcon.js'),
  ChatGptIcon: () => import('./ChatGptIcon.js'),
  ChatIncome01Icon: () => import('./ChatIncome01Icon.js'),
  ChatIncomeIcon: () => import('./ChatIncomeIcon.js'),
  ChatLock01Icon: () => import('./ChatLock01Icon.js'),
  ChatLockIcon: () => import('./ChatLockIcon.js'),
  ChatNotification01Icon: () => import('./ChatNotification01Icon.js'),
  ChatNotificationIcon: () => import('./ChatNotificationIcon.js'),
  ChatOutcome01Icon: () => import('./ChatOutcome01Icon.js'),
  ChatOutcomeIcon: () => import('./ChatOutcomeIcon.js'),
  ChatPreview01Icon: () => import('./ChatPreview01Icon.js'),
  ChatPreviewIcon: () => import('./ChatPreviewIcon.js'),
  ChatQuestion01Icon: () => import('./ChatQuestion01Icon.js'),
  ChatQuestionIcon: () => import('./ChatQuestionIcon.js'),
  ChatSearch01Icon: () => import('./ChatSearch01Icon.js'),
  ChatSearchIcon: () => import('./ChatSearchIcon.js'),
  ChatSecure01Icon: () => import('./ChatSecure01Icon.js'),
  ChatSecureIcon: () => import('./ChatSecureIcon.js'),
  ChatSpark01Icon: () => import('./ChatSpark01Icon.js'),
  ChatSparkIcon: () => import('./ChatSparkIcon.js'),
  ChatIcon: () => import('./ChatIcon.js'),
  ChatTranslate01Icon: () => import('./ChatTranslate01Icon.js'),
  ChatTranslateIcon: () => import('./ChatTranslateIcon.js'),
  ChatUnlock01Icon: () => import('./ChatUnlock01Icon.js'),
  ChatUnlockIcon: () => import('./ChatUnlockIcon.js'),
  ChatUpload01Icon: () => import('./ChatUpload01Icon.js'),
  ChatUploadIcon: () => import('./ChatUploadIcon.js'),
  ChatUser01Icon: () => import('./ChatUser01Icon.js'),
  ChatUserIcon: () => import('./ChatUserIcon.js'),
  Chatting01Icon: () => import('./Chatting01Icon.js'),
  CheckCheckIcon: () => import('./CheckCheckIcon.js'),
  CheckLineIcon: () => import('./CheckLineIcon.js'),
  CheckListIcon: () => import('./CheckListIcon.js'),
  CheckIcon: () => import('./CheckIcon.js'),
  CheckUnread01Icon: () => import('./CheckUnread01Icon.js'),
  CheckUnread02Icon: () => import('./CheckUnread02Icon.js'),
  CheckUnread03Icon: () => import('./CheckUnread03Icon.js'),
  CheckUnread04Icon: () => import('./CheckUnread04Icon.js'),
  CheckmarkBadge01Icon: () => import('./CheckmarkBadge01Icon.js'),
  CheckmarkBadge02Icon: () => import('./CheckmarkBadge02Icon.js'),
  CheckmarkBadge03Icon: () => import('./CheckmarkBadge03Icon.js'),
  CheckmarkBadge04Icon: () => import('./CheckmarkBadge04Icon.js'),
  CheckmarkCircle01Icon: () => import('./CheckmarkCircle01Icon.js'),
  CheckmarkCircle02Icon: () => import('./CheckmarkCircle02Icon.js'),
  CheckmarkCircle03Icon: () => import('./CheckmarkCircle03Icon.js'),
  CheckmarkCircle04Icon: () => import('./CheckmarkCircle04Icon.js'),
  CheckmarkSquare01Icon: () => import('./CheckmarkSquare01Icon.js'),
  CheckmarkSquare02Icon: () => import('./CheckmarkSquare02Icon.js'),
  CheckmarkSquare03Icon: () => import('./CheckmarkSquare03Icon.js'),
  CheckmarkSquare04Icon: () => import('./CheckmarkSquare04Icon.js'),
  CheeseCake01Icon: () => import('./CheeseCake01Icon.js'),
  CheeseCake02Icon: () => import('./CheeseCake02Icon.js'),
  CheeseIcon: () => import('./CheeseIcon.js'),
  ChefHatIcon: () => import('./ChefHatIcon.js'),
  ChefIcon: () => import('./ChefIcon.js'),
  Chemistry01Icon: () => import('./Chemistry01Icon.js'),
  Chemistry02Icon: () => import('./Chemistry02Icon.js'),
  Chemistry03Icon: () => import('./Chemistry03Icon.js'),
  CherryIcon: () => import('./CherryIcon.js'),
  Chess01Icon: () => import('./Chess01Icon.js'),
  Chess02Icon: () => import('./Chess02Icon.js'),
  ChessBishopIcon: () => import('./ChessBishopIcon.js'),
  ChessKingIcon: () => import('./ChessKingIcon.js'),
  ChessKnightIcon: () => import('./ChessKnightIcon.js'),
  ChessPawnIcon: () => import('./ChessPawnIcon.js'),
  ChessQueenIcon: () => import('./ChessQueenIcon.js'),
  ChessRookIcon: () => import('./ChessRookIcon.js'),
  ChevronDoubleCloseIcon: () => import('./ChevronDoubleCloseIcon.js'),
  ChevronDownIcon: () => import('./ChevronDownIcon.js'),
  ChevronFirstIcon: () => import('./ChevronFirstIcon.js'),
  ChevronLastIcon: () => import('./ChevronLastIcon.js'),
  ChevronLeftIcon: () => import('./ChevronLeftIcon.js'),
  ChevronRightIcon: () => import('./ChevronRightIcon.js'),
  ChevronUpIcon: () => import('./ChevronUpIcon.js'),
  ChevronsDownIcon: () => import('./ChevronsDownIcon.js'),
  ChevronsDownUpIcon: () => import('./ChevronsDownUpIcon.js'),
  ChevronsLeftRightEllipsisIcon: () => import('./ChevronsLeftRightEllipsisIcon.js'),
  ChevronsLeftRightIcon: () => import('./ChevronsLeftRightIcon.js'),
  ChevronsLeftIcon: () => import('./ChevronsLeftIcon.js'),
  ChevronsRightLeftIcon: () => import('./ChevronsRightLeftIcon.js'),
  ChevronsRightIcon: () => import('./ChevronsRightIcon.js'),
  ChevronsUpIcon: () => import('./ChevronsUpIcon.js'),
  ChickenThighsIcon: () => import('./ChickenThighsIcon.js'),
  ChildIcon: () => import('./ChildIcon.js'),
  ChimneyIcon: () => import('./ChimneyIcon.js'),
  ChinaTempleIcon: () => import('./ChinaTempleIcon.js'),
  Chip02Icon: () => import('./Chip02Icon.js'),
  ChipIcon: () => import('./ChipIcon.js'),
  ChocolateIcon: () => import('./ChocolateIcon.js'),
  ChopsticksIcon: () => import('./ChopsticksIcon.js'),
  ChromeIcon: () => import('./ChromeIcon.js'),
  ChryslerIcon: () => import('./ChryslerIcon.js'),
  ChurchIcon: () => import('./ChurchIcon.js'),
  CigaretteOffIcon: () => import('./CigaretteOffIcon.js'),
  CigaretteIcon: () => import('./CigaretteIcon.js'),
  CinnamonRollIcon: () => import('./CinnamonRollIcon.js'),
  CircleArrowDataTransferDiagonalIcon: () => import('./CircleArrowDataTransferDiagonalIcon.js'),
  CircleArrowDataTransferHorizontalIcon: () => import('./CircleArrowDataTransferHorizontalIcon.js'),
  CircleArrowDataTransferVerticalIcon: () => import('./CircleArrowDataTransferVerticalIcon.js'),
  CircleArrowDiagonal01Icon: () => import('./CircleArrowDiagonal01Icon.js'),
  CircleArrowDiagonal02Icon: () => import('./CircleArrowDiagonal02Icon.js'),
  CircleArrowDown01Icon: () => import('./CircleArrowDown01Icon.js'),
  CircleArrowDown02Icon: () => import('./CircleArrowDown02Icon.js'),
  CircleArrowDown03Icon: () => import('./CircleArrowDown03Icon.js'),
  CircleArrowDownDoubleIcon: () => import('./CircleArrowDownDoubleIcon.js'),
  CircleArrowDownLeftIcon: () => import('./CircleArrowDownLeftIcon.js'),
  CircleArrowDownRightIcon: () => import('./CircleArrowDownRightIcon.js'),
  CircleArrowExpand01Icon: () => import('./CircleArrowExpand01Icon.js'),
  CircleArrowExpand02Icon: () => import('./CircleArrowExpand02Icon.js'),
  CircleArrowHorizontalIcon: () => import('./CircleArrowHorizontalIcon.js'),
  CircleArrowLeft01Icon: () => import('./CircleArrowLeft01Icon.js'),
  CircleArrowLeft02Icon: () => import('./CircleArrowLeft02Icon.js'),
  CircleArrowLeft03Icon: () => import('./CircleArrowLeft03Icon.js'),
  CircleArrowLeftDoubleIcon: () => import('./CircleArrowLeftDoubleIcon.js'),
  CircleArrowLeftRightIcon: () => import('./CircleArrowLeftRightIcon.js'),
  CircleArrowMoveDownLeftIcon: () => import('./CircleArrowMoveDownLeftIcon.js'),
  CircleArrowMoveDownRightIcon: () => import('./CircleArrowMoveDownRightIcon.js'),
  CircleArrowMoveLeftDownIcon: () => import('./CircleArrowMoveLeftDownIcon.js'),
  CircleArrowMoveRightDownIcon: () => import('./CircleArrowMoveRightDownIcon.js'),
  CircleArrowMoveUpLeftIcon: () => import('./CircleArrowMoveUpLeftIcon.js'),
  CircleArrowMoveUpRightIcon: () => import('./CircleArrowMoveUpRightIcon.js'),
  CircleArrowOutDownLeftIcon: () => import('./CircleArrowOutDownLeftIcon.js'),
  CircleArrowOutDownRightIcon: () => import('./CircleArrowOutDownRightIcon.js'),
  CircleArrowOutUpLeftIcon: () => import('./CircleArrowOutUpLeftIcon.js'),
  CircleArrowOutUpRightIcon: () => import('./CircleArrowOutUpRightIcon.js'),
  CircleArrowReload01Icon: () => import('./CircleArrowReload01Icon.js'),
  CircleArrowReload02Icon: () => import('./CircleArrowReload02Icon.js'),
  CircleArrowRight01Icon: () => import('./CircleArrowRight01Icon.js'),
  CircleArrowRight02Icon: () => import('./CircleArrowRight02Icon.js'),
  CircleArrowRight03Icon: () => import('./CircleArrowRight03Icon.js'),
  CircleArrowRightDoubleIcon: () => import('./CircleArrowRightDoubleIcon.js'),
  CircleArrowShrink01Icon: () => import('./CircleArrowShrink01Icon.js'),
  CircleArrowShrink02Icon: () => import('./CircleArrowShrink02Icon.js'),
  CircleArrowUp01Icon: () => import('./CircleArrowUp01Icon.js'),
  CircleArrowUp02Icon: () => import('./CircleArrowUp02Icon.js'),
  CircleArrowUp03Icon: () => import('./CircleArrowUp03Icon.js'),
  CircleArrowUpDoubleIcon: () => import('./CircleArrowUpDoubleIcon.js'),
  CircleArrowUpDownIcon: () => import('./CircleArrowUpDownIcon.js'),
  CircleArrowUpLeftIcon: () => import('./CircleArrowUpLeftIcon.js'),
  CircleArrowUpRight02Icon: () => import('./CircleArrowUpRight02Icon.js'),
  CircleArrowUpRightIcon: () => import('./CircleArrowUpRightIcon.js'),
  CircleArrowVerticalIcon: () => import('./CircleArrowVerticalIcon.js'),
  CircleCheckBigIcon: () => import('./CircleCheckBigIcon.js'),
  CircleCheckIcon: () => import('./CircleCheckIcon.js'),
  CircleChevronDownIcon: () => import('./CircleChevronDownIcon.js'),
  CircleChevronLeftIcon: () => import('./CircleChevronLeftIcon.js'),
  CircleChevronRightIcon: () => import('./CircleChevronRightIcon.js'),
  CircleChevronUpIcon: () => import('./CircleChevronUpIcon.js'),
  CircleDashedIcon: () => import('./CircleDashedIcon.js'),
  CircleDollarSignIcon: () => import('./CircleDollarSignIcon.js'),
  CircleDotDashedIcon: () => import('./CircleDotDashedIcon.js'),
  CircleDotIcon: () => import('./CircleDotIcon.js'),
  CircleEllipsisIcon: () => import('./CircleEllipsisIcon.js'),
  CircleFadingArrowUpIcon: () => import('./CircleFadingArrowUpIcon.js'),
  CircleFadingPlusIcon: () => import('./CircleFadingPlusIcon.js'),
  CircleGaugeIcon: () => import('./CircleGaugeIcon.js'),
  CircleLock01Icon: () => import('./CircleLock01Icon.js'),
  CircleLock02Icon: () => import('./CircleLock02Icon.js'),
  CircleLockAdd01Icon: () => import('./CircleLockAdd01Icon.js'),
  CircleLockAdd02Icon: () => import('./CircleLockAdd02Icon.js'),
  CircleLockCheck01Icon: () => import('./CircleLockCheck01Icon.js'),
  CircleLockCheck02Icon: () => import('./CircleLockCheck02Icon.js'),
  CircleLockMinus01Icon: () => import('./CircleLockMinus01Icon.js'),
  CircleLockMinus02Icon: () => import('./CircleLockMinus02Icon.js'),
  CircleLockRemove01Icon: () => import('./CircleLockRemove01Icon.js'),
  CircleLockRemove02Icon: () => import('./CircleLockRemove02Icon.js'),
  CircleMinusIcon: () => import('./CircleMinusIcon.js'),
  CircleOffIcon: () => import('./CircleOffIcon.js'),
  CircleParkingOffIcon: () => import('./CircleParkingOffIcon.js'),
  CircleParkingIcon: () => import('./CircleParkingIcon.js'),
  CirclePasswordIcon: () => import('./CirclePasswordIcon.js'),
  CirclePileIcon: () => import('./CirclePileIcon.js'),
  CirclePlusIcon: () => import('./CirclePlusIcon.js'),
  CirclePoundSterlingIcon: () => import('./CirclePoundSterlingIcon.js'),
  CirclePowerIcon: () => import('./CirclePowerIcon.js'),
  CircleQuestionMarkIcon: () => import('./CircleQuestionMarkIcon.js'),
  CircleSlashTwoIcon: () => import('./CircleSlashTwoIcon.js'),
  CircleSmallIcon: () => import('./CircleSmallIcon.js'),
  CircleIcon: () => import('./CircleIcon.js'),
  CircleUnlock01Icon: () => import('./CircleUnlock01Icon.js'),
  CircleUnlock02Icon: () => import('./CircleUnlock02Icon.js'),
  CircleXIcon: () => import('./CircleXIcon.js'),
  CircuitBoardIcon: () => import('./CircuitBoardIcon.js'),
  CitrusIcon: () => import('./CitrusIcon.js'),
  City01Icon: () => import('./City01Icon.js'),
  City02Icon: () => import('./City02Icon.js'),
  City03Icon: () => import('./City03Icon.js'),
  ClapperboardIcon: () => import('./ClapperboardIcon.js'),
  Clapping01Icon: () => import('./Clapping01Icon.js'),
  Clapping02Icon: () => import('./Clapping02Icon.js'),
  ClaudeIcon: () => import('./ClaudeIcon.js'),
  CleanIcon: () => import('./CleanIcon.js'),
  CleaningBucketIcon: () => import('./CleaningBucketIcon.js'),
  ClinicIcon: () => import('./ClinicIcon.js'),
  ClipIcon: () => import('./ClipIcon.js'),
  ClipartsIcon: () => import('./ClipartsIcon.js'),
  ClipboardCheckIcon: () => import('./ClipboardCheckIcon.js'),
  ClipboardClockIcon: () => import('./ClipboardClockIcon.js'),
  ClipboardCopyIcon: () => import('./ClipboardCopyIcon.js'),
  ClipboardListIcon: () => import('./ClipboardListIcon.js'),
  ClipboardMinusIcon: () => import('./ClipboardMinusIcon.js'),
  ClipboardPasteIcon: () => import('./ClipboardPasteIcon.js'),
  ClipboardPenLineIcon: () => import('./ClipboardPenLineIcon.js'),
  ClipboardPenIcon: () => import('./ClipboardPenIcon.js'),
  ClipboardPlusIcon: () => import('./ClipboardPlusIcon.js'),
  ClipboardIcon: () => import('./ClipboardIcon.js'),
  ClipboardTypeIcon: () => import('./ClipboardTypeIcon.js'),
  ClipboardXIcon: () => import('./ClipboardXIcon.js'),
  Clock01Icon: () => import('./Clock01Icon.js'),
  Clock02Icon: () => import('./Clock02Icon.js'),
  Clock03Icon: () => import('./Clock03Icon.js'),
  Clock04Icon: () => import('./Clock04Icon.js'),
  Clock05Icon: () => import('./Clock05Icon.js'),
  ClockAddIcon: () => import('./ClockAddIcon.js'),
  ClockAlertIcon: () => import('./ClockAlertIcon.js'),
  ClockArrowDownIcon: () => import('./ClockArrowDownIcon.js'),
  ClockArrowUpIcon: () => import('./ClockArrowUpIcon.js'),
  ClockCheckIcon: () => import('./ClockCheckIcon.js'),
  ClockFadingIcon: () => import('./ClockFadingIcon.js'),
  ClockPlusIcon: () => import('./ClockPlusIcon.js'),
  ClosedCaptionAltIcon: () => import('./ClosedCaptionAltIcon.js'),
  ClosedCaptionIcon: () => import('./ClosedCaptionIcon.js'),
  ClothesIcon: () => import('./ClothesIcon.js'),
  CloudAlertIcon: () => import('./CloudAlertIcon.js'),
  CloudAngledRainIcon: () => import('./CloudAngledRainIcon.js'),
  CloudAngledRainZapIcon: () => import('./CloudAngledRainZapIcon.js'),
  CloudAngledZapIcon: () => import('./CloudAngledZapIcon.js'),
  CloudBackupIcon: () => import('./CloudBackupIcon.js'),
  CloudBigRainIcon: () => import('./CloudBigRainIcon.js'),
  CloudCheckIcon: () => import('./CloudCheckIcon.js'),
  CloudCogIcon: () => import('./CloudCogIcon.js'),
  CloudDownloadIcon: () => import('./CloudDownloadIcon.js'),
  CloudDrizzleIcon: () => import('./CloudDrizzleIcon.js'),
  CloudFastWindIcon: () => import('./CloudFastWindIcon.js'),
  CloudFogIcon: () => import('./CloudFogIcon.js'),
  CloudHailIcon: () => import('./CloudHailIcon.js'),
  CloudHailstoneIcon: () => import('./CloudHailstoneIcon.js'),
  CloudLightningIcon: () => import('./CloudLightningIcon.js'),
  CloudLittleRainIcon: () => import('./CloudLittleRainIcon.js'),
  CloudLittleSnowIcon: () => import('./CloudLittleSnowIcon.js'),
  CloudLoadingIcon: () => import('./CloudLoadingIcon.js'),
  CloudMidRainIcon: () => import('./CloudMidRainIcon.js'),
  CloudMidSnowIcon: () => import('./CloudMidSnowIcon.js'),
  CloudMoonRainIcon: () => import('./CloudMoonRainIcon.js'),
  CloudOffIcon: () => import('./CloudOffIcon.js'),
  CloudRainIcon: () => import('./CloudRainIcon.js'),
  CloudRainWindIcon: () => import('./CloudRainWindIcon.js'),
  CloudSavingDone01Icon: () => import('./CloudSavingDone01Icon.js'),
  CloudSavingDone02Icon: () => import('./CloudSavingDone02Icon.js'),
  CloudServerIcon: () => import('./CloudServerIcon.js'),
  CloudSlowWindIcon: () => import('./CloudSlowWindIcon.js'),
  CloudSnowIcon: () => import('./CloudSnowIcon.js'),
  CloudIcon: () => import('./CloudIcon.js'),
  CloudSunRainIcon: () => import('./CloudSunRainIcon.js'),
  CloudSyncIcon: () => import('./CloudSyncIcon.js'),
  CloudUploadIcon: () => import('./CloudUploadIcon.js'),
  CloudyIcon: () => import('./CloudyIcon.js'),
  CloverIcon: () => import('./CloverIcon.js'),
  ClubIcon: () => import('./ClubIcon.js'),
  Clubs01Icon: () => import('./Clubs01Icon.js'),
  Clubs02Icon: () => import('./Clubs02Icon.js'),
  CoPresentIcon: () => import('./CoPresentIcon.js'),
  CodeCircleIcon: () => import('./CodeCircleIcon.js'),
  CodeFolderIcon: () => import('./CodeFolderIcon.js'),
  CodeSimpleIcon: () => import('./CodeSimpleIcon.js'),
  CodeSquareIcon: () => import('./CodeSquareIcon.js'),
  CodeIcon: () => import('./CodeIcon.js'),
  CodeXmlIcon: () => import('./CodeXmlIcon.js'),
  CodepenIcon: () => import('./CodepenIcon.js'),
  CodesandboxIcon: () => import('./CodesandboxIcon.js'),
  Coffee01Icon: () => import('./Coffee01Icon.js'),
  Coffee02Icon: () => import('./Coffee02Icon.js'),
  Coffee03Icon: () => import('./Coffee03Icon.js'),
  Coffee04Icon: () => import('./Coffee04Icon.js'),
  CoffeeBeansIcon: () => import('./CoffeeBeansIcon.js'),
  CogIcon: () => import('./CogIcon.js'),
  CoinbaseIcon: () => import('./CoinbaseIcon.js'),
  Coins01Icon: () => import('./Coins01Icon.js'),
  Coins02Icon: () => import('./Coins02Icon.js'),
  CoinsBitcoinIcon: () => import('./CoinsBitcoinIcon.js'),
  CoinsDollarIcon: () => import('./CoinsDollarIcon.js'),
  CoinsEuroIcon: () => import('./CoinsEuroIcon.js'),
  CoinsPoundIcon: () => import('./CoinsPoundIcon.js'),
  CoinsSwapIcon: () => import('./CoinsSwapIcon.js'),
  CoinsYenIcon: () => import('./CoinsYenIcon.js'),
  CollapseIcon: () => import('./CollapseIcon.js'),
  CollectionsBookmarkIcon: () => import('./CollectionsBookmarkIcon.js'),
  ColorPickerIcon: () => import('./ColorPickerIcon.js'),
  ColorsIcon: () => import('./ColorsIcon.js'),
  ColosseumIcon: () => import('./ColosseumIcon.js'),
  ColumnDeleteIcon: () => import('./ColumnDeleteIcon.js'),
  ColumnInsertIcon: () => import('./ColumnInsertIcon.js'),
  ColumnsThreeCogIcon: () => import('./ColumnsThreeCogIcon.js'),
  CombineIcon: () => import('./CombineIcon.js'),
  Comet01Icon: () => import('./Comet01Icon.js'),
  Comet02Icon: () => import('./Comet02Icon.js'),
  ComingSoon01Icon: () => import('./ComingSoon01Icon.js'),
  ComingSoon02Icon: () => import('./ComingSoon02Icon.js'),
  CommandLineIcon: () => import('./CommandLineIcon.js'),
  CommandIcon: () => import('./CommandIcon.js'),
  CommentAdd03Icon: () => import('./CommentAdd03Icon.js'),
  CommentBlock03Icon: () => import('./CommentBlock03Icon.js'),
  CommentRemove03Icon: () => import('./CommentRemove03Icon.js'),
  Comment01Icon: () => import('./Comment01Icon.js'),
  Comment02Icon: () => import('./Comment02Icon.js'),
  Comment03Icon: () => import('./Comment03Icon.js'),
  CommentAdd01Icon: () => import('./CommentAdd01Icon.js'),
  CommentAdd02Icon: () => import('./CommentAdd02Icon.js'),
  CommentBlock01Icon: () => import('./CommentBlock01Icon.js'),
  CommentBlock02Icon: () => import('./CommentBlock02Icon.js'),
  CommentRemove01Icon: () => import('./CommentRemove01Icon.js'),
  CommentRemove02Icon: () => import('./CommentRemove02Icon.js'),
  Compass01Icon: () => import('./Compass01Icon.js'),
  CompassIcon: () => import('./CompassIcon.js'),
  ComplaintIcon: () => import('./ComplaintIcon.js'),
  ComponentIcon: () => import('./ComponentIcon.js'),
  ComputerActivityIcon: () => import('./ComputerActivityIcon.js'),
  ComputerAddIcon: () => import('./ComputerAddIcon.js'),
  ComputerArrowDownIcon: () => import('./ComputerArrowDownIcon.js'),
  ComputerArrowUpIcon: () => import('./ComputerArrowUpIcon.js'),
  ComputerChartDownIcon: () => import('./ComputerChartDownIcon.js'),
  ComputerChartUpIcon: () => import('./ComputerChartUpIcon.js'),
  ComputerCheckIcon: () => import('./ComputerCheckIcon.js'),
  ComputerCloudIcon: () => import('./ComputerCloudIcon.js'),
  ComputerDesk01Icon: () => import('./ComputerDesk01Icon.js'),
  ComputerDesk02Icon: () => import('./ComputerDesk02Icon.js'),
  ComputerDesk03Icon: () => import('./ComputerDesk03Icon.js'),
  ComputerDollarIcon: () => import('./ComputerDollarIcon.js'),
  ComputerEthernetIcon: () => import('./ComputerEthernetIcon.js'),
  ComputerPhoneSyncIcon: () => import('./ComputerPhoneSyncIcon.js'),
  ComputerProgramming01Icon: () => import('./ComputerProgramming01Icon.js'),
  ComputerProgramming02Icon: () => import('./ComputerProgramming02Icon.js'),
  ComputerProtectionIcon: () => import('./ComputerProtectionIcon.js'),
  ComputerRemoveIcon: () => import('./ComputerRemoveIcon.js'),
  ComputerScreenShareIcon: () => import('./ComputerScreenShareIcon.js'),
  ComputerSettingsIcon: () => import('./ComputerSettingsIcon.js'),
  ComputerIcon: () => import('./ComputerIcon.js'),
  ComputerTerminal01Icon: () => import('./ComputerTerminal01Icon.js'),
  ComputerTerminal02Icon: () => import('./ComputerTerminal02Icon.js'),
  ComputerUserIcon: () => import('./ComputerUserIcon.js'),
  ComputerVideoCallIcon: () => import('./ComputerVideoCallIcon.js'),
  ComputerVideoIcon: () => import('./ComputerVideoIcon.js'),
  ConciergeBellIcon: () => import('./ConciergeBellIcon.js'),
  Cone01Icon: () => import('./Cone01Icon.js'),
  Cone02Icon: () => import('./Cone02Icon.js'),
  ConferenceIcon: () => import('./ConferenceIcon.js'),
  Configuration01Icon: () => import('./Configuration01Icon.js'),
  Configuration02Icon: () => import('./Configuration02Icon.js'),
  ConfusedIcon: () => import('./ConfusedIcon.js'),
  CongruentToCircleIcon: () => import('./CongruentToCircleIcon.js'),
  CongruentToSquareIcon: () => import('./CongruentToSquareIcon.js'),
  CongruentToIcon: () => import('./CongruentToIcon.js'),
  ConnectIcon: () => import('./ConnectIcon.js'),
  ConsoleIcon: () => import('./ConsoleIcon.js'),
  ConstellationIcon: () => import('./ConstellationIcon.js'),
  ConstructionIcon: () => import('./ConstructionIcon.js'),
  Contact01Icon: () => import('./Contact01Icon.js'),
  Contact02Icon: () => import('./Contact02Icon.js'),
  ContactBookIcon: () => import('./ContactBookIcon.js'),
  ContactRoundIcon: () => import('./ContactRoundIcon.js'),
  ContactIcon: () => import('./ContactIcon.js'),
  ContainerIcon: () => import('./ContainerIcon.js'),
  ContainerTruck01Icon: () => import('./ContainerTruck01Icon.js'),
  ContainerTruck02Icon: () => import('./ContainerTruck02Icon.js'),
  ContainerTruckIcon: () => import('./ContainerTruckIcon.js'),
  ContentWritingIcon: () => import('./ContentWritingIcon.js'),
  ContractsIcon: () => import('./ContractsIcon.js'),
  ContrastIcon: () => import('./ContrastIcon.js'),
  ConversationIcon: () => import('./ConversationIcon.js'),
  CookBookIcon: () => import('./CookBookIcon.js'),
  CookieIcon: () => import('./CookieIcon.js'),
  CookingPotIcon: () => import('./CookingPotIcon.js'),
  Coordinate01Icon: () => import('./Coordinate01Icon.js'),
  Coordinate02Icon: () => import('./Coordinate02Icon.js'),
  CopilotIcon: () => import('./CopilotIcon.js'),
  Copy01Icon: () => import('./Copy01Icon.js'),
  Copy02Icon: () => import('./Copy02Icon.js'),
  CopyCheckIcon: () => import('./CopyCheckIcon.js'),
  CopyLinkIcon: () => import('./CopyLinkIcon.js'),
  CopyMinusIcon: () => import('./CopyMinusIcon.js'),
  CopyPlusIcon: () => import('./CopyPlusIcon.js'),
  CopySlashIcon: () => import('./CopySlashIcon.js'),
  CopyIcon: () => import('./CopyIcon.js'),
  CopyXIcon: () => import('./CopyXIcon.js'),
  CopyleftIcon: () => import('./CopyleftIcon.js'),
  CopyrightIcon: () => import('./CopyrightIcon.js'),
  CornIcon: () => import('./CornIcon.js'),
  CornerDownLeftIcon: () => import('./CornerDownLeftIcon.js'),
  CornerDownRightIcon: () => import('./CornerDownRightIcon.js'),
  CornerLeftDownIcon: () => import('./CornerLeftDownIcon.js'),
  CornerLeftUpIcon: () => import('./CornerLeftUpIcon.js'),
  CornerRightDownIcon: () => import('./CornerRightDownIcon.js'),
  CornerRightUpIcon: () => import('./CornerRightUpIcon.js'),
  CornerUpLeftIcon: () => import('./CornerUpLeftIcon.js'),
  CornerUpRightIcon: () => import('./CornerUpRightIcon.js'),
  CorporateIcon: () => import('./CorporateIcon.js'),
  CosIcon: () => import('./CosIcon.js'),
  Cosine01Icon: () => import('./Cosine01Icon.js'),
  Cosine02Icon: () => import('./Cosine02Icon.js'),
  CottageIcon: () => import('./CottageIcon.js'),
  CottonCandyIcon: () => import('./CottonCandyIcon.js'),
  Coupon01Icon: () => import('./Coupon01Icon.js'),
  Coupon02Icon: () => import('./Coupon02Icon.js'),
  Coupon03Icon: () => import('./Coupon03Icon.js'),
  CouponPercentIcon: () => import('./CouponPercentIcon.js'),
  CourseIcon: () => import('./CourseIcon.js'),
  CourtHouseIcon: () => import('./CourtHouseIcon.js'),
  CourtLawIcon: () => import('./CourtLawIcon.js'),
  CovariateIcon: () => import('./CovariateIcon.js'),
  CovidInfoIcon: () => import('./CovidInfoIcon.js'),
  CowboyHatIcon: () => import('./CowboyHatIcon.js'),
  CppIcon: () => import('./CppIcon.js'),
  CpuChargeIcon: () => import('./CpuChargeIcon.js'),
  CpuSettingsIcon: () => import('./CpuSettingsIcon.js'),
  CpuIcon: () => import('./CpuIcon.js'),
  CrabIcon: () => import('./CrabIcon.js'),
  CraneIcon: () => import('./CraneIcon.js'),
  CrazyIcon: () => import('./CrazyIcon.js'),
  CreativeCommonsIcon: () => import('./CreativeCommonsIcon.js'),
  CreativeMarketIcon: () => import('./CreativeMarketIcon.js'),
  CreditCardAcceptIcon: () => import('./CreditCardAcceptIcon.js'),
  CreditCardAddIcon: () => import('./CreditCardAddIcon.js'),
  CreditCardChangeIcon: () => import('./CreditCardChangeIcon.js'),
  CreditCardDefrostIcon: () => import('./CreditCardDefrostIcon.js'),
  CreditCardFreezeIcon: () => import('./CreditCardFreezeIcon.js'),
  CreditCardNotAcceptIcon: () => import('./CreditCardNotAcceptIcon.js'),
  CreditCardNotFoundIcon: () => import('./CreditCardNotFoundIcon.js'),
  CreditCardPosIcon: () => import('./CreditCardPosIcon.js'),
  CreditCardIcon: () => import('./CreditCardIcon.js'),
  CreditCardValidationIcon: () => import('./CreditCardValidationIcon.js'),
  CricketBatIcon: () => import('./CricketBatIcon.js'),
  CricketHelmetIcon: () => import('./CricketHelmetIcon.js'),
  CroissantIcon: () => import('./CroissantIcon.js'),
  CropIcon: () => import('./CropIcon.js'),
  CrossIcon: () => import('./CrossIcon.js'),
  CrosshairIcon: () => import('./CrosshairIcon.js'),
  CrowdfundingIcon: () => import('./CrowdfundingIcon.js'),
  Crown02Icon: () => import('./Crown02Icon.js'),
  Crown03Icon: () => import('./Crown03Icon.js'),
  CrownMinusIcon: () => import('./CrownMinusIcon.js'),
  CrownPlusIcon: () => import('./CrownPlusIcon.js'),
  CrownIcon: () => import('./CrownIcon.js'),
  CryingIcon: () => import('./CryingIcon.js'),
  CssThreeIcon: () => import('./CssThreeIcon.js'),
  CssFile01Icon: () => import('./CssFile01Icon.js'),
  CssFile02Icon: () => import('./CssFile02Icon.js'),
  Csv01Icon: () => import('./Csv01Icon.js'),
  Csv02Icon: () => import('./Csv02Icon.js'),
  CubeIcon: () => import('./CubeIcon.js'),
  CupSodaIcon: () => import('./CupSodaIcon.js'),
  Cupcake01Icon: () => import('./Cupcake01Icon.js'),
  Cupcake02Icon: () => import('./Cupcake02Icon.js'),
  Cupcake03Icon: () => import('./Cupcake03Icon.js'),
  CurlingIcon: () => import('./CurlingIcon.js'),
  CurrencyIcon: () => import('./CurrencyIcon.js'),
  Cursor01Icon: () => import('./Cursor01Icon.js'),
  Cursor02Icon: () => import('./Cursor02Icon.js'),
  CursorAddSelection01Icon: () => import('./CursorAddSelection01Icon.js'),
  CursorAddSelection02Icon: () => import('./CursorAddSelection02Icon.js'),
  CursorCircleSelection01Icon: () => import('./CursorCircleSelection01Icon.js'),
  CursorCircleSelection02Icon: () => import('./CursorCircleSelection02Icon.js'),
  CursorDisabled01Icon: () => import('./CursorDisabled01Icon.js'),
  CursorDisabled02Icon: () => import('./CursorDisabled02Icon.js'),
  CursorEdit01Icon: () => import('./CursorEdit01Icon.js'),
  CursorEdit02Icon: () => import('./CursorEdit02Icon.js'),
  CursorHold01Icon: () => import('./CursorHold01Icon.js'),
  CursorHold02Icon: () => import('./CursorHold02Icon.js'),
  CursorInWindowIcon: () => import('./CursorInWindowIcon.js'),
  CursorInfo01Icon: () => import('./CursorInfo01Icon.js'),
  CursorInfo02Icon: () => import('./CursorInfo02Icon.js'),
  CursorLoading01Icon: () => import('./CursorLoading01Icon.js'),
  CursorLoading02Icon: () => import('./CursorLoading02Icon.js'),
  CursorMagicSelection01Icon: () => import('./CursorMagicSelection01Icon.js'),
  CursorMagicSelection02Icon: () => import('./CursorMagicSelection02Icon.js'),
  CursorMagicSelection03Icon: () => import('./CursorMagicSelection03Icon.js'),
  CursorMagicSelection04Icon: () => import('./CursorMagicSelection04Icon.js'),
  CursorMove01Icon: () => import('./CursorMove01Icon.js'),
  CursorMove02Icon: () => import('./CursorMove02Icon.js'),
  CursorPointer01Icon: () => import('./CursorPointer01Icon.js'),
  CursorPointer02Icon: () => import('./CursorPointer02Icon.js'),
  CursorProgress01Icon: () => import('./CursorProgress01Icon.js'),
  CursorProgress02Icon: () => import('./CursorProgress02Icon.js'),
  CursorProgress03Icon: () => import('./CursorProgress03Icon.js'),
  CursorProgress04Icon: () => import('./CursorProgress04Icon.js'),
  CursorRectangleSelection01Icon: () => import('./CursorRectangleSelection01Icon.js'),
  CursorRectangleSelection02Icon: () => import('./CursorRectangleSelection02Icon.js'),
  CursorRemoveSelection01Icon: () => import('./CursorRemoveSelection01Icon.js'),
  CursorRemoveSelection02Icon: () => import('./CursorRemoveSelection02Icon.js'),
  CursorTextIcon: () => import('./CursorTextIcon.js'),
  CurtainsIcon: () => import('./CurtainsIcon.js'),
  CurvyLeftDirectionIcon: () => import('./CurvyLeftDirectionIcon.js'),
  CurvyLeftRightDirectionIcon: () => import('./CurvyLeftRightDirectionIcon.js'),
  CurvyRightDirectionIcon: () => import('./CurvyRightDirectionIcon.js'),
  CurvyUpDownDirectionIcon: () => import('./CurvyUpDownDirectionIcon.js'),
  CustomFieldIcon: () => import('./CustomFieldIcon.js'),
  CustomerService01Icon: () => import('./CustomerService01Icon.js'),
  CustomerService02Icon: () => import('./CustomerService02Icon.js'),
  CustomerServiceIcon: () => import('./CustomerServiceIcon.js'),
  CustomerSupportIcon: () => import('./CustomerSupportIcon.js'),
  CustomizeIcon: () => import('./CustomizeIcon.js'),
  Cylinder01Icon: () => import('./Cylinder01Icon.js'),
  Cylinder02Icon: () => import('./Cylinder02Icon.js'),
  Cylinder03Icon: () => import('./Cylinder03Icon.js'),
  Cylinder04Icon: () => import('./Cylinder04Icon.js'),
  DamIcon: () => import('./DamIcon.js'),
  DangerIcon: () => import('./DangerIcon.js'),
  DarkModeIcon: () => import('./DarkModeIcon.js'),
  DartIcon: () => import('./DartIcon.js'),
  DashboardBrowsingIcon: () => import('./DashboardBrowsingIcon.js'),
  DashboardCircleAddIcon: () => import('./DashboardCircleAddIcon.js'),
  DashboardCircleEditIcon: () => import('./DashboardCircleEditIcon.js'),
  DashboardCircleRemoveIcon: () => import('./DashboardCircleRemoveIcon.js'),
  DashboardCircleSettingsIcon: () => import('./DashboardCircleSettingsIcon.js'),
  DashboardCircleIcon: () => import('./DashboardCircleIcon.js'),
  DashboardSpeed01Icon: () => import('./DashboardSpeed01Icon.js'),
  DashboardSpeed02Icon: () => import('./DashboardSpeed02Icon.js'),
  DashboardSquare01Icon: () => import('./DashboardSquare01Icon.js'),
  DashboardSquare02Icon: () => import('./DashboardSquare02Icon.js'),
  DashboardSquare03Icon: () => import('./DashboardSquare03Icon.js'),
  DashboardSquareAddIcon: () => import('./DashboardSquareAddIcon.js'),
  DashboardSquareEditIcon: () => import('./DashboardSquareEditIcon.js'),
  DashboardSquareRemoveIcon: () => import('./DashboardSquareRemoveIcon.js'),
  DashboardSquareSettingIcon: () => import('./DashboardSquareSettingIcon.js'),
  DashedLine01Icon: () => import('./DashedLine01Icon.js'),
  DashedLine02Icon: () => import('./DashedLine02Icon.js'),
  DashedLineCircleIcon: () => import('./DashedLineCircleIcon.js'),
  DataRecoveryIcon: () => import('./DataRecoveryIcon.js'),
  Database01Icon: () => import('./Database01Icon.js'),
  Database02Icon: () => import('./Database02Icon.js'),
  DatabaseAddIcon: () => import('./DatabaseAddIcon.js'),
  DatabaseBackupIcon: () => import('./DatabaseBackupIcon.js'),
  DatabaseExpandIcon: () => import('./DatabaseExpandIcon.js'),
  DatabaseExportIcon: () => import('./DatabaseExportIcon.js'),
  DatabaseImportIcon: () => import('./DatabaseImportIcon.js'),
  DatabaseLightningIcon: () => import('./DatabaseLightningIcon.js'),
  DatabaseLockedIcon: () => import('./DatabaseLockedIcon.js'),
  DatabaseRestoreIcon: () => import('./DatabaseRestoreIcon.js'),
  DatabaseSearchIcon: () => import('./DatabaseSearchIcon.js'),
  DatabaseSettingIcon: () => import('./DatabaseSettingIcon.js'),
  DatabaseIcon: () => import('./DatabaseIcon.js'),
  DatabaseSync01Icon: () => import('./DatabaseSync01Icon.js'),
  DatabaseSyncIcon: () => import('./DatabaseSyncIcon.js'),
  DatabaseZapIcon: () => import('./DatabaseZapIcon.js'),
  DateTimeIcon: () => import('./DateTimeIcon.js'),
  DatesIcon: () => import('./DatesIcon.js'),
  DatevIcon: () => import('./DatevIcon.js'),
  DeadIcon: () => import('./DeadIcon.js'),
  DeepseekIcon: () => import('./DeepseekIcon.js'),
  Delete01Icon: () => import('./Delete01Icon.js'),
  Delete02Icon: () => import('./Delete02Icon.js'),
  Delete03Icon: () => import('./Delete03Icon.js'),
  Delete04Icon: () => import('./Delete04Icon.js'),
  DeleteColumnIcon: () => import('./DeleteColumnIcon.js'),
  DeletePutBackIcon: () => import('./DeletePutBackIcon.js'),
  DeleteRowIcon: () => import('./DeleteRowIcon.js'),
  DeleteThrowIcon: () => import('./DeleteThrowIcon.js'),
  DeliveredSentIcon: () => import('./DeliveredSentIcon.js'),
  DeliveryBox01Icon: () => import('./DeliveryBox01Icon.js'),
  DeliveryBox02Icon: () => import('./DeliveryBox02Icon.js'),
  DeliveryDelay01Icon: () => import('./DeliveryDelay01Icon.js'),
  DeliveryDelay02Icon: () => import('./DeliveryDelay02Icon.js'),
  DeliveryReturn01Icon: () => import('./DeliveryReturn01Icon.js'),
  DeliveryReturn02Icon: () => import('./DeliveryReturn02Icon.js'),
  DeliverySecure01Icon: () => import('./DeliverySecure01Icon.js'),
  DeliverySecure02Icon: () => import('./DeliverySecure02Icon.js'),
  DeliverySent01Icon: () => import('./DeliverySent01Icon.js'),
  DeliverySent02Icon: () => import('./DeliverySent02Icon.js'),
  DeliveryTracking01Icon: () => import('./DeliveryTracking01Icon.js'),
  DeliveryTracking02Icon: () => import('./DeliveryTracking02Icon.js'),
  DeliveryTruck01Icon: () => import('./DeliveryTruck01Icon.js'),
  DeliveryTruck02Icon: () => import('./DeliveryTruck02Icon.js'),
  DeliveryView01Icon: () => import('./DeliveryView01Icon.js'),
  DeliveryView02Icon: () => import('./DeliveryView02Icon.js'),
  DentalBracesIcon: () => import('./DentalBracesIcon.js'),
  DentalBrokenToothIcon: () => import('./DentalBrokenToothIcon.js'),
  DentalCareIcon: () => import('./DentalCareIcon.js'),
  DentalToothIcon: () => import('./DentalToothIcon.js'),
  DepartementIcon: () => import('./DepartementIcon.js'),
  DesertIcon: () => import('./DesertIcon.js'),
  Desk01Icon: () => import('./Desk01Icon.js'),
  Desk02Icon: () => import('./Desk02Icon.js'),
  DeskIcon: () => import('./DeskIcon.js'),
  DessertIcon: () => import('./DessertIcon.js'),
  DetergentIcon: () => import('./DetergentIcon.js'),
  DeveloperIcon: () => import('./DeveloperIcon.js'),
  DeviantartIcon: () => import('./DeviantartIcon.js'),
  DeviceAccessIcon: () => import('./DeviceAccessIcon.js'),
  DiagonalScrollPoint01Icon: () => import('./DiagonalScrollPoint01Icon.js'),
  DiagonalScrollPoint02Icon: () => import('./DiagonalScrollPoint02Icon.js'),
  DialpadCircle01Icon: () => import('./DialpadCircle01Icon.js'),
  DialpadCircle02Icon: () => import('./DialpadCircle02Icon.js'),
  DialpadSquare01Icon: () => import('./DialpadSquare01Icon.js'),
  DialpadSquare02Icon: () => import('./DialpadSquare02Icon.js'),
  DiameterIcon: () => import('./DiameterIcon.js'),
  Diamond01Icon: () => import('./Diamond01Icon.js'),
  Diamond02Icon: () => import('./Diamond02Icon.js'),
  DiamondMinusIcon: () => import('./DiamondMinusIcon.js'),
  DiamondPercentIcon: () => import('./DiamondPercentIcon.js'),
  DiamondPlusIcon: () => import('./DiamondPlusIcon.js'),
  DiamondIcon: () => import('./DiamondIcon.js'),
  DiaperIcon: () => import('./DiaperIcon.js'),
  DiceFaces01Icon: () => import('./DiceFaces01Icon.js'),
  DiceFaces02Icon: () => import('./DiceFaces02Icon.js'),
  DiceFaces03Icon: () => import('./DiceFaces03Icon.js'),
  DiceFaces04Icon: () => import('./DiceFaces04Icon.js'),
  DiceFaces05Icon: () => import('./DiceFaces05Icon.js'),
  DiceFaces06Icon: () => import('./DiceFaces06Icon.js'),
  DiceIcon: () => import('./DiceIcon.js'),
  DicesIcon: () => import('./DicesIcon.js'),
  DigestionIcon: () => import('./DigestionIcon.js'),
  DiggIcon: () => import('./DiggIcon.js'),
  DigitalClockIcon: () => import('./DigitalClockIcon.js'),
  DimSum01Icon: () => import('./DimSum01Icon.js'),
  DimSum02Icon: () => import('./DimSum02Icon.js'),
  DiningTableIcon: () => import('./DiningTableIcon.js'),
  DiplomaIcon: () => import('./DiplomaIcon.js'),
  DirectionLeft01Icon: () => import('./DirectionLeft01Icon.js'),
  DirectionLeft02Icon: () => import('./DirectionLeft02Icon.js'),
  DirectionRight01Icon: () => import('./DirectionRight01Icon.js'),
  DirectionRight02Icon: () => import('./DirectionRight02Icon.js'),
  Directions01Icon: () => import('./Directions01Icon.js'),
  Directions02Icon: () => import('./Directions02Icon.js'),
  DirhamIcon: () => import('./DirhamIcon.js'),
  Disability01Icon: () => import('./Disability01Icon.js'),
  Disability02Icon: () => import('./Disability02Icon.js'),
  DiscTwoIcon: () => import('./DiscTwoIcon.js'),
  DiscThreeIcon: () => import('./DiscThreeIcon.js'),
  DiscAlbumIcon: () => import('./DiscAlbumIcon.js'),
  DiscIcon: () => import('./DiscIcon.js'),
  DiscordIcon: () => import('./DiscordIcon.js'),
  Discount01Icon: () => import('./Discount01Icon.js'),
  DiscountIcon: () => import('./DiscountIcon.js'),
  DiscountTag01Icon: () => import('./DiscountTag01Icon.js'),
  DiscountTag02Icon: () => import('./DiscountTag02Icon.js'),
  DiscoverCircleIcon: () => import('./DiscoverCircleIcon.js'),
  DiscoverSquareIcon: () => import('./DiscoverSquareIcon.js'),
  Dish01Icon: () => import('./Dish01Icon.js'),
  Dish02Icon: () => import('./Dish02Icon.js'),
  DishWasherIcon: () => import('./DishWasherIcon.js'),
  DispleasedIcon: () => import('./DispleasedIcon.js'),
  DistributeHorizontalCenterIcon: () => import('./DistributeHorizontalCenterIcon.js'),
  DistributeHorizontalLeftIcon: () => import('./DistributeHorizontalLeftIcon.js'),
  DistributeHorizontalRightIcon: () => import('./DistributeHorizontalRightIcon.js'),
  DistributeVerticalBottomIcon: () => import('./DistributeVerticalBottomIcon.js'),
  DistributeVerticalCenterIcon: () => import('./DistributeVerticalCenterIcon.js'),
  DistributeVerticalTopIcon: () => import('./DistributeVerticalTopIcon.js'),
  DistributionIcon: () => import('./DistributionIcon.js'),
  DivideSignCircleIcon: () => import('./DivideSignCircleIcon.js'),
  DivideSignSquareIcon: () => import('./DivideSignSquareIcon.js'),
  DivideSignIcon: () => import('./DivideSignIcon.js'),
  Dna01Icon: () => import('./Dna01Icon.js'),
  DnaOffIcon: () => import('./DnaOffIcon.js'),
  DnaIcon: () => import('./DnaIcon.js'),
  DoNotTouch01Icon: () => import('./DoNotTouch01Icon.js'),
  DoNotTouch02Icon: () => import('./DoNotTouch02Icon.js'),
  Doc01Icon: () => import('./Doc01Icon.js'),
  Doc02Icon: () => import('./Doc02Icon.js'),
  DockIcon: () => import('./DockIcon.js'),
  Doctor01Icon: () => import('./Doctor01Icon.js'),
  Doctor02Icon: () => import('./Doctor02Icon.js'),
  Doctor03Icon: () => import('./Doctor03Icon.js'),
  DocumentAttachmentIcon: () => import('./DocumentAttachmentIcon.js'),
  DocumentCodeIcon: () => import('./DocumentCodeIcon.js'),
  DocumentValidationIcon: () => import('./DocumentValidationIcon.js'),
  Dollar01Icon: () => import('./Dollar01Icon.js'),
  Dollar02Icon: () => import('./Dollar02Icon.js'),
  DollarCircleIcon: () => import('./DollarCircleIcon.js'),
  DollarReceive01Icon: () => import('./DollarReceive01Icon.js'),
  DollarReceive02Icon: () => import('./DollarReceive02Icon.js'),
  DollarSend01Icon: () => import('./DollarSend01Icon.js'),
  DollarSend02Icon: () => import('./DollarSend02Icon.js'),
  DollarSignIcon: () => import('./DollarSignIcon.js'),
  DollarSquareIcon: () => import('./DollarSquareIcon.js'),
  DomeIcon: () => import('./DomeIcon.js'),
  DominoIcon: () => import('./DominoIcon.js'),
  DonutIcon: () => import('./DonutIcon.js'),
  Door01Icon: () => import('./Door01Icon.js'),
  Door02Icon: () => import('./Door02Icon.js'),
  DoorClosedLockedIcon: () => import('./DoorClosedLockedIcon.js'),
  DoorClosedIcon: () => import('./DoorClosedIcon.js'),
  DoorLockIcon: () => import('./DoorLockIcon.js'),
  DoorOpenIcon: () => import('./DoorOpenIcon.js'),
  DoorIcon: () => import('./DoorIcon.js'),
  DotIcon: () => import('./DotIcon.js'),
  DoughnutIcon: () => import('./DoughnutIcon.js'),
  Download01Icon: () => import('./Download01Icon.js'),
  Download02Icon: () => import('./Download02Icon.js'),
  Download03Icon: () => import('./Download03Icon.js'),
  Download04Icon: () => import('./Download04Icon.js'),
  Download05Icon: () => import('./Download05Icon.js'),
  Download06Icon: () => import('./Download06Icon.js'),
  DownloadCircle01Icon: () => import('./DownloadCircle01Icon.js'),
  DownloadCircle02Icon: () => import('./DownloadCircle02Icon.js'),
  DownloadSquare01Icon: () => import('./DownloadSquare01Icon.js'),
  DownloadSquare02Icon: () => import('./DownloadSquare02Icon.js'),
  DraftingCompassIcon: () => import('./DraftingCompassIcon.js'),
  Drag01Icon: () => import('./Drag01Icon.js'),
  Drag02Icon: () => import('./Drag02Icon.js'),
  Drag03Icon: () => import('./Drag03Icon.js'),
  Drag04Icon: () => import('./Drag04Icon.js'),
  DragDropHorizontalIcon: () => import('./DragDropHorizontalIcon.js'),
  DragDropIcon: () => import('./DragDropIcon.js'),
  DragDropVerticalIcon: () => import('./DragDropVerticalIcon.js'),
  DragLeft01Icon: () => import('./DragLeft01Icon.js'),
  DragLeft02Icon: () => import('./DragLeft02Icon.js'),
  DragLeft03Icon: () => import('./DragLeft03Icon.js'),
  DragLeft04Icon: () => import('./DragLeft04Icon.js'),
  DragRight01Icon: () => import('./DragRight01Icon.js'),
  DragRight02Icon: () => import('./DragRight02Icon.js'),
  DragRight03Icon: () => import('./DragRight03Icon.js'),
  DragRight04Icon: () => import('./DragRight04Icon.js'),
  DramaIcon: () => import('./DramaIcon.js'),
  DrawingCompassIcon: () => import('./DrawingCompassIcon.js'),
  DrawingModeIcon: () => import('./DrawingModeIcon.js'),
  Dress01Icon: () => import('./Dress01Icon.js'),
  Dress02Icon: () => import('./Dress02Icon.js'),
  Dress03Icon: () => import('./Dress03Icon.js'),
  Dress04Icon: () => import('./Dress04Icon.js'),
  Dress05Icon: () => import('./Dress05Icon.js'),
  Dress06Icon: () => import('./Dress06Icon.js'),
  Dress07Icon: () => import('./Dress07Icon.js'),
  DressingTable01Icon: () => import('./DressingTable01Icon.js'),
  DressingTable02Icon: () => import('./DressingTable02Icon.js'),
  DressingTable03Icon: () => import('./DressingTable03Icon.js'),
  DribbbleIcon: () => import('./DribbbleIcon.js'),
  DrillIcon: () => import('./DrillIcon.js'),
  DrinkIcon: () => import('./DrinkIcon.js'),
  DriveIcon: () => import('./DriveIcon.js'),
  DroneIcon: () => import('./DroneIcon.js'),
  DroolingIcon: () => import('./DroolingIcon.js'),
  DropboxIcon: () => import('./DropboxIcon.js'),
  DropdownFieldTypeIcon: () => import('./DropdownFieldTypeIcon.js'),
  DropletOffIcon: () => import('./DropletOffIcon.js'),
  DropletIcon: () => import('./DropletIcon.js'),
  DropletsIcon: () => import('./DropletsIcon.js'),
  DropperIcon: () => import('./DropperIcon.js'),
  DrumIcon: () => import('./DrumIcon.js'),
  DrumstickIcon: () => import('./DrumstickIcon.js'),
  Ds3ToolIcon: () => import('./Ds3ToolIcon.js'),
  DuaIcon: () => import('./DuaIcon.js'),
  Dumbbell01Icon: () => import('./Dumbbell01Icon.js'),
  Dumbbell02Icon: () => import('./Dumbbell02Icon.js'),
  Dumbbell03Icon: () => import('./Dumbbell03Icon.js'),
  DuplexIcon: () => import('./DuplexIcon.js'),
  EarOffIcon: () => import('./EarOffIcon.js'),
  EarRings01Icon: () => import('./EarRings01Icon.js'),
  EarRings02Icon: () => import('./EarRings02Icon.js'),
  EarRings03Icon: () => import('./EarRings03Icon.js'),
  EarIcon: () => import('./EarIcon.js'),
  EarthLockIcon: () => import('./EarthLockIcon.js'),
  EarthIcon: () => import('./EarthIcon.js'),
  EaseCurveControlPointsIcon: () => import('./EaseCurveControlPointsIcon.js'),
  EaseInControlPointIcon: () => import('./EaseInControlPointIcon.js'),
  EaseInOutIcon: () => import('./EaseInOutIcon.js'),
  EaseInIcon: () => import('./EaseInIcon.js'),
  EaseOutControlPointIcon: () => import('./EaseOutControlPointIcon.js'),
  EaseOutIcon: () => import('./EaseOutIcon.js'),
  EclipseIcon: () => import('./EclipseIcon.js'),
  EcoEnergyIcon: () => import('./EcoEnergyIcon.js'),
  EcoLab01Icon: () => import('./EcoLab01Icon.js'),
  EcoLab02Icon: () => import('./EcoLab02Icon.js'),
  EcoLabIcon: () => import('./EcoLabIcon.js'),
  EcoPowerIcon: () => import('./EcoPowerIcon.js'),
  EdgeStyleIcon: () => import('./EdgeStyleIcon.js'),
  Edit01Icon: () => import('./Edit01Icon.js'),
  Edit02Icon: () => import('./Edit02Icon.js'),
  Edit03Icon: () => import('./Edit03Icon.js'),
  Edit04Icon: () => import('./Edit04Icon.js'),
  EditOff03Icon: () => import('./EditOff03Icon.js'),
  EditOff04Icon: () => import('./EditOff04Icon.js'),
  EditOffIcon: () => import('./EditOffIcon.js'),
  EditRoadIcon: () => import('./EditRoadIcon.js'),
  EditTableIcon: () => import('./EditTableIcon.js'),
  EditUser02Icon: () => import('./EditUser02Icon.js'),
  EggFriedIcon: () => import('./EggFriedIcon.js'),
  EggOffIcon: () => import('./EggOffIcon.js'),
  EggIcon: () => import('./EggIcon.js'),
  EggsIcon: () => import('./EggsIcon.js'),
  EidMubarakIcon: () => import('./EidMubarakIcon.js'),
  EiffelTowerIcon: () => import('./EiffelTowerIcon.js'),
  EightCircleIcon: () => import('./EightCircleIcon.js'),
  EightSquareIcon: () => import('./EightSquareIcon.js'),
  ElearningExchangeIcon: () => import('./ElearningExchangeIcon.js'),
  ElectricHome01Icon: () => import('./ElectricHome01Icon.js'),
  ElectricHome02Icon: () => import('./ElectricHome02Icon.js'),
  ElectricPlugsIcon: () => import('./ElectricPlugsIcon.js'),
  ElectricTower01Icon: () => import('./ElectricTower01Icon.js'),
  ElectricTower02Icon: () => import('./ElectricTower02Icon.js'),
  ElectricWireIcon: () => import('./ElectricWireIcon.js'),
  ElectricityStackIcon: () => import('./ElectricityStackIcon.js'),
  EllipseSelectionIcon: () => import('./EllipseSelectionIcon.js'),
  EllipseIcon: () => import('./EllipseIcon.js'),
  EllipsisIcon: () => import('./EllipsisIcon.js'),
  EllipsisVerticalIcon: () => import('./EllipsisVerticalIcon.js'),
  EncryptIcon: () => import('./EncryptIcon.js'),
  EnergyEllipseIcon: () => import('./EnergyEllipseIcon.js'),
  EnergyRectangleIcon: () => import('./EnergyRectangleIcon.js'),
  EnergyIcon: () => import('./EnergyIcon.js'),
  EngineSlashIcon: () => import('./EngineSlashIcon.js'),
  EngineIcon: () => import('./EngineIcon.js'),
  EnteringGeoFenceIcon: () => import('./EnteringGeoFenceIcon.js'),
  EntranceStairsIcon: () => import('./EntranceStairsIcon.js'),
  EnvatoIcon: () => import('./EnvatoIcon.js'),
  EqualSignCircleIcon: () => import('./EqualSignCircleIcon.js'),
  EqualSignSquareIcon: () => import('./EqualSignSquareIcon.js'),
  EqualSignIcon: () => import('./EqualSignIcon.js'),
  EquipmentBenchPressIcon: () => import('./EquipmentBenchPressIcon.js'),
  EquipmentChestPressIcon: () => import('./EquipmentChestPressIcon.js'),
  EquipmentGym01Icon: () => import('./EquipmentGym01Icon.js'),
  EquipmentGym02Icon: () => import('./EquipmentGym02Icon.js'),
  EquipmentGym03Icon: () => import('./EquipmentGym03Icon.js'),
  EquipmentWeightliftingIcon: () => import('./EquipmentWeightliftingIcon.js'),
  Eraser01Icon: () => import('./Eraser01Icon.js'),
  EraserAddIcon: () => import('./EraserAddIcon.js'),
  EraserAutoIcon: () => import('./EraserAutoIcon.js'),
  EraserIcon: () => import('./EraserIcon.js'),
  Estimate01Icon: () => import('./Estimate01Icon.js'),
  Estimate02Icon: () => import('./Estimate02Icon.js'),
  EthereumEllipseIcon: () => import('./EthereumEllipseIcon.js'),
  EthereumRectangleIcon: () => import('./EthereumRectangleIcon.js'),
  EthereumIcon: () => import('./EthereumIcon.js'),
  EthernetPortIcon: () => import('./EthernetPortIcon.js'),
  EuroCircleIcon: () => import('./EuroCircleIcon.js'),
  EuroReceiveIcon: () => import('./EuroReceiveIcon.js'),
  EuroSendIcon: () => import('./EuroSendIcon.js'),
  EuroSquareIcon: () => import('./EuroSquareIcon.js'),
  EuroIcon: () => import('./EuroIcon.js'),
  EvChargerIcon: () => import('./EvChargerIcon.js'),
  EvChargingIcon: () => import('./EvChargingIcon.js'),
  EvilIcon: () => import('./EvilIcon.js'),
  Exchange01Icon: () => import('./Exchange01Icon.js'),
  Exchange02Icon: () => import('./Exchange02Icon.js'),
  Exchange03Icon: () => import('./Exchange03Icon.js'),
  ExchangeBitcoinIcon: () => import('./ExchangeBitcoinIcon.js'),
  ExchangeDollarIcon: () => import('./ExchangeDollarIcon.js'),
  ExclamationMarkBigSlashIcon: () => import('./ExclamationMarkBigSlashIcon.js'),
  ExclamationMarkBigIcon: () => import('./ExclamationMarkBigIcon.js'),
  ExclamationMarkSlashIcon: () => import('./ExclamationMarkSlashIcon.js'),
  ExclamationMarkIcon: () => import('./ExclamationMarkIcon.js'),
  ExpandParagraphIcon: () => import('./ExpandParagraphIcon.js'),
  ExpandIcon: () => import('./ExpandIcon.js'),
  ExpanderIcon: () => import('./ExpanderIcon.js'),
  ExternalDriveIcon: () => import('./ExternalDriveIcon.js'),
  ExternalLinkIcon: () => import('./ExternalLinkIcon.js'),
  EyeClosedIcon: () => import('./EyeClosedIcon.js'),
  EyeOffIcon: () => import('./EyeOffIcon.js'),
  EyeIcon: () => import('./EyeIcon.js'),
  FaceIdIcon: () => import('./FaceIdIcon.js'),
  Facebook01Icon: () => import('./Facebook01Icon.js'),
  Facebook02Icon: () => import('./Facebook02Icon.js'),
  Factory01Icon: () => import('./Factory01Icon.js'),
  Factory02Icon: () => import('./Factory02Icon.js'),
  FactoryIcon: () => import('./FactoryIcon.js'),
  FahrenheitIcon: () => import('./FahrenheitIcon.js'),
  FallingStarIcon: () => import('./FallingStarIcon.js'),
  Fan01Icon: () => import('./Fan01Icon.js'),
  Fan02Icon: () => import('./Fan02Icon.js'),
  FastForwardIcon: () => import('./FastForwardIcon.js'),
  FastWindIcon: () => import('./FastWindIcon.js'),
  FavouriteCircleIcon: () => import('./FavouriteCircleIcon.js'),
  FavouriteSquareIcon: () => import('./FavouriteSquareIcon.js'),
  FavouriteIcon: () => import('./FavouriteIcon.js'),
  FeatherIcon: () => import('./FeatherIcon.js'),
  Female02Icon: () => import('./Female02Icon.js'),
  FemaleSymbolIcon: () => import('./FemaleSymbolIcon.js'),
  FenceIcon: () => import('./FenceIcon.js'),
  FencingMaskIcon: () => import('./FencingMaskIcon.js'),
  FencingIcon: () => import('./FencingIcon.js'),
  FerrisWheelIcon: () => import('./FerrisWheelIcon.js'),
  FerryBoatIcon: () => import('./FerryBoatIcon.js'),
  FigmaIcon: () => import('./FigmaIcon.js'),
  File01Icon: () => import('./File01Icon.js'),
  File02Icon: () => import('./File02Icon.js'),
  FileAddIcon: () => import('./FileAddIcon.js'),
  FileArchiveIcon: () => import('./FileArchiveIcon.js'),
  FileAttachmentIcon: () => import('./FileAttachmentIcon.js'),
  FileAudioIcon: () => import('./FileAudioIcon.js'),
  FileAxisThreeDIcon: () => import('./FileAxisThreeDIcon.js'),
  FileBadgeIcon: () => import('./FileBadgeIcon.js'),
  FileBitcoinIcon: () => import('./FileBitcoinIcon.js'),
  FileBlockIcon: () => import('./FileBlockIcon.js'),
  FileBookmarkIcon: () => import('./FileBookmarkIcon.js'),
  FileBoxIcon: () => import('./FileBoxIcon.js'),
  FileBracesCornerIcon: () => import('./FileBracesCornerIcon.js'),
  FileBracesIcon: () => import('./FileBracesIcon.js'),
  FileChartColumnIncreasingIcon: () => import('./FileChartColumnIncreasingIcon.js'),
  FileChartColumnIcon: () => import('./FileChartColumnIcon.js'),
  FileChartLineIcon: () => import('./FileChartLineIcon.js'),
  FileChartPieIcon: () => import('./FileChartPieIcon.js'),
  FileCheckCornerIcon: () => import('./FileCheckCornerIcon.js'),
  FileCheckIcon: () => import('./FileCheckIcon.js'),
  FileClockIcon: () => import('./FileClockIcon.js'),
  FileCloudIcon: () => import('./FileCloudIcon.js'),
  FileCodeCornerIcon: () => import('./FileCodeCornerIcon.js'),
  FileCodeIcon: () => import('./FileCodeIcon.js'),
  FileCogIcon: () => import('./FileCogIcon.js'),
  FileCorruptIcon: () => import('./FileCorruptIcon.js'),
  FileDatabaseIcon: () => import('./FileDatabaseIcon.js'),
  FileDiffIcon: () => import('./FileDiffIcon.js'),
  FileDigitIcon: () => import('./FileDigitIcon.js'),
  FileDollarIcon: () => import('./FileDollarIcon.js'),
  FileDownIcon: () => import('./FileDownIcon.js'),
  FileDownloadIcon: () => import('./FileDownloadIcon.js'),
  FileEditIcon: () => import('./FileEditIcon.js'),
  FileEmpty01Icon: () => import('./FileEmpty01Icon.js'),
  FileEmpty02Icon: () => import('./FileEmpty02Icon.js'),
  FileEuroIcon: () => import('./FileEuroIcon.js'),
  FileExclamationPointIcon: () => import('./FileExclamationPointIcon.js'),
  FileExportIcon: () => import('./FileExportIcon.js'),
  FileFavouriteIcon: () => import('./FileFavouriteIcon.js'),
  FileHeadphoneIcon: () => import('./FileHeadphoneIcon.js'),
  FileHeartIcon: () => import('./FileHeartIcon.js'),
  FileImageIcon: () => import('./FileImageIcon.js'),
  FileImportIcon: () => import('./FileImportIcon.js'),
  FileInputIcon: () => import('./FileInputIcon.js'),
  FileKeyIcon: () => import('./FileKeyIcon.js'),
  FileLinkIcon: () => import('./FileLinkIcon.js'),
  FileLockIcon: () => import('./FileLockIcon.js'),
  FileLockedIcon: () => import('./FileLockedIcon.js'),
  FileManagementIcon: () => import('./FileManagementIcon.js'),
  FileMinusIcon: () => import('./FileMinusIcon.js'),
  FileMusicIcon: () => import('./FileMusicIcon.js'),
  FileNotFoundIcon: () => import('./FileNotFoundIcon.js'),
  FilePasteIcon: () => import('./FilePasteIcon.js'),
  FilePenIcon: () => import('./FilePenIcon.js'),
  FilePinIcon: () => import('./FilePinIcon.js'),
  FilePlayIcon: () => import('./FilePlayIcon.js'),
  FilePlusIcon: () => import('./FilePlusIcon.js'),
  FilePoundIcon: () => import('./FilePoundIcon.js'),
  FileQuestionMarkIcon: () => import('./FileQuestionMarkIcon.js'),
  FileRemoveIcon: () => import('./FileRemoveIcon.js'),
  FileScanIcon: () => import('./FileScanIcon.js'),
  FileScriptIcon: () => import('./FileScriptIcon.js'),
  FileSearchIcon: () => import('./FileSearchIcon.js'),
  FileSecurityIcon: () => import('./FileSecurityIcon.js'),
  FileShredderIcon: () => import('./FileShredderIcon.js'),
  FileSignalIcon: () => import('./FileSignalIcon.js'),
  FileSlidersIcon: () => import('./FileSlidersIcon.js'),
  FileSpreadsheetIcon: () => import('./FileSpreadsheetIcon.js'),
  FileStackIcon: () => import('./FileStackIcon.js'),
  FileStarIcon: () => import('./FileStarIcon.js'),
  FileSyncIcon: () => import('./FileSyncIcon.js'),
  FileTerminalIcon: () => import('./FileTerminalIcon.js'),
  FileTypeIcon: () => import('./FileTypeIcon.js'),
  FileUnknownIcon: () => import('./FileUnknownIcon.js'),
  FileUnlockedIcon: () => import('./FileUnlockedIcon.js'),
  FileUpIcon: () => import('./FileUpIcon.js'),
  FileUploadIcon: () => import('./FileUploadIcon.js'),
  FileUserIcon: () => import('./FileUserIcon.js'),
  FileValidationIcon: () => import('./FileValidationIcon.js'),
  FileVerifiedIcon: () => import('./FileVerifiedIcon.js'),
  FileVideoCameraIcon: () => import('./FileVideoCameraIcon.js'),
  FileVideoIcon: () => import('./FileVideoIcon.js'),
  FileViewIcon: () => import('./FileViewIcon.js'),
  FileVolumeIcon: () => import('./FileVolumeIcon.js'),
  FileXCornerIcon: () => import('./FileXCornerIcon.js'),
  FileXIcon: () => import('./FileXIcon.js'),
  FileYenIcon: () => import('./FileYenIcon.js'),
  FileZipIcon: () => import('./FileZipIcon.js'),
  Files01Icon: () => import('./Files01Icon.js'),
  Files02Icon: () => import('./Files02Icon.js'),
  Film01Icon: () => import('./Film01Icon.js'),
  Film02Icon: () => import('./Film02Icon.js'),
  FilmRoll01Icon: () => import('./FilmRoll01Icon.js'),
  FilmRoll02Icon: () => import('./FilmRoll02Icon.js'),
  FilterAddIcon: () => import('./FilterAddIcon.js'),
  FilterEditIcon: () => import('./FilterEditIcon.js'),
  FilterHorizontalIcon: () => import('./FilterHorizontalIcon.js'),
  FilterMailCircleIcon: () => import('./FilterMailCircleIcon.js'),
  FilterMailEditIcon: () => import('./FilterMailEditIcon.js'),
  FilterMailRemoveIcon: () => import('./FilterMailRemoveIcon.js'),
  FilterMailSquareIcon: () => import('./FilterMailSquareIcon.js'),
  FilterMailIcon: () => import('./FilterMailIcon.js'),
  FilterRemoveIcon: () => import('./FilterRemoveIcon.js'),
  FilterResetIcon: () => import('./FilterResetIcon.js'),
  FilterIcon: () => import('./FilterIcon.js'),
  FilterVerticalIcon: () => import('./FilterVerticalIcon.js'),
  FingerAccessIcon: () => import('./FingerAccessIcon.js'),
  FingerPrintAddIcon: () => import('./FingerPrintAddIcon.js'),
  FingerPrintCheckIcon: () => import('./FingerPrintCheckIcon.js'),
  FingerPrintMinusIcon: () => import('./FingerPrintMinusIcon.js'),
  FingerPrintRemoveIcon: () => import('./FingerPrintRemoveIcon.js'),
  FingerPrintScanIcon: () => import('./FingerPrintScanIcon.js'),
  FingerPrintIcon: () => import('./FingerPrintIcon.js'),
  FingerprintPatternIcon: () => import('./FingerprintPatternIcon.js'),
  FinsIcon: () => import('./FinsIcon.js'),
  Fire02Icon: () => import('./Fire02Icon.js'),
  Fire03Icon: () => import('./Fire03Icon.js'),
  FireExtinguisherIcon: () => import('./FireExtinguisherIcon.js'),
  FirePitIcon: () => import('./FirePitIcon.js'),
  FireSecurityIcon: () => import('./FireSecurityIcon.js'),
  FireIcon: () => import('./FireIcon.js'),
  FirewallIcon: () => import('./FirewallIcon.js'),
  FireworksIcon: () => import('./FireworksIcon.js'),
  FirstAidKitIcon: () => import('./FirstAidKitIcon.js'),
  FishFoodIcon: () => import('./FishFoodIcon.js'),
  FishOffIcon: () => import('./FishOffIcon.js'),
  FishIcon: () => import('./FishIcon.js'),
  FishSymbolIcon: () => import('./FishSymbolIcon.js'),
  FishingHookIcon: () => import('./FishingHookIcon.js'),
  FishingRodIcon: () => import('./FishingRodIcon.js'),
  FitToScreenIcon: () => import('./FitToScreenIcon.js'),
  FiveCircleIcon: () => import('./FiveCircleIcon.js'),
  FiveSquareIcon: () => import('./FiveSquareIcon.js'),
  FiverrIcon: () => import('./FiverrIcon.js'),
  Flag01Icon: () => import('./Flag01Icon.js'),
  Flag02Icon: () => import('./Flag02Icon.js'),
  Flag03Icon: () => import('./Flag03Icon.js'),
  FlagOffIcon: () => import('./FlagOffIcon.js'),
  FlagTriangleLeftIcon: () => import('./FlagTriangleLeftIcon.js'),
  FlagTriangleRightIcon: () => import('./FlagTriangleRightIcon.js'),
  FlameKindlingIcon: () => import('./FlameKindlingIcon.js'),
  FlameIcon: () => import('./FlameIcon.js'),
  FlashOffIcon: () => import('./FlashOffIcon.js'),
  FlashIcon: () => import('./FlashIcon.js'),
  FlashlightOffIcon: () => import('./FlashlightOffIcon.js'),
  FlashlightIcon: () => import('./FlashlightIcon.js'),
  FlaskConicalOffIcon: () => import('./FlaskConicalOffIcon.js'),
  FlaskConicalIcon: () => import('./FlaskConicalIcon.js'),
  FlaskRoundIcon: () => import('./FlaskRoundIcon.js'),
  FlaticonIcon: () => import('./FlaticonIcon.js'),
  FlickrIcon: () => import('./FlickrIcon.js'),
  FlimSlateIcon: () => import('./FlimSlateIcon.js'),
  FlipBottomIcon: () => import('./FlipBottomIcon.js'),
  FlipHorizontalIcon: () => import('./FlipHorizontalIcon.js'),
  FlipLeftIcon: () => import('./FlipLeftIcon.js'),
  FlipPhoneIcon: () => import('./FlipPhoneIcon.js'),
  FlipRightIcon: () => import('./FlipRightIcon.js'),
  FlipTopIcon: () => import('./FlipTopIcon.js'),
  FlipVerticalIcon: () => import('./FlipVerticalIcon.js'),
  FloorPlanIcon: () => import('./FloorPlanIcon.js'),
  FloppyDiskIcon: () => import('./FloppyDiskIcon.js'),
  FlowCircleIcon: () => import('./FlowCircleIcon.js'),
  FlowConnectionIcon: () => import('./FlowConnectionIcon.js'),
  FlowSquareIcon: () => import('./FlowSquareIcon.js'),
  FlowIcon: () => import('./FlowIcon.js'),
  Flowchart01Icon: () => import('./Flowchart01Icon.js'),
  Flowchart02Icon: () => import('./Flowchart02Icon.js'),
  FlowerPotIcon: () => import('./FlowerPotIcon.js'),
  FlowerIcon: () => import('./FlowerIcon.js'),
  FlushedIcon: () => import('./FlushedIcon.js'),
  FlyingHumanIcon: () => import('./FlyingHumanIcon.js'),
  FocusPointIcon: () => import('./FocusPointIcon.js'),
  FocusIcon: () => import('./FocusIcon.js'),
  FoldHorizontalIcon: () => import('./FoldHorizontalIcon.js'),
  FoldVerticalIcon: () => import('./FoldVerticalIcon.js'),
  Folder01Icon: () => import('./Folder01Icon.js'),
  Folder02Icon: () => import('./Folder02Icon.js'),
  Folder03Icon: () => import('./Folder03Icon.js'),
  FolderAddIcon: () => import('./FolderAddIcon.js'),
  FolderArchiveIcon: () => import('./FolderArchiveIcon.js'),
  FolderAttachmentIcon: () => import('./FolderAttachmentIcon.js'),
  FolderAudioIcon: () => import('./FolderAudioIcon.js'),
  FolderBlockIcon: () => import('./FolderBlockIcon.js'),
  FolderCheckIcon: () => import('./FolderCheckIcon.js'),
  FolderClockIcon: () => import('./FolderClockIcon.js'),
  FolderCloudIcon: () => import('./FolderCloudIcon.js'),
  FolderCodeIcon: () => import('./FolderCodeIcon.js'),
  FolderCogIcon: () => import('./FolderCogIcon.js'),
  FolderDetailsReferenceIcon: () => import('./FolderDetailsReferenceIcon.js'),
  FolderDetailsIcon: () => import('./FolderDetailsIcon.js'),
  FolderDotIcon: () => import('./FolderDotIcon.js'),
  FolderDownloadIcon: () => import('./FolderDownloadIcon.js'),
  FolderEditIcon: () => import('./FolderEditIcon.js'),
  FolderExportIcon: () => import('./FolderExportIcon.js'),
  FolderFavouriteIcon: () => import('./FolderFavouriteIcon.js'),
  FolderFileStorageIcon: () => import('./FolderFileStorageIcon.js'),
  FolderGitTwoIcon: () => import('./FolderGitTwoIcon.js'),
  FolderGitIcon: () => import('./FolderGitIcon.js'),
  FolderHeartIcon: () => import('./FolderHeartIcon.js'),
  FolderImportIcon: () => import('./FolderImportIcon.js'),
  FolderInputIcon: () => import('./FolderInputIcon.js'),
  FolderKanbanIcon: () => import('./FolderKanbanIcon.js'),
  FolderKeyIcon: () => import('./FolderKeyIcon.js'),
  FolderLibraryIcon: () => import('./FolderLibraryIcon.js'),
  FolderLinksIcon: () => import('./FolderLinksIcon.js'),
  FolderLockedIcon: () => import('./FolderLockedIcon.js'),
  FolderManagementIcon: () => import('./FolderManagementIcon.js'),
  FolderMinusIcon: () => import('./FolderMinusIcon.js'),
  FolderMusicIcon: () => import('./FolderMusicIcon.js'),
  FolderOffIcon: () => import('./FolderOffIcon.js'),
  FolderOpenIcon: () => import('./FolderOpenIcon.js'),
  FolderOutputIcon: () => import('./FolderOutputIcon.js'),
  FolderPenIcon: () => import('./FolderPenIcon.js'),
  FolderPinIcon: () => import('./FolderPinIcon.js'),
  FolderRemoveIcon: () => import('./FolderRemoveIcon.js'),
  FolderRootIcon: () => import('./FolderRootIcon.js'),
  FolderSearchTwoIcon: () => import('./FolderSearchTwoIcon.js'),
  FolderSearchIcon: () => import('./FolderSearchIcon.js'),
  FolderSecurityIcon: () => import('./FolderSecurityIcon.js'),
  FolderShared01Icon: () => import('./FolderShared01Icon.js'),
  FolderShared02Icon: () => import('./FolderShared02Icon.js'),
  FolderShared03Icon: () => import('./FolderShared03Icon.js'),
  FolderSymlinkIcon: () => import('./FolderSymlinkIcon.js'),
  FolderSyncIcon: () => import('./FolderSyncIcon.js'),
  FolderTransferIcon: () => import('./FolderTransferIcon.js'),
  FolderTreeIcon: () => import('./FolderTreeIcon.js'),
  FolderUnknownIcon: () => import('./FolderUnknownIcon.js'),
  FolderUnlockedIcon: () => import('./FolderUnlockedIcon.js'),
  FolderUploadIcon: () => import('./FolderUploadIcon.js'),
  FolderVideoIcon: () => import('./FolderVideoIcon.js'),
  FolderViewIcon: () => import('./FolderViewIcon.js'),
  FolderZipIcon: () => import('./FolderZipIcon.js'),
  FoldersIcon: () => import('./FoldersIcon.js'),
  FootballPitchIcon: () => import('./FootballPitchIcon.js'),
  FootballIcon: () => import('./FootballIcon.js'),
  FootprintsIcon: () => import('./FootprintsIcon.js'),
  ForgotPasswordIcon: () => import('./ForgotPasswordIcon.js'),
  ForkIcon: () => import('./ForkIcon.js'),
  ForkliftIcon: () => import('./ForkliftIcon.js'),
  FormIcon: () => import('./FormIcon.js'),
  ForrstIcon: () => import('./ForrstIcon.js'),
  FortressIcon: () => import('./FortressIcon.js'),
  Forward01Icon: () => import('./Forward01Icon.js'),
  Forward02Icon: () => import('./Forward02Icon.js'),
  FourCircleIcon: () => import('./FourCircleIcon.js'),
  FourFinger02Icon: () => import('./FourFinger02Icon.js'),
  FourFinger03Icon: () => import('./FourFinger03Icon.js'),
  FourSquareIcon: () => import('./FourSquareIcon.js'),
  FrameIcon: () => import('./FrameIcon.js'),
  FramerIcon: () => import('./FramerIcon.js'),
  FrameworksIcon: () => import('./FrameworksIcon.js'),
  FrenchFries01Icon: () => import('./FrenchFries01Icon.js'),
  FrenchFries02Icon: () => import('./FrenchFries02Icon.js'),
  FridgeIcon: () => import('./FridgeIcon.js'),
  FrisbeeIcon: () => import('./FrisbeeIcon.js'),
  FrownIcon: () => import('./FrownIcon.js'),
  FryIcon: () => import('./FryIcon.js'),
  Fuel01Icon: () => import('./Fuel01Icon.js'),
  Fuel02Icon: () => import('./Fuel02Icon.js'),
  FuelStationIcon: () => import('./FuelStationIcon.js'),
  FuelIcon: () => import('./FuelIcon.js'),
  FullScreenIcon: () => import('./FullScreenIcon.js'),
  FullSignalIcon: () => import('./FullSignalIcon.js'),
  FunctionCircleIcon: () => import('./FunctionCircleIcon.js'),
  FunctionOfXIcon: () => import('./FunctionOfXIcon.js'),
  FunctionSquareIcon: () => import('./FunctionSquareIcon.js'),
  FunctionIcon: () => import('./FunctionIcon.js'),
  FunnelPlusIcon: () => import('./FunnelPlusIcon.js'),
  FunnelIcon: () => import('./FunnelIcon.js'),
  FunnelXIcon: () => import('./FunnelXIcon.js'),
  GalaxyIcon: () => import('./GalaxyIcon.js'),
  GalleryHorizontalEndIcon: () => import('./GalleryHorizontalEndIcon.js'),
  GalleryHorizontalIcon: () => import('./GalleryHorizontalIcon.js'),
  GalleryThumbnailsIcon: () => import('./GalleryThumbnailsIcon.js'),
  GalleryVerticalEndIcon: () => import('./GalleryVerticalEndIcon.js'),
  GalleryVerticalIcon: () => import('./GalleryVerticalIcon.js'),
  GameController01Icon: () => import('./GameController01Icon.js'),
  GameController02Icon: () => import('./GameController02Icon.js'),
  GameController03Icon: () => import('./GameController03Icon.js'),
  GameIcon: () => import('./GameIcon.js'),
  GameboyIcon: () => import('./GameboyIcon.js'),
  GamepadDirectionalIcon: () => import('./GamepadDirectionalIcon.js'),
  GamepadIcon: () => import('./GamepadIcon.js'),
  GarageIcon: () => import('./GarageIcon.js'),
  GarbageTruckIcon: () => import('./GarbageTruckIcon.js'),
  GarlandsIcon: () => import('./GarlandsIcon.js'),
  GasPipeIcon: () => import('./GasPipeIcon.js'),
  GasStoveIcon: () => import('./GasStoveIcon.js'),
  GaugeIcon: () => import('./GaugeIcon.js'),
  GavelIcon: () => import('./GavelIcon.js'),
  GearsIcon: () => import('./GearsIcon.js'),
  GemIcon: () => import('./GemIcon.js'),
  GeologyCrustIcon: () => import('./GeologyCrustIcon.js'),
  GeometricShapes01Icon: () => import('./GeometricShapes01Icon.js'),
  GeometricShapes02Icon: () => import('./GeometricShapes02Icon.js'),
  GeorgianLariIcon: () => import('./GeorgianLariIcon.js'),
  GhostIcon: () => import('./GhostIcon.js'),
  GibbousMoonIcon: () => import('./GibbousMoonIcon.js'),
  Gif01Icon: () => import('./Gif01Icon.js'),
  Gif02Icon: () => import('./Gif02Icon.js'),
  GiftCard02Icon: () => import('./GiftCard02Icon.js'),
  GiftCardIcon: () => import('./GiftCardIcon.js'),
  GiftIcon: () => import('./GiftIcon.js'),
  GitBranchMinusIcon: () => import('./GitBranchMinusIcon.js'),
  GitBranchPlusIcon: () => import('./GitBranchPlusIcon.js'),
  GitBranchIcon: () => import('./GitBranchIcon.js'),
  GitCommitHorizontalIcon: () => import('./GitCommitHorizontalIcon.js'),
  GitCommitIcon: () => import('./GitCommitIcon.js'),
  GitCommitVerticalIcon: () => import('./GitCommitVerticalIcon.js'),
  GitCompareArrowsIcon: () => import('./GitCompareArrowsIcon.js'),
  GitCompareIcon: () => import('./GitCompareIcon.js'),
  GitForkIcon: () => import('./GitForkIcon.js'),
  GitGraphIcon: () => import('./GitGraphIcon.js'),
  GitMergeConflictIcon: () => import('./GitMergeConflictIcon.js'),
  GitMergeIcon: () => import('./GitMergeIcon.js'),
  GitPullRequestArrowIcon: () => import('./GitPullRequestArrowIcon.js'),
  GitPullRequestClosedIcon: () => import('./GitPullRequestClosedIcon.js'),
  GitPullRequestCreateArrowIcon: () => import('./GitPullRequestCreateArrowIcon.js'),
  GitPullRequestCreateIcon: () => import('./GitPullRequestCreateIcon.js'),
  GitPullRequestDraftIcon: () => import('./GitPullRequestDraftIcon.js'),
  GitPullRequestIcon: () => import('./GitPullRequestIcon.js'),
  GitbookIcon: () => import('./GitbookIcon.js'),
  Github01Icon: () => import('./Github01Icon.js'),
  GithubIcon: () => import('./GithubIcon.js'),
  GitlabIcon: () => import('./GitlabIcon.js'),
  GiveBloodIcon: () => import('./GiveBloodIcon.js'),
  GivePillIcon: () => import('./GivePillIcon.js'),
  GlassWaterIcon: () => import('./GlassWaterIcon.js'),
  GlassesIcon: () => import('./GlassesIcon.js'),
  GlobalEditingIcon: () => import('./GlobalEditingIcon.js'),
  GlobalEducationIcon: () => import('./GlobalEducationIcon.js'),
  GlobalRefreshIcon: () => import('./GlobalRefreshIcon.js'),
  GlobalSearchIcon: () => import('./GlobalSearchIcon.js'),
  GlobalIcon: () => import('./GlobalIcon.js'),
  Globe02Icon: () => import('./Globe02Icon.js'),
  GlobeLockIcon: () => import('./GlobeLockIcon.js'),
  GlobeOffIcon: () => import('./GlobeOffIcon.js'),
  GlobeIcon: () => import('./GlobeIcon.js'),
  GlobeXIcon: () => import('./GlobeXIcon.js'),
  GloveIcon: () => import('./GloveIcon.js'),
  GnomeIcon: () => import('./GnomeIcon.js'),
  GoBackward10SecIcon: () => import('./GoBackward10SecIcon.js'),
  GoBackward15SecIcon: () => import('./GoBackward15SecIcon.js'),
  GoBackward30SecIcon: () => import('./GoBackward30SecIcon.js'),
  GoBackwardFiveSecIcon: () => import('./GoBackwardFiveSecIcon.js'),
  GoBackward60SecIcon: () => import('./GoBackward60SecIcon.js'),
  GoForward10SecIcon: () => import('./GoForward10SecIcon.js'),
  GoForward15SecIcon: () => import('./GoForward15SecIcon.js'),
  GoForward30SecIcon: () => import('./GoForward30SecIcon.js'),
  GoForwardFiveSecIcon: () => import('./GoForwardFiveSecIcon.js'),
  GoForward60SecIcon: () => import('./GoForward60SecIcon.js'),
  GoalIcon: () => import('./GoalIcon.js'),
  GoldBuyIcon: () => import('./GoldBuyIcon.js'),
  GoldIngotsIcon: () => import('./GoldIngotsIcon.js'),
  GoldSellIcon: () => import('./GoldSellIcon.js'),
  GoldIcon: () => import('./GoldIcon.js'),
  GolfBallIcon: () => import('./GolfBallIcon.js'),
  GolfBatIcon: () => import('./GolfBatIcon.js'),
  GolfCartIcon: () => import('./GolfCartIcon.js'),
  GolfHoleIcon: () => import('./GolfHoleIcon.js'),
  GoogleDocIcon: () => import('./GoogleDocIcon.js'),
  GoogleDriveIcon: () => import('./GoogleDriveIcon.js'),
  GoogleGeminiIcon: () => import('./GoogleGeminiIcon.js'),
  GoogleHomeIcon: () => import('./GoogleHomeIcon.js'),
  GoogleLensIcon: () => import('./GoogleLensIcon.js'),
  GoogleMapsIcon: () => import('./GoogleMapsIcon.js'),
  GooglePhotosIcon: () => import('./GooglePhotosIcon.js'),
  GoogleSheetIcon: () => import('./GoogleSheetIcon.js'),
  GoogleIcon: () => import('./GoogleIcon.js'),
  Gps01Icon: () => import('./Gps01Icon.js'),
  Gps02Icon: () => import('./Gps02Icon.js'),
  GpsDisconnectedIcon: () => import('./GpsDisconnectedIcon.js'),
  GpsOff01Icon: () => import('./GpsOff01Icon.js'),
  GpsOff02Icon: () => import('./GpsOff02Icon.js'),
  GpsSignal01Icon: () => import('./GpsSignal01Icon.js'),
  GpsSignal02Icon: () => import('./GpsSignal02Icon.js'),
  GpuIcon: () => import('./GpuIcon.js'),
  GraduateFemaleIcon: () => import('./GraduateFemaleIcon.js'),
  GraduateMaleIcon: () => import('./GraduateMaleIcon.js'),
  GraduationCapIcon: () => import('./GraduationCapIcon.js'),
  GraduationScrollIcon: () => import('./GraduationScrollIcon.js'),
  GrapeIcon: () => import('./GrapeIcon.js'),
  GrapesIcon: () => import('./GrapesIcon.js'),
  GraphicCardIcon: () => import('./GraphicCardIcon.js'),
  GravityIcon: () => import('./GravityIcon.js'),
  GreaterThanCircleIcon: () => import('./GreaterThanCircleIcon.js'),
  GreaterThanSquareIcon: () => import('./GreaterThanSquareIcon.js'),
  GreaterThanIcon: () => import('./GreaterThanIcon.js'),
  GreekHelmetIcon: () => import('./GreekHelmetIcon.js'),
  GreenHouseIcon: () => import('./GreenHouseIcon.js'),
  Grid02Icon: () => import('./Grid02Icon.js'),
  GridOffIcon: () => import('./GridOffIcon.js'),
  GridIcon: () => import('./GridIcon.js'),
  GridTableIcon: () => import('./GridTableIcon.js'),
  GridViewIcon: () => import('./GridViewIcon.js'),
  Grid2X2CheckIcon: () => import('./Grid2X2CheckIcon.js'),
  Grid2X2PlusIcon: () => import('./Grid2X2PlusIcon.js'),
  Grid2X2Icon: () => import('./Grid2X2Icon.js'),
  Grid2X2XIcon: () => import('./Grid2X2XIcon.js'),
  Grid3X2Icon: () => import('./Grid3X2Icon.js'),
  Grid3X3Icon: () => import('./Grid3X3Icon.js'),
  GrimacingIcon: () => import('./GrimacingIcon.js'),
  GrinningIcon: () => import('./GrinningIcon.js'),
  GripHorizontalIcon: () => import('./GripHorizontalIcon.js'),
  GripIcon: () => import('./GripIcon.js'),
  GripVerticalIcon: () => import('./GripVerticalIcon.js'),
  Grok02Icon: () => import('./Grok02Icon.js'),
  GrokIcon: () => import('./GrokIcon.js'),
  Group01Icon: () => import('./Group01Icon.js'),
  GroupItemsIcon: () => import('./GroupItemsIcon.js'),
  GroupLayersIcon: () => import('./GroupLayersIcon.js'),
  GroupIcon: () => import('./GroupIcon.js'),
  GrowwIcon: () => import('./GrowwIcon.js'),
  GuestHouseIcon: () => import('./GuestHouseIcon.js'),
  GuitarIcon: () => import('./GuitarIcon.js'),
  GunIcon: () => import('./GunIcon.js'),
  GymnasticRingsIcon: () => import('./GymnasticRingsIcon.js'),
  GymnasticIcon: () => import('./GymnasticIcon.js'),
  HackerrankIcon: () => import('./HackerrankIcon.js'),
  HairClipsIcon: () => import('./HairClipsIcon.js'),
  HairDryerIcon: () => import('./HairDryerIcon.js'),
  HajiIcon: () => import('./HajiIcon.js'),
  HalalLabIcon: () => import('./HalalLabIcon.js'),
  HalalIcon: () => import('./HalalIcon.js'),
  HamIcon: () => import('./HamIcon.js'),
  Hamburger01Icon: () => import('./Hamburger01Icon.js'),
  Hamburger02Icon: () => import('./Hamburger02Icon.js'),
  HammerIcon: () => import('./HammerIcon.js'),
  HandBag01Icon: () => import('./HandBag01Icon.js'),
  HandBag02Icon: () => import('./HandBag02Icon.js'),
  HandBeaterIcon: () => import('./HandBeaterIcon.js'),
  HandCoinsIcon: () => import('./HandCoinsIcon.js'),
  HandFistIcon: () => import('./HandFistIcon.js'),
  HandGrabIcon: () => import('./HandGrabIcon.js'),
  HandGripIcon: () => import('./HandGripIcon.js'),
  HandHeartIcon: () => import('./HandHeartIcon.js'),
  HandHelpingIcon: () => import('./HandHelpingIcon.js'),
  HandMetalIcon: () => import('./HandMetalIcon.js'),
  HandPlatterIcon: () => import('./HandPlatterIcon.js'),
  HandPointingDown01Icon: () => import('./HandPointingDown01Icon.js'),
  HandPointingDown02Icon: () => import('./HandPointingDown02Icon.js'),
  HandPointingDown03Icon: () => import('./HandPointingDown03Icon.js'),
  HandPointingDown04Icon: () => import('./HandPointingDown04Icon.js'),
  HandPointingLeft01Icon: () => import('./HandPointingLeft01Icon.js'),
  HandPointingLeft02Icon: () => import('./HandPointingLeft02Icon.js'),
  HandPointingLeft03Icon: () => import('./HandPointingLeft03Icon.js'),
  HandPointingLeft04Icon: () => import('./HandPointingLeft04Icon.js'),
  HandPointingRight01Icon: () => import('./HandPointingRight01Icon.js'),
  HandPointingRight02Icon: () => import('./HandPointingRight02Icon.js'),
  HandPointingRight03Icon: () => import('./HandPointingRight03Icon.js'),
  HandPointingRight04Icon: () => import('./HandPointingRight04Icon.js'),
  HandPrayerIcon: () => import('./HandPrayerIcon.js'),
  HandSanitizerIcon: () => import('./HandSanitizerIcon.js'),
  HandIcon: () => import('./HandIcon.js'),
  HandbagIcon: () => import('./HandbagIcon.js'),
  HandcuffsIcon: () => import('./HandcuffsIcon.js'),
  HandsClappingIcon: () => import('./HandsClappingIcon.js'),
  HandshakeIcon: () => import('./HandshakeIcon.js'),
  HangerIcon: () => import('./HangerIcon.js'),
  HangingClockIcon: () => import('./HangingClockIcon.js'),
  HangoutIcon: () => import('./HangoutIcon.js'),
  Happy01Icon: () => import('./Happy01Icon.js'),
  HappyIcon: () => import('./HappyIcon.js'),
  HardDriveDownloadIcon: () => import('./HardDriveDownloadIcon.js'),
  HardDriveIcon: () => import('./HardDriveIcon.js'),
  HardDriveUploadIcon: () => import('./HardDriveUploadIcon.js'),
  HardHatIcon: () => import('./HardHatIcon.js'),
  HashIcon: () => import('./HashIcon.js'),
  HashtagIcon: () => import('./HashtagIcon.js'),
  HatGlassesIcon: () => import('./HatGlassesIcon.js'),
  HatIcon: () => import('./HatIcon.js'),
  HazeIcon: () => import('./HazeIcon.js'),
  HdIcon: () => import('./HdIcon.js'),
  HddIcon: () => import('./HddIcon.js'),
  HdmiPortIcon: () => import('./HdmiPortIcon.js'),
  Hdr01Icon: () => import('./Hdr01Icon.js'),
  Hdr02Icon: () => import('./Hdr02Icon.js'),
  Heading01Icon: () => import('./Heading01Icon.js'),
  Heading02Icon: () => import('./Heading02Icon.js'),
  Heading03Icon: () => import('./Heading03Icon.js'),
  Heading04Icon: () => import('./Heading04Icon.js'),
  Heading05Icon: () => import('./Heading05Icon.js'),
  Heading06Icon: () => import('./Heading06Icon.js'),
  HeadingIcon: () => import('./HeadingIcon.js'),
  HeadphoneMuteIcon: () => import('./HeadphoneMuteIcon.js'),
  HeadphoneOffIcon: () => import('./HeadphoneOffIcon.js'),
  HeadphonesIcon: () => import('./HeadphonesIcon.js'),
  HeadsetConnectedIcon: () => import('./HeadsetConnectedIcon.js'),
  HeadsetOffIcon: () => import('./HeadsetOffIcon.js'),
  HeadsetIcon: () => import('./HeadsetIcon.js'),
  HealtcareIcon: () => import('./HealtcareIcon.js'),
  HealthIcon: () => import('./HealthIcon.js'),
  HeartAddIcon: () => import('./HeartAddIcon.js'),
  HeartCheckIcon: () => import('./HeartCheckIcon.js'),
  HeartCrackIcon: () => import('./HeartCrackIcon.js'),
  HeartHandshakeIcon: () => import('./HeartHandshakeIcon.js'),
  HeartMinusIcon: () => import('./HeartMinusIcon.js'),
  HeartOffIcon: () => import('./HeartOffIcon.js'),
  HeartPlusIcon: () => import('./HeartPlusIcon.js'),
  HeartPulseIcon: () => import('./HeartPulseIcon.js'),
  HeartRemoveIcon: () => import('./HeartRemoveIcon.js'),
  HeartIcon: () => import('./HeartIcon.js'),
  HeartbreakIcon: () => import('./HeartbreakIcon.js'),
  HeaterIcon: () => import('./HeaterIcon.js'),
  HelicopterIcon: () => import('./HelicopterIcon.js'),
  HelpCircleIcon: () => import('./HelpCircleIcon.js'),
  HelpSquareIcon: () => import('./HelpSquareIcon.js'),
  Hexagon01Icon: () => import('./Hexagon01Icon.js'),
  HexagonIcon: () => import('./HexagonIcon.js'),
  HierarchyCircle01Icon: () => import('./HierarchyCircle01Icon.js'),
  HierarchyCircle02Icon: () => import('./HierarchyCircle02Icon.js'),
  HierarchyCircle03Icon: () => import('./HierarchyCircle03Icon.js'),
  HierarchyFilesIcon: () => import('./HierarchyFilesIcon.js'),
  HierarchySquare01Icon: () => import('./HierarchySquare01Icon.js'),
  HierarchySquare02Icon: () => import('./HierarchySquare02Icon.js'),
  HierarchySquare03Icon: () => import('./HierarchySquare03Icon.js'),
  HierarchySquare04Icon: () => import('./HierarchySquare04Icon.js'),
  HierarchySquare05Icon: () => import('./HierarchySquare05Icon.js'),
  HierarchySquare06Icon: () => import('./HierarchySquare06Icon.js'),
  HierarchySquare07Icon: () => import('./HierarchySquare07Icon.js'),
  HierarchySquare08Icon: () => import('./HierarchySquare08Icon.js'),
  HierarchySquare10Icon: () => import('./HierarchySquare10Icon.js'),
  HierarchyIcon: () => import('./HierarchyIcon.js'),
  HighHeels01Icon: () => import('./HighHeels01Icon.js'),
  HighHeels02Icon: () => import('./HighHeels02Icon.js'),
  HighlighterIcon: () => import('./HighlighterIcon.js'),
  HijabIcon: () => import('./HijabIcon.js'),
  HistoryIcon: () => import('./HistoryIcon.js'),
  HockeyIcon: () => import('./HockeyIcon.js'),
  Hold01Icon: () => import('./Hold01Icon.js'),
  Hold02Icon: () => import('./Hold02Icon.js'),
  Hold03Icon: () => import('./Hold03Icon.js'),
  Hold04Icon: () => import('./Hold04Icon.js'),
  Hold05Icon: () => import('./Hold05Icon.js'),
  HoldLocked01Icon: () => import('./HoldLocked01Icon.js'),
  HoldLocked02Icon: () => import('./HoldLocked02Icon.js'),
  HoldPhoneIcon: () => import('./HoldPhoneIcon.js'),
  HologramIcon: () => import('./HologramIcon.js'),
  Home01Icon: () => import('./Home01Icon.js'),
  Home02Icon: () => import('./Home02Icon.js'),
  Home03Icon: () => import('./Home03Icon.js'),
  Home04Icon: () => import('./Home04Icon.js'),
  Home05Icon: () => import('./Home05Icon.js'),
  Home06Icon: () => import('./Home06Icon.js'),
  Home07Icon: () => import('./Home07Icon.js'),
  Home08Icon: () => import('./Home08Icon.js'),
  Home09Icon: () => import('./Home09Icon.js'),
  Home10Icon: () => import('./Home10Icon.js'),
  Home11Icon: () => import('./Home11Icon.js'),
  Home12Icon: () => import('./Home12Icon.js'),
  Home13Icon: () => import('./Home13Icon.js'),
  HomeWifiIcon: () => import('./HomeWifiIcon.js'),
  Honey01Icon: () => import('./Honey01Icon.js'),
  Honey02Icon: () => import('./Honey02Icon.js'),
  HonorIcon: () => import('./HonorIcon.js'),
  HonourStarIcon: () => import('./HonourStarIcon.js'),
  HoodieIcon: () => import('./HoodieIcon.js'),
  HopOffIcon: () => import('./HopOffIcon.js'),
  HopIcon: () => import('./HopIcon.js'),
  HorizonalScrollPointIcon: () => import('./HorizonalScrollPointIcon.js'),
  HorizontalResizeIcon: () => import('./HorizontalResizeIcon.js'),
  HorseHeadIcon: () => import('./HorseHeadIcon.js'),
  HorseSaddleIcon: () => import('./HorseSaddleIcon.js'),
  HorseIcon: () => import('./HorseIcon.js'),
  Hospital01Icon: () => import('./Hospital01Icon.js'),
  Hospital02Icon: () => import('./Hospital02Icon.js'),
  HospitalBed01Icon: () => import('./HospitalBed01Icon.js'),
  HospitalBed02Icon: () => import('./HospitalBed02Icon.js'),
  HospitalLocationIcon: () => import('./HospitalLocationIcon.js'),
  HotAirBalloonIcon: () => import('./HotAirBalloonIcon.js'),
  HotPriceIcon: () => import('./HotPriceIcon.js'),
  HotTubeIcon: () => import('./HotTubeIcon.js'),
  HotdogIcon: () => import('./HotdogIcon.js'),
  Hotel01Icon: () => import('./Hotel01Icon.js'),
  Hotel02Icon: () => import('./Hotel02Icon.js'),
  HotelBellIcon: () => import('./HotelBellIcon.js'),
  HotspotOfflineIcon: () => import('./HotspotOfflineIcon.js'),
  HotspotIcon: () => import('./HotspotIcon.js'),
  HourglassOffIcon: () => import('./HourglassOffIcon.js'),
  HourglassIcon: () => import('./HourglassIcon.js'),
  House01Icon: () => import('./House01Icon.js'),
  House02Icon: () => import('./House02Icon.js'),
  House03Icon: () => import('./House03Icon.js'),
  House04Icon: () => import('./House04Icon.js'),
  House05Icon: () => import('./House05Icon.js'),
  HouseHeartIcon: () => import('./HouseHeartIcon.js'),
  HousePlugIcon: () => import('./HousePlugIcon.js'),
  HousePlusIcon: () => import('./HousePlusIcon.js'),
  HouseSolarPanelIcon: () => import('./HouseSolarPanelIcon.js'),
  HouseWifiIcon: () => import('./HouseWifiIcon.js'),
  HtmlFiveIcon: () => import('./HtmlFiveIcon.js'),
  HtmlFile01Icon: () => import('./HtmlFile01Icon.js'),
  HtmlFile02Icon: () => import('./HtmlFile02Icon.js'),
  HugeiconsIcon: () => import('./HugeiconsIcon.js'),
  HumidityIcon: () => import('./HumidityIcon.js'),
  HutIcon: () => import('./HutIcon.js'),
  HydroPowerIcon: () => import('./HydroPowerIcon.js'),
  HyperboleIcon: () => import('./HyperboleIcon.js'),
  IceCream01Icon: () => import('./IceCream01Icon.js'),
  IceCream02Icon: () => import('./IceCream02Icon.js'),
  IceCream03Icon: () => import('./IceCream03Icon.js'),
  IceCream04Icon: () => import('./IceCream04Icon.js'),
  IceCubesIcon: () => import('./IceCubesIcon.js'),
  IceHockeyIcon: () => import('./IceHockeyIcon.js'),
  IceSkatingIcon: () => import('./IceSkatingIcon.js'),
  IcoIcon: () => import('./IcoIcon.js'),
  IconjarIcon: () => import('./IconjarIcon.js'),
  IdCardLanyardIcon: () => import('./IdCardLanyardIcon.js'),
  IdNotVerifiedIcon: () => import('./IdNotVerifiedIcon.js'),
  IdIcon: () => import('./IdIcon.js'),
  IdVerifiedIcon: () => import('./IdVerifiedIcon.js'),
  Idea01Icon: () => import('./Idea01Icon.js'),
  IdeaIcon: () => import('./IdeaIcon.js'),
  IdentificationIcon: () => import('./IdentificationIcon.js'),
  IdentityCardIcon: () => import('./IdentityCardIcon.js'),
  Image01Icon: () => import('./Image01Icon.js'),
  Image02Icon: () => import('./Image02Icon.js'),
  Image03Icon: () => import('./Image03Icon.js'),
  ImageActualSizeIcon: () => import('./ImageActualSizeIcon.js'),
  ImageAdd01Icon: () => import('./ImageAdd01Icon.js'),
  ImageAdd02Icon: () => import('./ImageAdd02Icon.js'),
  ImageCompositionOvalIcon: () => import('./ImageCompositionOvalIcon.js'),
  ImageCompositionIcon: () => import('./ImageCompositionIcon.js'),
  ImageCounterClockwiseIcon: () => import('./ImageCounterClockwiseIcon.js'),
  ImageCropIcon: () => import('./ImageCropIcon.js'),
  ImageDelete01Icon: () => import('./ImageDelete01Icon.js'),
  ImageDelete02Icon: () => import('./ImageDelete02Icon.js'),
  ImageDone01Icon: () => import('./ImageDone01Icon.js'),
  ImageDone02Icon: () => import('./ImageDone02Icon.js'),
  ImageDownload02Icon: () => import('./ImageDownload02Icon.js'),
  ImageDownloadIcon: () => import('./ImageDownloadIcon.js'),
  ImageFlipHorizontalIcon: () => import('./ImageFlipHorizontalIcon.js'),
  ImageFlipVerticalIcon: () => import('./ImageFlipVerticalIcon.js'),
  ImageNotFound01Icon: () => import('./ImageNotFound01Icon.js'),
  ImageNotFound02Icon: () => import('./ImageNotFound02Icon.js'),
  ImagePlayIcon: () => import('./ImagePlayIcon.js'),
  ImageRemove01Icon: () => import('./ImageRemove01Icon.js'),
  ImageRemove02Icon: () => import('./ImageRemove02Icon.js'),
  ImageRotationClockwiseIcon: () => import('./ImageRotationClockwiseIcon.js'),
  ImageUpload01Icon: () => import('./ImageUpload01Icon.js'),
  ImageUploadIcon: () => import('./ImageUploadIcon.js'),
  ImoIcon: () => import('./ImoIcon.js'),
  ImportantBookIcon: () => import('./ImportantBookIcon.js'),
  InLoveIcon: () => import('./InLoveIcon.js'),
  InboxCheckIcon: () => import('./InboxCheckIcon.js'),
  InboxDownloadIcon: () => import('./InboxDownloadIcon.js'),
  InboxIcon: () => import('./InboxIcon.js'),
  InboxUnreadIcon: () => import('./InboxUnreadIcon.js'),
  InboxUploadIcon: () => import('./InboxUploadIcon.js'),
  IncognitoIcon: () => import('./IncognitoIcon.js'),
  IndiaGateIcon: () => import('./IndiaGateIcon.js'),
  Inequality01Icon: () => import('./Inequality01Icon.js'),
  Inequality02Icon: () => import('./Inequality02Icon.js'),
  InequalityCircle01Icon: () => import('./InequalityCircle01Icon.js'),
  InequalityCircle02Icon: () => import('./InequalityCircle02Icon.js'),
  InequalitySquare01Icon: () => import('./InequalitySquare01Icon.js'),
  InequalitySquare02Icon: () => import('./InequalitySquare02Icon.js'),
  InfantIcon: () => import('./InfantIcon.js'),
  Infinity01Icon: () => import('./Infinity01Icon.js'),
  Infinity02Icon: () => import('./Infinity02Icon.js'),
  InfinityCircleIcon: () => import('./InfinityCircleIcon.js'),
  InfinitySquareIcon: () => import('./InfinitySquareIcon.js'),
  InformationCircleIcon: () => import('./InformationCircleIcon.js'),
  InformationDiamondIcon: () => import('./InformationDiamondIcon.js'),
  InformationSquareIcon: () => import('./InformationSquareIcon.js'),
  InjectionIcon: () => import('./InjectionIcon.js'),
  InputCursorTextIcon: () => import('./InputCursorTextIcon.js'),
  InputLongTextIcon: () => import('./InputLongTextIcon.js'),
  InputNumericIcon: () => import('./InputNumericIcon.js'),
  InputShortTextIcon: () => import('./InputShortTextIcon.js'),
  InputTextIcon: () => import('./InputTextIcon.js'),
  InsertBottomImageIcon: () => import('./InsertBottomImageIcon.js'),
  InsertCenterImageIcon: () => import('./InsertCenterImageIcon.js'),
  InsertColumnLeftIcon: () => import('./InsertColumnLeftIcon.js'),
  InsertColumnRightIcon: () => import('./InsertColumnRightIcon.js'),
  InsertColumnIcon: () => import('./InsertColumnIcon.js'),
  InsertPiIcon: () => import('./InsertPiIcon.js'),
  InsertRowDownIcon: () => import('./InsertRowDownIcon.js'),
  InsertRowIcon: () => import('./InsertRowIcon.js'),
  InsertRowUpIcon: () => import('./InsertRowUpIcon.js'),
  InsertTopImageIcon: () => import('./InsertTopImageIcon.js'),
  InspectCodeIcon: () => import('./InspectCodeIcon.js'),
  InstagramIcon: () => import('./InstagramIcon.js'),
  InstallingUpdates01Icon: () => import('./InstallingUpdates01Icon.js'),
  InstallingUpdates02Icon: () => import('./InstallingUpdates02Icon.js'),
  InternetAntenna01Icon: () => import('./InternetAntenna01Icon.js'),
  InternetAntenna02Icon: () => import('./InternetAntenna02Icon.js'),
  InternetAntenna03Icon: () => import('./InternetAntenna03Icon.js'),
  InternetAntenna04Icon: () => import('./InternetAntenna04Icon.js'),
  InternetIcon: () => import('./InternetIcon.js'),
  InvestigationIcon: () => import('./InvestigationIcon.js'),
  Invoice01Icon: () => import('./Invoice01Icon.js'),
  Invoice02Icon: () => import('./Invoice02Icon.js'),
  Invoice03Icon: () => import('./Invoice03Icon.js'),
  Invoice04Icon: () => import('./Invoice04Icon.js'),
  InvoiceIcon: () => import('./InvoiceIcon.js'),
  IpodIcon: () => import('./IpodIcon.js'),
  IrisScanIcon: () => import('./IrisScanIcon.js'),
  IslandIcon: () => import('./IslandIcon.js'),
  IterationCcwIcon: () => import('./IterationCcwIcon.js'),
  IterationCwIcon: () => import('./IterationCwIcon.js'),
  JarIcon: () => import('./JarIcon.js'),
  JavaScriptIcon: () => import('./JavaScriptIcon.js'),
  JavaIcon: () => import('./JavaIcon.js'),
  JobLinkIcon: () => import('./JobLinkIcon.js'),
  JobSearchIcon: () => import('./JobSearchIcon.js'),
  JobShareIcon: () => import('./JobShareIcon.js'),
  JoggerPantsIcon: () => import('./JoggerPantsIcon.js'),
  JoinBevelIcon: () => import('./JoinBevelIcon.js'),
  JoinRoundIcon: () => import('./JoinRoundIcon.js'),
  JoinStraightIcon: () => import('./JoinStraightIcon.js'),
  JokerIcon: () => import('./JokerIcon.js'),
  Joystick01Icon: () => import('./Joystick01Icon.js'),
  Joystick02Icon: () => import('./Joystick02Icon.js'),
  Joystick03Icon: () => import('./Joystick03Icon.js'),
  Joystick04Icon: () => import('./Joystick04Icon.js'),
  Joystick05Icon: () => import('./Joystick05Icon.js'),
  Jpg01Icon: () => import('./Jpg01Icon.js'),
  Jpg02Icon: () => import('./Jpg02Icon.js'),
  Jsx01Icon: () => import('./Jsx01Icon.js'),
  Jsx02Icon: () => import('./Jsx02Icon.js'),
  Jsx03Icon: () => import('./Jsx03Icon.js'),
  JudgeIcon: () => import('./JudgeIcon.js'),
  JumpersIcon: () => import('./JumpersIcon.js'),
  JupiterIcon: () => import('./JupiterIcon.js'),
  JusticeScale01Icon: () => import('./JusticeScale01Icon.js'),
  JusticeScale02Icon: () => import('./JusticeScale02Icon.js'),
  Kaaba01Icon: () => import('./Kaaba01Icon.js'),
  Kaaba02Icon: () => import('./Kaaba02Icon.js'),
  KanbanIcon: () => import('./KanbanIcon.js'),
  KayakIcon: () => import('./KayakIcon.js'),
  KeffiyehIcon: () => import('./KeffiyehIcon.js'),
  Kettle01Icon: () => import('./Kettle01Icon.js'),
  KettleIcon: () => import('./KettleIcon.js'),
  KettlebellIcon: () => import('./KettlebellIcon.js'),
  KetupatIcon: () => import('./KetupatIcon.js'),
  Key01Icon: () => import('./Key01Icon.js'),
  Key02Icon: () => import('./Key02Icon.js'),
  KeyGeneratorFobIcon: () => import('./KeyGeneratorFobIcon.js'),
  KeyboardIcon: () => import('./KeyboardIcon.js'),
  KeyframeAddIcon: () => import('./KeyframeAddIcon.js'),
  KeyframeAlignCenterIcon: () => import('./KeyframeAlignCenterIcon.js'),
  KeyframeAlignHorizontalIcon: () => import('./KeyframeAlignHorizontalIcon.js'),
  KeyframeAlignVerticalIcon: () => import('./KeyframeAlignVerticalIcon.js'),
  KeyframeBottomIcon: () => import('./KeyframeBottomIcon.js'),
  KeyframeLeftIcon: () => import('./KeyframeLeftIcon.js'),
  KeyframeRemoveIcon: () => import('./KeyframeRemoveIcon.js'),
  KeyframeRightIcon: () => import('./KeyframeRightIcon.js'),
  KeyframeIcon: () => import('./KeyframeIcon.js'),
  KeyframeTopIcon: () => import('./KeyframeTopIcon.js'),
  KeyframesDoubleAddIcon: () => import('./KeyframesDoubleAddIcon.js'),
  KeyframesDoubleRemoveIcon: () => import('./KeyframesDoubleRemoveIcon.js'),
  KeyframesDoubleIcon: () => import('./KeyframesDoubleIcon.js'),
  KeyframesMultipleAddIcon: () => import('./KeyframesMultipleAddIcon.js'),
  KeyframesMultipleRemoveIcon: () => import('./KeyframesMultipleRemoveIcon.js'),
  KeyframesMultipleIcon: () => import('./KeyframesMultipleIcon.js'),
  Kickstarter01Icon: () => import('./Kickstarter01Icon.js'),
  Kickstarter02Icon: () => import('./Kickstarter02Icon.js'),
  KidIcon: () => import('./KidIcon.js'),
  KidneysIcon: () => import('./KidneysIcon.js'),
  KimiAiIcon: () => import('./KimiAiIcon.js'),
  KissingIcon: () => import('./KissingIcon.js'),
  KitchenUtensilsIcon: () => import('./KitchenUtensilsIcon.js'),
  KiteIcon: () => import('./KiteIcon.js'),
  KlarnaIcon: () => import('./KlarnaIcon.js'),
  Knife01Icon: () => import('./Knife01Icon.js'),
  Knife02Icon: () => import('./Knife02Icon.js'),
  KnifeBreadIcon: () => import('./KnifeBreadIcon.js'),
  KnightShieldIcon: () => import('./KnightShieldIcon.js'),
  KnivesIcon: () => import('./KnivesIcon.js'),
  Knowledge01Icon: () => import('./Knowledge01Icon.js'),
  Knowledge02Icon: () => import('./Knowledge02Icon.js'),
  KoFiIcon: () => import('./KoFiIcon.js'),
  Kurta01Icon: () => import('./Kurta01Icon.js'),
  KurtaIcon: () => import('./KurtaIcon.js'),
  LabelImportantIcon: () => import('./LabelImportantIcon.js'),
  LabelIcon: () => import('./LabelIcon.js'),
  LaborIcon: () => import('./LaborIcon.js'),
  LabsIcon: () => import('./LabsIcon.js'),
  LakeIcon: () => import('./LakeIcon.js'),
  Lamp01Icon: () => import('./Lamp01Icon.js'),
  Lamp02Icon: () => import('./Lamp02Icon.js'),
  Lamp03Icon: () => import('./Lamp03Icon.js'),
  Lamp04Icon: () => import('./Lamp04Icon.js'),
  Lamp05Icon: () => import('./Lamp05Icon.js'),
  LampIcon: () => import('./LampIcon.js'),
  LampWallDownIcon: () => import('./LampWallDownIcon.js'),
  LampWallUpIcon: () => import('./LampWallUpIcon.js'),
  LandPlotIcon: () => import('./LandPlotIcon.js'),
  LandmarkIcon: () => import('./LandmarkIcon.js'),
  LanguageCircleIcon: () => import('./LanguageCircleIcon.js'),
  LanguageSkillIcon: () => import('./LanguageSkillIcon.js'),
  LanguageSquareIcon: () => import('./LanguageSquareIcon.js'),
  LanternIcon: () => import('./LanternIcon.js'),
  LaptopAddIcon: () => import('./LaptopAddIcon.js'),
  LaptopChargingIcon: () => import('./LaptopChargingIcon.js'),
  LaptopCheckIcon: () => import('./LaptopCheckIcon.js'),
  LaptopCloudIcon: () => import('./LaptopCloudIcon.js'),
  LaptopIssueIcon: () => import('./LaptopIssueIcon.js'),
  LaptopPerformanceIcon: () => import('./LaptopPerformanceIcon.js'),
  LaptopPhoneSyncIcon: () => import('./LaptopPhoneSyncIcon.js'),
  LaptopProgrammingIcon: () => import('./LaptopProgrammingIcon.js'),
  LaptopRemoveIcon: () => import('./LaptopRemoveIcon.js'),
  LaptopSettingsIcon: () => import('./LaptopSettingsIcon.js'),
  LaptopIcon: () => import('./LaptopIcon.js'),
  LaptopVideoIcon: () => import('./LaptopVideoIcon.js'),
  LassoSelectIcon: () => import('./LassoSelectIcon.js'),
  LassoTool01Icon: () => import('./LassoTool01Icon.js'),
  LassoTool02Icon: () => import('./LassoTool02Icon.js'),
  LastFmIcon: () => import('./LastFmIcon.js'),
  LatitudeIcon: () => import('./LatitudeIcon.js'),
  LaughingIcon: () => import('./LaughingIcon.js'),
  LaurelWreath01Icon: () => import('./LaurelWreath01Icon.js'),
  LaurelWreath02Icon: () => import('./LaurelWreath02Icon.js'),
  LaurelWreathFirst01Icon: () => import('./LaurelWreathFirst01Icon.js'),
  LaurelWreathFirst02Icon: () => import('./LaurelWreathFirst02Icon.js'),
  LaurelWreathLeft01Icon: () => import('./LaurelWreathLeft01Icon.js'),
  LaurelWreathLeft02Icon: () => import('./LaurelWreathLeft02Icon.js'),
  LaurelWreathLeft03Icon: () => import('./LaurelWreathLeft03Icon.js'),
  LaurelWreathRight01Icon: () => import('./LaurelWreathRight01Icon.js'),
  LaurelWreathRight02Icon: () => import('./LaurelWreathRight02Icon.js'),
  LaurelWreathRight03Icon: () => import('./LaurelWreathRight03Icon.js'),
  LayerAddIcon: () => import('./LayerAddIcon.js'),
  LayerBringForwardIcon: () => import('./LayerBringForwardIcon.js'),
  LayerBringToFrontIcon: () => import('./LayerBringToFrontIcon.js'),
  LayerMask01Icon: () => import('./LayerMask01Icon.js'),
  LayerMask02Icon: () => import('./LayerMask02Icon.js'),
  LayerSendBackwardIcon: () => import('./LayerSendBackwardIcon.js'),
  LayerSendToBackIcon: () => import('./LayerSendToBackIcon.js'),
  LayerIcon: () => import('./LayerIcon.js'),
  Layers01Icon: () => import('./Layers01Icon.js'),
  Layers02Icon: () => import('./Layers02Icon.js'),
  LayersLogoIcon: () => import('./LayersLogoIcon.js'),
  Layout01Icon: () => import('./Layout01Icon.js'),
  Layout02Icon: () => import('./Layout02Icon.js'),
  Layout03Icon: () => import('./Layout03Icon.js'),
  Layout04Icon: () => import('./Layout04Icon.js'),
  Layout05Icon: () => import('./Layout05Icon.js'),
  Layout06Icon: () => import('./Layout06Icon.js'),
  Layout07Icon: () => import('./Layout07Icon.js'),
  LayoutTwoColumnIcon: () => import('./LayoutTwoColumnIcon.js'),
  LayoutTwoRowIcon: () => import('./LayoutTwoRowIcon.js'),
  LayoutThreeColumnIcon: () => import('./LayoutThreeColumnIcon.js'),
  LayoutThreeRowIcon: () => import('./LayoutThreeRowIcon.js'),
  LayoutAlignBottomIcon: () => import('./LayoutAlignBottomIcon.js'),
  LayoutAlignLeftIcon: () => import('./LayoutAlignLeftIcon.js'),
  LayoutAlignRightIcon: () => import('./LayoutAlignRightIcon.js'),
  LayoutAlignTopIcon: () => import('./LayoutAlignTopIcon.js'),
  LayoutBottomIcon: () => import('./LayoutBottomIcon.js'),
  LayoutGridIcon: () => import('./LayoutGridIcon.js'),
  LayoutLeftIcon: () => import('./LayoutLeftIcon.js'),
  LayoutRightIcon: () => import('./LayoutRightIcon.js'),
  LayoutTable01Icon: () => import('./LayoutTable01Icon.js'),
  LayoutTable02Icon: () => import('./LayoutTable02Icon.js'),
  LayoutTopIcon: () => import('./LayoutTopIcon.js'),
  Leaf01Icon: () => import('./Leaf01Icon.js'),
  Leaf02Icon: () => import('./Leaf02Icon.js'),
  Leaf03Icon: () => import('./Leaf03Icon.js'),
  Leaf04Icon: () => import('./Leaf04Icon.js'),
  LeafyGreenIcon: () => import('./LeafyGreenIcon.js'),
  LeavingGeoFenceIcon: () => import('./LeavingGeoFenceIcon.js'),
  LeetcodeIcon: () => import('./LeetcodeIcon.js'),
  LeftAngleIcon: () => import('./LeftAngleIcon.js'),
  LeftToRightBlockQuoteIcon: () => import('./LeftToRightBlockQuoteIcon.js'),
  LeftToRightListBulletIcon: () => import('./LeftToRightListBulletIcon.js'),
  LeftToRightListDashIcon: () => import('./LeftToRightListDashIcon.js'),
  LeftToRightListNumberIcon: () => import('./LeftToRightListNumberIcon.js'),
  LeftToRightListStar01Icon: () => import('./LeftToRightListStar01Icon.js'),
  LeftToRightListStarIcon: () => import('./LeftToRightListStarIcon.js'),
  LeftToRightListTriangleIcon: () => import('./LeftToRightListTriangleIcon.js'),
  LeftTriangleIcon: () => import('./LeftTriangleIcon.js'),
  Legal01Icon: () => import('./Legal01Icon.js'),
  Legal02Icon: () => import('./Legal02Icon.js'),
  LegalDocument01Icon: () => import('./LegalDocument01Icon.js'),
  LegalDocument02Icon: () => import('./LegalDocument02Icon.js'),
  LegalHammerIcon: () => import('./LegalHammerIcon.js'),
  LessThanCircleIcon: () => import('./LessThanCircleIcon.js'),
  LessThanSquareIcon: () => import('./LessThanSquareIcon.js'),
  LessThanIcon: () => import('./LessThanIcon.js'),
  LetterSpacingIcon: () => import('./LetterSpacingIcon.js'),
  LibrariesIcon: () => import('./LibrariesIcon.js'),
  LibraryIcon: () => import('./LibraryIcon.js'),
  LicenseDraftIcon: () => import('./LicenseDraftIcon.js'),
  LicenseMaintenanceIcon: () => import('./LicenseMaintenanceIcon.js'),
  LicenseNoIcon: () => import('./LicenseNoIcon.js'),
  LicensePinIcon: () => import('./LicensePinIcon.js'),
  LicenseIcon: () => import('./LicenseIcon.js'),
  LicenseThirdPartyIcon: () => import('./LicenseThirdPartyIcon.js'),
  LifebuoyIcon: () => import('./LifebuoyIcon.js'),
  LiftTruckIcon: () => import('./LiftTruckIcon.js'),
  LigatureIcon: () => import('./LigatureIcon.js'),
  LightbulbOffIcon: () => import('./LightbulbOffIcon.js'),
  LighthouseIcon: () => import('./LighthouseIcon.js'),
  LimitOrderIcon: () => import('./LimitOrderIcon.js'),
  LimitationIcon: () => import('./LimitationIcon.js'),
  LineIcon: () => import('./LineIcon.js'),
  LinerIcon: () => import('./LinerIcon.js'),
  Link01Icon: () => import('./Link01Icon.js'),
  Link02Icon: () => import('./Link02Icon.js'),
  Link03Icon: () => import('./Link03Icon.js'),
  Link04Icon: () => import('./Link04Icon.js'),
  Link05Icon: () => import('./Link05Icon.js'),
  Link06Icon: () => import('./Link06Icon.js'),
  LinkBackwardIcon: () => import('./LinkBackwardIcon.js'),
  LinkCircle02Icon: () => import('./LinkCircle02Icon.js'),
  LinkCircleIcon: () => import('./LinkCircleIcon.js'),
  LinkForwardIcon: () => import('./LinkForwardIcon.js'),
  LinkSquare01Icon: () => import('./LinkSquare01Icon.js'),
  LinkSquare02Icon: () => import('./LinkSquare02Icon.js'),
  Linkedin01Icon: () => import('./Linkedin01Icon.js'),
  Linkedin02Icon: () => import('./Linkedin02Icon.js'),
  ListChevronsDownUpIcon: () => import('./ListChevronsDownUpIcon.js'),
  ListFilterPlusIcon: () => import('./ListFilterPlusIcon.js'),
  ListIndentDecreaseIcon: () => import('./ListIndentDecreaseIcon.js'),
  ListIndentIncreaseIcon: () => import('./ListIndentIncreaseIcon.js'),
  ListMusicIcon: () => import('./ListMusicIcon.js'),
  ListPlusIcon: () => import('./ListPlusIcon.js'),
  ListRestartIcon: () => import('./ListRestartIcon.js'),
  ListSettingIcon: () => import('./ListSettingIcon.js'),
  ListStartIcon: () => import('./ListStartIcon.js'),
  ListTreeIcon: () => import('./ListTreeIcon.js'),
  ListVideoIcon: () => import('./ListVideoIcon.js'),
  ListViewIcon: () => import('./ListViewIcon.js'),
  ListXIcon: () => import('./ListXIcon.js'),
  LitecoinIcon: () => import('./LitecoinIcon.js'),
  LiveStreaming01Icon: () => import('./LiveStreaming01Icon.js'),
  LiveStreaming02Icon: () => import('./LiveStreaming02Icon.js'),
  LiveStreaming03Icon: () => import('./LiveStreaming03Icon.js'),
  LiverIcon: () => import('./LiverIcon.js'),
  LoaderPinwheelIcon: () => import('./LoaderPinwheelIcon.js'),
  Loading01Icon: () => import('./Loading01Icon.js'),
  Loading02Icon: () => import('./Loading02Icon.js'),
  Loading03Icon: () => import('./Loading03Icon.js'),
  Loading04Icon: () => import('./Loading04Icon.js'),
  Location01Icon: () => import('./Location01Icon.js'),
  Location02Icon: () => import('./Location02Icon.js'),
  Location03Icon: () => import('./Location03Icon.js'),
  Location04Icon: () => import('./Location04Icon.js'),
  Location05Icon: () => import('./Location05Icon.js'),
  Location06Icon: () => import('./Location06Icon.js'),
  Location07Icon: () => import('./Location07Icon.js'),
  Location08Icon: () => import('./Location08Icon.js'),
  Location09Icon: () => import('./Location09Icon.js'),
  Location10Icon: () => import('./Location10Icon.js'),
  LocationAdd01Icon: () => import('./LocationAdd01Icon.js'),
  LocationAdd02Icon: () => import('./LocationAdd02Icon.js'),
  LocationCheck01Icon: () => import('./LocationCheck01Icon.js'),
  LocationCheck02Icon: () => import('./LocationCheck02Icon.js'),
  LocationFavourite01Icon: () => import('./LocationFavourite01Icon.js'),
  LocationFavourite02Icon: () => import('./LocationFavourite02Icon.js'),
  LocationOffline01Icon: () => import('./LocationOffline01Icon.js'),
  LocationOffline02Icon: () => import('./LocationOffline02Icon.js'),
  LocationOffline03Icon: () => import('./LocationOffline03Icon.js'),
  LocationOffline04Icon: () => import('./LocationOffline04Icon.js'),
  LocationRemove01Icon: () => import('./LocationRemove01Icon.js'),
  LocationRemove02Icon: () => import('./LocationRemove02Icon.js'),
  LocationShare01Icon: () => import('./LocationShare01Icon.js'),
  LocationShare02Icon: () => import('./LocationShare02Icon.js'),
  LocationStar01Icon: () => import('./LocationStar01Icon.js'),
  LocationStar02Icon: () => import('./LocationStar02Icon.js'),
  LocationUpdate01Icon: () => import('./LocationUpdate01Icon.js'),
  LocationUpdate02Icon: () => import('./LocationUpdate02Icon.js'),
  LocationUser01Icon: () => import('./LocationUser01Icon.js'),
  LocationUser02Icon: () => import('./LocationUser02Icon.js'),
  LocationUser03Icon: () => import('./LocationUser03Icon.js'),
  LocationUser04Icon: () => import('./LocationUser04Icon.js'),
  LockComputerIcon: () => import('./LockComputerIcon.js'),
  LockKeyIcon: () => import('./LockKeyIcon.js'),
  LockPasswordIcon: () => import('./LockPasswordIcon.js'),
  LockIcon: () => import('./LockIcon.js'),
  LockSync01Icon: () => import('./LockSync01Icon.js'),
  LockSync02Icon: () => import('./LockSync02Icon.js'),
  LockedIcon: () => import('./LockedIcon.js'),
  Locker01Icon: () => import('./Locker01Icon.js'),
  LockerIcon: () => import('./LockerIcon.js'),
  Login01Icon: () => import('./Login01Icon.js'),
  Login02Icon: () => import('./Login02Icon.js'),
  Login03Icon: () => import('./Login03Icon.js'),
  LoginCircle01Icon: () => import('./LoginCircle01Icon.js'),
  LoginCircle02Icon: () => import('./LoginCircle02Icon.js'),
  LoginMethodIcon: () => import('./LoginMethodIcon.js'),
  LoginSquare01Icon: () => import('./LoginSquare01Icon.js'),
  LoginSquare02Icon: () => import('./LoginSquare02Icon.js'),
  Logout01Icon: () => import('./Logout01Icon.js'),
  Logout02Icon: () => import('./Logout02Icon.js'),
  Logout03Icon: () => import('./Logout03Icon.js'),
  Logout04Icon: () => import('./Logout04Icon.js'),
  Logout05Icon: () => import('./Logout05Icon.js'),
  LogoutCircle01Icon: () => import('./LogoutCircle01Icon.js'),
  LogoutCircle02Icon: () => import('./LogoutCircle02Icon.js'),
  LogoutSquare01Icon: () => import('./LogoutSquare01Icon.js'),
  LogoutSquare02Icon: () => import('./LogoutSquare02Icon.js'),
  LollipopIcon: () => import('./LollipopIcon.js'),
  LongSleeveShirtIcon: () => import('./LongSleeveShirtIcon.js'),
  LongitudeIcon: () => import('./LongitudeIcon.js'),
  LookBottomIcon: () => import('./LookBottomIcon.js'),
  LookLeftIcon: () => import('./LookLeftIcon.js'),
  LookRightIcon: () => import('./LookRightIcon.js'),
  LookTopIcon: () => import('./LookTopIcon.js'),
  LoomIcon: () => import('./LoomIcon.js'),
  LottiefilesIcon: () => import('./LottiefilesIcon.js'),
  LoveKoreanFingerIcon: () => import('./LoveKoreanFingerIcon.js'),
  LowSignalIcon: () => import('./LowSignalIcon.js'),
  LoyaltyCardIcon: () => import('./LoyaltyCardIcon.js'),
  Luggage01Icon: () => import('./Luggage01Icon.js'),
  Luggage02Icon: () => import('./Luggage02Icon.js'),
  LungsIcon: () => import('./LungsIcon.js'),
  MachineRobotIcon: () => import('./MachineRobotIcon.js'),
  MagicWand01Icon: () => import('./MagicWand01Icon.js'),
  MagicWand02Icon: () => import('./MagicWand02Icon.js'),
  MagicWand03Icon: () => import('./MagicWand03Icon.js'),
  MagicWand04Icon: () => import('./MagicWand04Icon.js'),
  MagicWand05Icon: () => import('./MagicWand05Icon.js'),
  Magnet01Icon: () => import('./Magnet01Icon.js'),
  Magnet02Icon: () => import('./Magnet02Icon.js'),
  MagnetIcon: () => import('./MagnetIcon.js'),
  Mail01Icon: () => import('./Mail01Icon.js'),
  Mail02Icon: () => import('./Mail02Icon.js'),
  MailAccount01Icon: () => import('./MailAccount01Icon.js'),
  MailAccount02Icon: () => import('./MailAccount02Icon.js'),
  MailAdd01Icon: () => import('./MailAdd01Icon.js'),
  MailAdd02Icon: () => import('./MailAdd02Icon.js'),
  MailAtSign01Icon: () => import('./MailAtSign01Icon.js'),
  MailAtSign02Icon: () => import('./MailAtSign02Icon.js'),
  MailBlock01Icon: () => import('./MailBlock01Icon.js'),
  MailBlock02Icon: () => import('./MailBlock02Icon.js'),
  MailDownload01Icon: () => import('./MailDownload01Icon.js'),
  MailDownload02Icon: () => import('./MailDownload02Icon.js'),
  MailEdit01Icon: () => import('./MailEdit01Icon.js'),
  MailEdit02Icon: () => import('./MailEdit02Icon.js'),
  MailLock01Icon: () => import('./MailLock01Icon.js'),
  MailLock02Icon: () => import('./MailLock02Icon.js'),
  MailLove01Icon: () => import('./MailLove01Icon.js'),
  MailLove02Icon: () => import('./MailLove02Icon.js'),
  MailMinus01Icon: () => import('./MailMinus01Icon.js'),
  MailMinus02Icon: () => import('./MailMinus02Icon.js'),
  MailOpen01Icon: () => import('./MailOpen01Icon.js'),
  MailOpen02Icon: () => import('./MailOpen02Icon.js'),
  MailOpenLoveIcon: () => import('./MailOpenLoveIcon.js'),
  MailOpenIcon: () => import('./MailOpenIcon.js'),
  MailQuestionMarkIcon: () => import('./MailQuestionMarkIcon.js'),
  MailReceive01Icon: () => import('./MailReceive01Icon.js'),
  MailReceive02Icon: () => import('./MailReceive02Icon.js'),
  MailRemove01Icon: () => import('./MailRemove01Icon.js'),
  MailRemove02Icon: () => import('./MailRemove02Icon.js'),
  MailReply01Icon: () => import('./MailReply01Icon.js'),
  MailReply02Icon: () => import('./MailReply02Icon.js'),
  MailReplyAll01Icon: () => import('./MailReplyAll01Icon.js'),
  MailReplyAll02Icon: () => import('./MailReplyAll02Icon.js'),
  MailSearch01Icon: () => import('./MailSearch01Icon.js'),
  MailSearch02Icon: () => import('./MailSearch02Icon.js'),
  MailSecure01Icon: () => import('./MailSecure01Icon.js'),
  MailSecure02Icon: () => import('./MailSecure02Icon.js'),
  MailSend01Icon: () => import('./MailSend01Icon.js'),
  MailSend02Icon: () => import('./MailSend02Icon.js'),
  MailSetting01Icon: () => import('./MailSetting01Icon.js'),
  MailSetting02Icon: () => import('./MailSetting02Icon.js'),
  MailUnlock01Icon: () => import('./MailUnlock01Icon.js'),
  MailUnlock02Icon: () => import('./MailUnlock02Icon.js'),
  MailUpload01Icon: () => import('./MailUpload01Icon.js'),
  MailUpload02Icon: () => import('./MailUpload02Icon.js'),
  MailValidation01Icon: () => import('./MailValidation01Icon.js'),
  MailValidation02Icon: () => import('./MailValidation02Icon.js'),
  MailVoice01Icon: () => import('./MailVoice01Icon.js'),
  MailVoice02Icon: () => import('./MailVoice02Icon.js'),
  MailWarningIcon: () => import('./MailWarningIcon.js'),
  Mailbox01Icon: () => import('./Mailbox01Icon.js'),
  MailboxIcon: () => import('./MailboxIcon.js'),
  MailsIcon: () => import('./MailsIcon.js'),
  Male02Icon: () => import('./Male02Icon.js'),
  MaleSymbolIcon: () => import('./MaleSymbolIcon.js'),
  ManIcon: () => import('./ManIcon.js'),
  ManWomanIcon: () => import('./ManWomanIcon.js'),
  ManagerIcon: () => import('./ManagerIcon.js'),
  MapPinCheckIcon: () => import('./MapPinCheckIcon.js'),
  MapPinHouseIcon: () => import('./MapPinHouseIcon.js'),
  MapPinMinusIcon: () => import('./MapPinMinusIcon.js'),
  MapPinPenIcon: () => import('./MapPinPenIcon.js'),
  MapPinPlusIcon: () => import('./MapPinPlusIcon.js'),
  MapPinIcon: () => import('./MapPinIcon.js'),
  MapPinXInsideIcon: () => import('./MapPinXInsideIcon.js'),
  MapPinXIcon: () => import('./MapPinXIcon.js'),
  MapPinpoint01Icon: () => import('./MapPinpoint01Icon.js'),
  MapPinpoint02Icon: () => import('./MapPinpoint02Icon.js'),
  MapingIcon: () => import('./MapingIcon.js'),
  MapsCircle01Icon: () => import('./MapsCircle01Icon.js'),
  MapsCircle02Icon: () => import('./MapsCircle02Icon.js'),
  MapsEditingIcon: () => import('./MapsEditingIcon.js'),
  MapsGlobal01Icon: () => import('./MapsGlobal01Icon.js'),
  MapsGlobal02Icon: () => import('./MapsGlobal02Icon.js'),
  MapsLocation01Icon: () => import('./MapsLocation01Icon.js'),
  MapsLocation02Icon: () => import('./MapsLocation02Icon.js'),
  MapsOffIcon: () => import('./MapsOffIcon.js'),
  MapsRefreshIcon: () => import('./MapsRefreshIcon.js'),
  MapsSearchIcon: () => import('./MapsSearchIcon.js'),
  MapsSquare01Icon: () => import('./MapsSquare01Icon.js'),
  MapsSquare02Icon: () => import('./MapsSquare02Icon.js'),
  MapsIcon: () => import('./MapsIcon.js'),
  MarketAnalysisIcon: () => import('./MarketAnalysisIcon.js'),
  MarketOrderIcon: () => import('./MarketOrderIcon.js'),
  MarketingIcon: () => import('./MarketingIcon.js'),
  MaskLoveIcon: () => import('./MaskLoveIcon.js'),
  MaskIcon: () => import('./MaskIcon.js'),
  MaskTheater01Icon: () => import('./MaskTheater01Icon.js'),
  MaskTheater02Icon: () => import('./MaskTheater02Icon.js'),
  MasterCardIcon: () => import('./MasterCardIcon.js'),
  MastodonIcon: () => import('./MastodonIcon.js'),
  MatchesIcon: () => import('./MatchesIcon.js'),
  MaterialAndTextureIcon: () => import('./MaterialAndTextureIcon.js'),
  MathIcon: () => import('./MathIcon.js'),
  MatrixIcon: () => import('./MatrixIcon.js'),
  Maximize01Icon: () => import('./Maximize01Icon.js'),
  Maximize02Icon: () => import('./Maximize02Icon.js'),
  Maximize03Icon: () => import('./Maximize03Icon.js'),
  Maximize04Icon: () => import('./Maximize04Icon.js'),
  MaximizeScreenIcon: () => import('./MaximizeScreenIcon.js'),
  MayanPyramidIcon: () => import('./MayanPyramidIcon.js'),
  MazeIcon: () => import('./MazeIcon.js'),
  McpServerIcon: () => import('./McpServerIcon.js'),
  MealScanIcon: () => import('./MealScanIcon.js'),
  Medal01Icon: () => import('./Medal01Icon.js'),
  Medal02Icon: () => import('./Medal02Icon.js'),
  Medal03Icon: () => import('./Medal03Icon.js'),
  Medal04Icon: () => import('./Medal04Icon.js'),
  Medal05Icon: () => import('./Medal05Icon.js'),
  Medal06Icon: () => import('./Medal06Icon.js'),
  Medal07Icon: () => import('./Medal07Icon.js'),
  MedalFirstPlaceIcon: () => import('./MedalFirstPlaceIcon.js'),
  MedalSecondPlaceIcon: () => import('./MedalSecondPlaceIcon.js'),
  MedalThirdPlaceIcon: () => import('./MedalThirdPlaceIcon.js'),
  MedicalFileIcon: () => import('./MedicalFileIcon.js'),
  MedicalMaskIcon: () => import('./MedicalMaskIcon.js'),
  Medicine01Icon: () => import('./Medicine01Icon.js'),
  Medicine02Icon: () => import('./Medicine02Icon.js'),
  MedicineBottle01Icon: () => import('./MedicineBottle01Icon.js'),
  MedicineBottle02Icon: () => import('./MedicineBottle02Icon.js'),
  MedicineSyrupIcon: () => import('./MedicineSyrupIcon.js'),
  MediumSignalIcon: () => import('./MediumSignalIcon.js'),
  MediumSquareIcon: () => import('./MediumSquareIcon.js'),
  MediumIcon: () => import('./MediumIcon.js'),
  MeetingRoomIcon: () => import('./MeetingRoomIcon.js'),
  Megaphone01Icon: () => import('./Megaphone01Icon.js'),
  Megaphone02Icon: () => import('./Megaphone02Icon.js'),
  Megaphone03Icon: () => import('./Megaphone03Icon.js'),
  MehIcon: () => import('./MehIcon.js'),
  MemoryStickIcon: () => import('./MemoryStickIcon.js'),
  MentorIcon: () => import('./MentorIcon.js'),
  MentoringIcon: () => import('./MentoringIcon.js'),
  Menu01Icon: () => import('./Menu01Icon.js'),
  Menu02Icon: () => import('./Menu02Icon.js'),
  Menu03Icon: () => import('./Menu03Icon.js'),
  Menu04Icon: () => import('./Menu04Icon.js'),
  Menu05Icon: () => import('./Menu05Icon.js'),
  Menu06Icon: () => import('./Menu06Icon.js'),
  Menu07Icon: () => import('./Menu07Icon.js'),
  Menu08Icon: () => import('./Menu08Icon.js'),
  Menu09Icon: () => import('./Menu09Icon.js'),
  Menu10Icon: () => import('./Menu10Icon.js'),
  Menu11Icon: () => import('./Menu11Icon.js'),
  MenuCircleIcon: () => import('./MenuCircleIcon.js'),
  MenuCollapseIcon: () => import('./MenuCollapseIcon.js'),
  MenuRestaurantIcon: () => import('./MenuRestaurantIcon.js'),
  MenuSquareIcon: () => import('./MenuSquareIcon.js'),
  MenuTwoLineIcon: () => import('./MenuTwoLineIcon.js'),
  Message01Icon: () => import('./Message01Icon.js'),
  Message02Icon: () => import('./Message02Icon.js'),
  MessageAdd01Icon: () => import('./MessageAdd01Icon.js'),
  MessageAdd02Icon: () => import('./MessageAdd02Icon.js'),
  MessageBlockedIcon: () => import('./MessageBlockedIcon.js'),
  MessageCancel01Icon: () => import('./MessageCancel01Icon.js'),
  MessageCancel02Icon: () => import('./MessageCancel02Icon.js'),
  MessageCircleCodeIcon: () => import('./MessageCircleCodeIcon.js'),
  MessageCircleReplyIcon: () => import('./MessageCircleReplyIcon.js'),
  MessageDelay01Icon: () => import('./MessageDelay01Icon.js'),
  MessageDelay02Icon: () => import('./MessageDelay02Icon.js'),
  MessageDone01Icon: () => import('./MessageDone01Icon.js'),
  MessageDone02Icon: () => import('./MessageDone02Icon.js'),
  MessageDownload01Icon: () => import('./MessageDownload01Icon.js'),
  MessageDownload02Icon: () => import('./MessageDownload02Icon.js'),
  MessageEdit01Icon: () => import('./MessageEdit01Icon.js'),
  MessageEdit02Icon: () => import('./MessageEdit02Icon.js'),
  MessageFavourite01Icon: () => import('./MessageFavourite01Icon.js'),
  MessageFavourite02Icon: () => import('./MessageFavourite02Icon.js'),
  MessageIncoming01Icon: () => import('./MessageIncoming01Icon.js'),
  MessageIncoming02Icon: () => import('./MessageIncoming02Icon.js'),
  MessageLock01Icon: () => import('./MessageLock01Icon.js'),
  MessageLock02Icon: () => import('./MessageLock02Icon.js'),
  MessageMultiple01Icon: () => import('./MessageMultiple01Icon.js'),
  MessageMultiple02Icon: () => import('./MessageMultiple02Icon.js'),
  MessageNotification01Icon: () => import('./MessageNotification01Icon.js'),
  MessageNotification02Icon: () => import('./MessageNotification02Icon.js'),
  MessageOutgoing01Icon: () => import('./MessageOutgoing01Icon.js'),
  MessageOutgoing02Icon: () => import('./MessageOutgoing02Icon.js'),
  MessagePreview01Icon: () => import('./MessagePreview01Icon.js'),
  MessagePreview02Icon: () => import('./MessagePreview02Icon.js'),
  MessageProgrammingIcon: () => import('./MessageProgrammingIcon.js'),
  MessageQuestionIcon: () => import('./MessageQuestionIcon.js'),
  MessageSearch01Icon: () => import('./MessageSearch01Icon.js'),
  MessageSearch02Icon: () => import('./MessageSearch02Icon.js'),
  MessageSecure01Icon: () => import('./MessageSecure01Icon.js'),
  MessageSecure02Icon: () => import('./MessageSecure02Icon.js'),
  MessageSquareCodeIcon: () => import('./MessageSquareCodeIcon.js'),
  MessageSquareDashedIcon: () => import('./MessageSquareDashedIcon.js'),
  MessageSquareDiffIcon: () => import('./MessageSquareDiffIcon.js'),
  MessageSquareShareIcon: () => import('./MessageSquareShareIcon.js'),
  MessageTranslateIcon: () => import('./MessageTranslateIcon.js'),
  MessageUnlock01Icon: () => import('./MessageUnlock01Icon.js'),
  MessageUnlock02Icon: () => import('./MessageUnlock02Icon.js'),
  MessageUpload01Icon: () => import('./MessageUpload01Icon.js'),
  MessageUpload02Icon: () => import('./MessageUpload02Icon.js'),
  MessageUser01Icon: () => import('./MessageUser01Icon.js'),
  MessageUser02Icon: () => import('./MessageUser02Icon.js'),
  MessengerIcon: () => import('./MessengerIcon.js'),
  MetaIcon: () => import('./MetaIcon.js'),
  MetroIcon: () => import('./MetroIcon.js'),
  Mic01Icon: () => import('./Mic01Icon.js'),
  Mic02Icon: () => import('./Mic02Icon.js'),
  MicOff01Icon: () => import('./MicOff01Icon.js'),
  MicOff02Icon: () => import('./MicOff02Icon.js'),
  MicrochipIcon: () => import('./MicrochipIcon.js'),
  MicroscopeIcon: () => import('./MicroscopeIcon.js'),
  MicrosoftAdminIcon: () => import('./MicrosoftAdminIcon.js'),
  MicrosoftIcon: () => import('./MicrosoftIcon.js'),
  MicrowaveIcon: () => import('./MicrowaveIcon.js'),
  MilkBottleIcon: () => import('./MilkBottleIcon.js'),
  MilkCartonIcon: () => import('./MilkCartonIcon.js'),
  MilkCoconutIcon: () => import('./MilkCoconutIcon.js'),
  MilkOatIcon: () => import('./MilkOatIcon.js'),
  Minimize01Icon: () => import('./Minimize01Icon.js'),
  Minimize02Icon: () => import('./Minimize02Icon.js'),
  Minimize03Icon: () => import('./Minimize03Icon.js'),
  Minimize04Icon: () => import('./Minimize04Icon.js'),
  MinimizeScreenIcon: () => import('./MinimizeScreenIcon.js'),
  Mining01Icon: () => import('./Mining01Icon.js'),
  Mining02Icon: () => import('./Mining02Icon.js'),
  Mining03Icon: () => import('./Mining03Icon.js'),
  MinusPlus01Icon: () => import('./MinusPlus01Icon.js'),
  MinusPlus02Icon: () => import('./MinusPlus02Icon.js'),
  MinusPlusCircle01Icon: () => import('./MinusPlusCircle01Icon.js'),
  MinusPlusCircle02Icon: () => import('./MinusPlusCircle02Icon.js'),
  MinusPlusSquare01Icon: () => import('./MinusPlusSquare01Icon.js'),
  MinusPlusSquare02Icon: () => import('./MinusPlusSquare02Icon.js'),
  MinusSignCircleIcon: () => import('./MinusSignCircleIcon.js'),
  MinusSignSquareIcon: () => import('./MinusSignSquareIcon.js'),
  MinusSignIcon: () => import('./MinusSignIcon.js'),
  MirrorIcon: () => import('./MirrorIcon.js'),
  MirroringScreenIcon: () => import('./MirroringScreenIcon.js'),
  MistralIcon: () => import('./MistralIcon.js'),
  MixerIcon: () => import('./MixerIcon.js'),
  MobileNavigator01Icon: () => import('./MobileNavigator01Icon.js'),
  MobileNavigator02Icon: () => import('./MobileNavigator02Icon.js'),
  MobileProgramming01Icon: () => import('./MobileProgramming01Icon.js'),
  MobileProgramming02Icon: () => import('./MobileProgramming02Icon.js'),
  MobileProtectionIcon: () => import('./MobileProtectionIcon.js'),
  MobileSecurityIcon: () => import('./MobileSecurityIcon.js'),
  MochiIcon: () => import('./MochiIcon.js'),
  ModernTvFourKIcon: () => import('./ModernTvFourKIcon.js'),
  ModernTvIssueIcon: () => import('./ModernTvIssueIcon.js'),
  ModernTvIcon: () => import('./ModernTvIcon.js'),
  MoleculesIcon: () => import('./MoleculesIcon.js'),
  MollieIcon: () => import('./MollieIcon.js'),
  MonasIcon: () => import('./MonasIcon.js'),
  Money01Icon: () => import('./Money01Icon.js'),
  Money02Icon: () => import('./Money02Icon.js'),
  Money03Icon: () => import('./Money03Icon.js'),
  Money04Icon: () => import('./Money04Icon.js'),
  MoneyAdd01Icon: () => import('./MoneyAdd01Icon.js'),
  MoneyAdd02Icon: () => import('./MoneyAdd02Icon.js'),
  MoneyBag01Icon: () => import('./MoneyBag01Icon.js'),
  MoneyBag02Icon: () => import('./MoneyBag02Icon.js'),
  MoneyExchange01Icon: () => import('./MoneyExchange01Icon.js'),
  MoneyExchange02Icon: () => import('./MoneyExchange02Icon.js'),
  MoneyExchange03Icon: () => import('./MoneyExchange03Icon.js'),
  MoneyLockIcon: () => import('./MoneyLockIcon.js'),
  MoneyNotFound01Icon: () => import('./MoneyNotFound01Icon.js'),
  MoneyNotFound02Icon: () => import('./MoneyNotFound02Icon.js'),
  MoneyNotFound03Icon: () => import('./MoneyNotFound03Icon.js'),
  MoneyNotFound04Icon: () => import('./MoneyNotFound04Icon.js'),
  MoneyReceive01Icon: () => import('./MoneyReceive01Icon.js'),
  MoneyReceive02Icon: () => import('./MoneyReceive02Icon.js'),
  MoneyReceiveCircleIcon: () => import('./MoneyReceiveCircleIcon.js'),
  MoneyReceiveFlow01Icon: () => import('./MoneyReceiveFlow01Icon.js'),
  MoneyReceiveFlow02Icon: () => import('./MoneyReceiveFlow02Icon.js'),
  MoneyReceiveSquareIcon: () => import('./MoneyReceiveSquareIcon.js'),
  MoneyRemove01Icon: () => import('./MoneyRemove01Icon.js'),
  MoneyRemove02Icon: () => import('./MoneyRemove02Icon.js'),
  MoneySafeIcon: () => import('./MoneySafeIcon.js'),
  MoneySavingJarIcon: () => import('./MoneySavingJarIcon.js'),
  MoneySecurityIcon: () => import('./MoneySecurityIcon.js'),
  MoneySend01Icon: () => import('./MoneySend01Icon.js'),
  MoneySend02Icon: () => import('./MoneySend02Icon.js'),
  MoneySendCircleIcon: () => import('./MoneySendCircleIcon.js'),
  MoneySendFlow01Icon: () => import('./MoneySendFlow01Icon.js'),
  MoneySendFlow02Icon: () => import('./MoneySendFlow02Icon.js'),
  MoneySendSquareIcon: () => import('./MoneySendSquareIcon.js'),
  MonitorDotIcon: () => import('./MonitorDotIcon.js'),
  MonitorPauseIcon: () => import('./MonitorPauseIcon.js'),
  MonitorSpeakerIcon: () => import('./MonitorSpeakerIcon.js'),
  MonitorStopIcon: () => import('./MonitorStopIcon.js'),
  Monocle01Icon: () => import('./Monocle01Icon.js'),
  MonocleIcon: () => import('./MonocleIcon.js'),
  MonsterIcon: () => import('./MonsterIcon.js'),
  Moon01Icon: () => import('./Moon01Icon.js'),
  Moon02Icon: () => import('./Moon02Icon.js'),
  MoonAngledRainZapIcon: () => import('./MoonAngledRainZapIcon.js'),
  MoonCloudAngledRainIcon: () => import('./MoonCloudAngledRainIcon.js'),
  MoonCloudAngledZapIcon: () => import('./MoonCloudAngledZapIcon.js'),
  MoonCloudBigRainIcon: () => import('./MoonCloudBigRainIcon.js'),
  MoonCloudFastWindIcon: () => import('./MoonCloudFastWindIcon.js'),
  MoonCloudHailstoneIcon: () => import('./MoonCloudHailstoneIcon.js'),
  MoonCloudLittleRainIcon: () => import('./MoonCloudLittleRainIcon.js'),
  MoonCloudLittleSnowIcon: () => import('./MoonCloudLittleSnowIcon.js'),
  MoonCloudMidRainIcon: () => import('./MoonCloudMidRainIcon.js'),
  MoonCloudMidSnowIcon: () => import('./MoonCloudMidSnowIcon.js'),
  MoonCloudSlowWindIcon: () => import('./MoonCloudSlowWindIcon.js'),
  MoonCloudSnowIcon: () => import('./MoonCloudSnowIcon.js'),
  MoonCloudIcon: () => import('./MoonCloudIcon.js'),
  MoonEclipseIcon: () => import('./MoonEclipseIcon.js'),
  MoonFastWindIcon: () => import('./MoonFastWindIcon.js'),
  MoonLandingIcon: () => import('./MoonLandingIcon.js'),
  MoonSlowWindIcon: () => import('./MoonSlowWindIcon.js'),
  MoonIcon: () => import('./MoonIcon.js'),
  MoonsetIcon: () => import('./MoonsetIcon.js'),
  More01Icon: () => import('./More01Icon.js'),
  More02Icon: () => import('./More02Icon.js'),
  More03Icon: () => import('./More03Icon.js'),
  MoreHorizontalCircle01Icon: () => import('./MoreHorizontalCircle01Icon.js'),
  MoreHorizontalCircle02Icon: () => import('./MoreHorizontalCircle02Icon.js'),
  MoreHorizontalSquare01Icon: () => import('./MoreHorizontalSquare01Icon.js'),
  MoreHorizontalSquare02Icon: () => import('./MoreHorizontalSquare02Icon.js'),
  MoreHorizontalIcon: () => import('./MoreHorizontalIcon.js'),
  MoreOrLessCircleIcon: () => import('./MoreOrLessCircleIcon.js'),
  MoreOrLessSquareIcon: () => import('./MoreOrLessSquareIcon.js'),
  MoreOrLessIcon: () => import('./MoreOrLessIcon.js'),
  MoreIcon: () => import('./MoreIcon.js'),
  MoreVerticalCircle01Icon: () => import('./MoreVerticalCircle01Icon.js'),
  MoreVerticalCircle02Icon: () => import('./MoreVerticalCircle02Icon.js'),
  MoreVerticalSquare01Icon: () => import('./MoreVerticalSquare01Icon.js'),
  MoreVerticalSquare02Icon: () => import('./MoreVerticalSquare02Icon.js'),
  MoreVerticalIcon: () => import('./MoreVerticalIcon.js'),
  MortarIcon: () => import('./MortarIcon.js'),
  Mortarboard01Icon: () => import('./Mortarboard01Icon.js'),
  Mortarboard02Icon: () => import('./Mortarboard02Icon.js'),
  Mosque01Icon: () => import('./Mosque01Icon.js'),
  Mosque02Icon: () => import('./Mosque02Icon.js'),
  Mosque03Icon: () => import('./Mosque03Icon.js'),
  Mosque04Icon: () => import('./Mosque04Icon.js'),
  Mosque05Icon: () => import('./Mosque05Icon.js'),
  MosqueLocationIcon: () => import('./MosqueLocationIcon.js'),
  Motion01Icon: () => import('./Motion01Icon.js'),
  Motion02Icon: () => import('./Motion02Icon.js'),
  Motorbike01Icon: () => import('./Motorbike01Icon.js'),
  Motorbike02Icon: () => import('./Motorbike02Icon.js'),
  MountainIcon: () => import('./MountainIcon.js'),
  Mouse01Icon: () => import('./Mouse01Icon.js'),
  Mouse02Icon: () => import('./Mouse02Icon.js'),
  Mouse03Icon: () => import('./Mouse03Icon.js'),
  Mouse04Icon: () => import('./Mouse04Icon.js'),
  Mouse05Icon: () => import('./Mouse05Icon.js'),
  Mouse06Icon: () => import('./Mouse06Icon.js'),
  Mouse07Icon: () => import('./Mouse07Icon.js'),
  Mouse08Icon: () => import('./Mouse08Icon.js'),
  Mouse09Icon: () => import('./Mouse09Icon.js'),
  Mouse10Icon: () => import('./Mouse10Icon.js'),
  Mouse11Icon: () => import('./Mouse11Icon.js'),
  Mouse12Icon: () => import('./Mouse12Icon.js'),
  Mouse13Icon: () => import('./Mouse13Icon.js'),
  Mouse14Icon: () => import('./Mouse14Icon.js'),
  Mouse15Icon: () => import('./Mouse15Icon.js'),
  Mouse16Icon: () => import('./Mouse16Icon.js'),
  Mouse17Icon: () => import('./Mouse17Icon.js'),
  Mouse18Icon: () => import('./Mouse18Icon.js'),
  Mouse19Icon: () => import('./Mouse19Icon.js'),
  Mouse20Icon: () => import('./Mouse20Icon.js'),
  Mouse21Icon: () => import('./Mouse21Icon.js'),
  Mouse22Icon: () => import('./Mouse22Icon.js'),
  Mouse23Icon: () => import('./Mouse23Icon.js'),
  MouseLeftClick01Icon: () => import('./MouseLeftClick01Icon.js'),
  MouseLeftClick02Icon: () => import('./MouseLeftClick02Icon.js'),
  MouseLeftClick03Icon: () => import('./MouseLeftClick03Icon.js'),
  MouseLeftClick04Icon: () => import('./MouseLeftClick04Icon.js'),
  MouseLeftClick05Icon: () => import('./MouseLeftClick05Icon.js'),
  MouseLeftClick06Icon: () => import('./MouseLeftClick06Icon.js'),
  MousePointerOffIcon: () => import('./MousePointerOffIcon.js'),
  MouseRightClick01Icon: () => import('./MouseRightClick01Icon.js'),
  MouseRightClick02Icon: () => import('./MouseRightClick02Icon.js'),
  MouseRightClick03Icon: () => import('./MouseRightClick03Icon.js'),
  MouseRightClick04Icon: () => import('./MouseRightClick04Icon.js'),
  MouseRightClick05Icon: () => import('./MouseRightClick05Icon.js'),
  MouseRightClick06Icon: () => import('./MouseRightClick06Icon.js'),
  MouseScroll01Icon: () => import('./MouseScroll01Icon.js'),
  MouseScroll02Icon: () => import('./MouseScroll02Icon.js'),
  Move01Icon: () => import('./Move01Icon.js'),
  Move02Icon: () => import('./Move02Icon.js'),
  MoveBottomIcon: () => import('./MoveBottomIcon.js'),
  MoveLeftIcon: () => import('./MoveLeftIcon.js'),
  MoveRightIcon: () => import('./MoveRightIcon.js'),
  MoveIcon: () => import('./MoveIcon.js'),
  MoveToIcon: () => import('./MoveToIcon.js'),
  MoveTopIcon: () => import('./MoveTopIcon.js'),
  MpThree02Icon: () => import('./MpThree02Icon.js'),
  MpFour01Icon: () => import('./MpFour01Icon.js'),
  MpFour02Icon: () => import('./MpFour02Icon.js'),
  Mp301Icon: () => import('./Mp301Icon.js'),
  MuhammadIcon: () => import('./MuhammadIcon.js'),
  MultiplicationSignCircleIcon: () => import('./MultiplicationSignCircleIcon.js'),
  MultiplicationSignSquareIcon: () => import('./MultiplicationSignSquareIcon.js'),
  MultiplicationSignIcon: () => import('./MultiplicationSignIcon.js'),
  Mushroom01Icon: () => import('./Mushroom01Icon.js'),
  MushroomIcon: () => import('./MushroomIcon.js'),
  MusicThreeIcon: () => import('./MusicThreeIcon.js'),
  MusicNote01Icon: () => import('./MusicNote01Icon.js'),
  MusicNote02Icon: () => import('./MusicNote02Icon.js'),
  MusicNote03Icon: () => import('./MusicNote03Icon.js'),
  MusicNote04Icon: () => import('./MusicNote04Icon.js'),
  MusicNoteSquare01Icon: () => import('./MusicNoteSquare01Icon.js'),
  MusicNoteSquare02Icon: () => import('./MusicNoteSquare02Icon.js'),
  MuslimIcon: () => import('./MuslimIcon.js'),
  MuteIcon: () => import('./MuteIcon.js'),
  MymindIcon: () => import('./MymindIcon.js'),
  NThRootCircleIcon: () => import('./NThRootCircleIcon.js'),
  NThRootSquareIcon: () => import('./NThRootSquareIcon.js'),
  NThRootIcon: () => import('./NThRootIcon.js'),
  NanoTechnologyIcon: () => import('./NanoTechnologyIcon.js'),
  Napkins01Icon: () => import('./Napkins01Icon.js'),
  Napkins02Icon: () => import('./Napkins02Icon.js'),
  NaturalFoodIcon: () => import('./NaturalFoodIcon.js'),
  Navigation01Icon: () => import('./Navigation01Icon.js'),
  Navigation02Icon: () => import('./Navigation02Icon.js'),
  Navigation03Icon: () => import('./Navigation03Icon.js'),
  Navigation04Icon: () => import('./Navigation04Icon.js'),
  Navigation05Icon: () => import('./Navigation05Icon.js'),
  Navigation06Icon: () => import('./Navigation06Icon.js'),
  NavigationTwoOffIcon: () => import('./NavigationTwoOffIcon.js'),
  NavigationTwoIcon: () => import('./NavigationTwoIcon.js'),
  NavigationOffIcon: () => import('./NavigationOffIcon.js'),
  NavigationIcon: () => import('./NavigationIcon.js'),
  Navigator01Icon: () => import('./Navigator01Icon.js'),
  Navigator02Icon: () => import('./Navigator02Icon.js'),
  NecklaceIcon: () => import('./NecklaceIcon.js'),
  NerdIcon: () => import('./NerdIcon.js'),
  NeuralNetworkIcon: () => import('./NeuralNetworkIcon.js'),
  NeutralIcon: () => import('./NeutralIcon.js'),
  NewJobIcon: () => import('./NewJobIcon.js'),
  NewOfficeIcon: () => import('./NewOfficeIcon.js'),
  NewReleasesIcon: () => import('./NewReleasesIcon.js'),
  NewTwitterEllipseIcon: () => import('./NewTwitterEllipseIcon.js'),
  NewTwitterRectangleIcon: () => import('./NewTwitterRectangleIcon.js'),
  NewTwitterIcon: () => import('./NewTwitterIcon.js'),
  News01Icon: () => import('./News01Icon.js'),
  NewsIcon: () => import('./NewsIcon.js'),
  NextIcon: () => import('./NextIcon.js'),
  NextWeekIcon: () => import('./NextWeekIcon.js'),
  NfcIcon: () => import('./NfcIcon.js'),
  NikeIcon: () => import('./NikeIcon.js'),
  NineCircleIcon: () => import('./NineCircleIcon.js'),
  NineSquareIcon: () => import('./NineSquareIcon.js'),
  NintendoIcon: () => import('./NintendoIcon.js'),
  NintendoSwitchIcon: () => import('./NintendoSwitchIcon.js'),
  NiqabIcon: () => import('./NiqabIcon.js'),
  NoInternetIcon: () => import('./NoInternetIcon.js'),
  NoMeetingRoomIcon: () => import('./NoMeetingRoomIcon.js'),
  NoSignalIcon: () => import('./NoSignalIcon.js'),
  NodeAddIcon: () => import('./NodeAddIcon.js'),
  NodeEditIcon: () => import('./NodeEditIcon.js'),
  NodeMoveDownIcon: () => import('./NodeMoveDownIcon.js'),
  NodeMoveUpIcon: () => import('./NodeMoveUpIcon.js'),
  NodeRemoveIcon: () => import('./NodeRemoveIcon.js'),
  NoodlesIcon: () => import('./NoodlesIcon.js'),
  NoseIcon: () => import('./NoseIcon.js'),
  NotEqualSignCircleIcon: () => import('./NotEqualSignCircleIcon.js'),
  NotEqualSignSquareIcon: () => import('./NotEqualSignSquareIcon.js'),
  NotEqualSignIcon: () => import('./NotEqualSignIcon.js'),
  Note01Icon: () => import('./Note01Icon.js'),
  Note02Icon: () => import('./Note02Icon.js'),
  Note03Icon: () => import('./Note03Icon.js'),
  Note04Icon: () => import('./Note04Icon.js'),
  Note05Icon: () => import('./Note05Icon.js'),
  NoteAddIcon: () => import('./NoteAddIcon.js'),
  NoteDoneIcon: () => import('./NoteDoneIcon.js'),
  NoteEditIcon: () => import('./NoteEditIcon.js'),
  NoteRemoveIcon: () => import('./NoteRemoveIcon.js'),
  NoteIcon: () => import('./NoteIcon.js'),
  Notebook01Icon: () => import('./Notebook01Icon.js'),
  Notebook02Icon: () => import('./Notebook02Icon.js'),
  NotebookIcon: () => import('./NotebookIcon.js'),
  NotepadTextDashedIcon: () => import('./NotepadTextDashedIcon.js'),
  Notification01Icon: () => import('./Notification01Icon.js'),
  Notification02Icon: () => import('./Notification02Icon.js'),
  Notification03Icon: () => import('./Notification03Icon.js'),
  NotificationBlock01Icon: () => import('./NotificationBlock01Icon.js'),
  NotificationBlock02Icon: () => import('./NotificationBlock02Icon.js'),
  NotificationBlock03Icon: () => import('./NotificationBlock03Icon.js'),
  NotificationBubbleIcon: () => import('./NotificationBubbleIcon.js'),
  NotificationCircleIcon: () => import('./NotificationCircleIcon.js'),
  NotificationOff01Icon: () => import('./NotificationOff01Icon.js'),
  NotificationOff02Icon: () => import('./NotificationOff02Icon.js'),
  NotificationOff03Icon: () => import('./NotificationOff03Icon.js'),
  NotificationSnooze01Icon: () => import('./NotificationSnooze01Icon.js'),
  NotificationSnooze02Icon: () => import('./NotificationSnooze02Icon.js'),
  NotificationSnooze03Icon: () => import('./NotificationSnooze03Icon.js'),
  NotificationSquareIcon: () => import('./NotificationSquareIcon.js'),
  Notion01Icon: () => import('./Notion01Icon.js'),
  Notion02Icon: () => import('./Notion02Icon.js'),
  NpmIcon: () => import('./NpmIcon.js'),
  NuclearPowerIcon: () => import('./NuclearPowerIcon.js'),
  NutOffIcon: () => import('./NutOffIcon.js'),
  NutIcon: () => import('./NutIcon.js'),
  ObtuseIcon: () => import('./ObtuseIcon.js'),
  OctagonIcon: () => import('./OctagonIcon.js'),
  OctagonXIcon: () => import('./OctagonXIcon.js'),
  OctopusIcon: () => import('./OctopusIcon.js'),
  Office365Icon: () => import('./Office365Icon.js'),
  OfficeChairIcon: () => import('./OfficeChairIcon.js'),
  OfficeIcon: () => import('./OfficeIcon.js'),
  OilBarrelIcon: () => import('./OilBarrelIcon.js'),
  OkFingerIcon: () => import('./OkFingerIcon.js'),
  OlympicTorchIcon: () => import('./OlympicTorchIcon.js'),
  OneCircleIcon: () => import('./OneCircleIcon.js'),
  OneSquareIcon: () => import('./OneSquareIcon.js'),
  OnlineLearning01Icon: () => import('./OnlineLearning01Icon.js'),
  OnlineLearning02Icon: () => import('./OnlineLearning02Icon.js'),
  OnlineLearning03Icon: () => import('./OnlineLearning03Icon.js'),
  OnlineLearning04Icon: () => import('./OnlineLearning04Icon.js'),
  OpenCaptionIcon: () => import('./OpenCaptionIcon.js'),
  OpenSourceIcon: () => import('./OpenSourceIcon.js'),
  OptionIcon: () => import('./OptionIcon.js'),
  OptionsCallIcon: () => import('./OptionsCallIcon.js'),
  OrangeIcon: () => import('./OrangeIcon.js'),
  Orbit01Icon: () => import('./Orbit01Icon.js'),
  Orbit02Icon: () => import('./Orbit02Icon.js'),
  OrganicFoodIcon: () => import('./OrganicFoodIcon.js'),
  OrientationImageLandscapeToPotraitIcon: () => import('./OrientationImageLandscapeToPotraitIcon.js'),
  OrientationImagePotraitToLandscapeIcon: () => import('./OrientationImagePotraitToLandscapeIcon.js'),
  OrientationLandscapeToPotraitIcon: () => import('./OrientationLandscapeToPotraitIcon.js'),
  OrientationPotraitToLandscapeIcon: () => import('./OrientationPotraitToLandscapeIcon.js'),
  OrigamiIcon: () => import('./OrigamiIcon.js'),
  OrthogonalEdgeIcon: () => import('./OrthogonalEdgeIcon.js'),
  OvalIcon: () => import('./OvalIcon.js'),
  OvenIcon: () => import('./OvenIcon.js'),
  Package01Icon: () => import('./Package01Icon.js'),
  Package02Icon: () => import('./Package02Icon.js'),
  Package03Icon: () => import('./Package03Icon.js'),
  PackageAdd01Icon: () => import('./PackageAdd01Icon.js'),
  PackageAddIcon: () => import('./PackageAddIcon.js'),
  PackageDelivered01Icon: () => import('./PackageDelivered01Icon.js'),
  PackageDeliveredIcon: () => import('./PackageDeliveredIcon.js'),
  PackageDimensions01Icon: () => import('./PackageDimensions01Icon.js'),
  PackageDimensions02Icon: () => import('./PackageDimensions02Icon.js'),
  PackageMoving01Icon: () => import('./PackageMoving01Icon.js'),
  PackageMovingIcon: () => import('./PackageMovingIcon.js'),
  PackageOpenIcon: () => import('./PackageOpenIcon.js'),
  PackageOutOfStockIcon: () => import('./PackageOutOfStockIcon.js'),
  PackageProcess01Icon: () => import('./PackageProcess01Icon.js'),
  PackageProcessIcon: () => import('./PackageProcessIcon.js'),
  PackageReceive01Icon: () => import('./PackageReceive01Icon.js'),
  PackageReceiveIcon: () => import('./PackageReceiveIcon.js'),
  PackageRemove01Icon: () => import('./PackageRemove01Icon.js'),
  PackageRemoveIcon: () => import('./PackageRemoveIcon.js'),
  PackageSearch01Icon: () => import('./PackageSearch01Icon.js'),
  PackageSearchIcon: () => import('./PackageSearchIcon.js'),
  PackageSent01Icon: () => import('./PackageSent01Icon.js'),
  PackageSentIcon: () => import('./PackageSentIcon.js'),
  PackageIcon: () => import('./PackageIcon.js'),
  PackagingIcon: () => import('./PackagingIcon.js'),
  Pacman01Icon: () => import('./Pacman01Icon.js'),
  Pacman02Icon: () => import('./Pacman02Icon.js'),
  PaellaIcon: () => import('./PaellaIcon.js'),
  PaintBoardIcon: () => import('./PaintBoardIcon.js'),
  PaintBrush01Icon: () => import('./PaintBrush01Icon.js'),
  PaintBrush02Icon: () => import('./PaintBrush02Icon.js'),
  PaintBrush03Icon: () => import('./PaintBrush03Icon.js'),
  PaintBrush04Icon: () => import('./PaintBrush04Icon.js'),
  PaintBucketIcon: () => import('./PaintBucketIcon.js'),
  Pan01Icon: () => import('./Pan01Icon.js'),
  Pan02Icon: () => import('./Pan02Icon.js'),
  Pan03Icon: () => import('./Pan03Icon.js'),
  PanelBottomCloseIcon: () => import('./PanelBottomCloseIcon.js'),
  PanelLeftCloseIcon: () => import('./PanelLeftCloseIcon.js'),
  PanelLeftDashedIcon: () => import('./PanelLeftDashedIcon.js'),
  PanelLeftOpenIcon: () => import('./PanelLeftOpenIcon.js'),
  PanelLeftIcon: () => import('./PanelLeftIcon.js'),
  PanelRightCloseIcon: () => import('./PanelRightCloseIcon.js'),
  PanelRightDashedIcon: () => import('./PanelRightDashedIcon.js'),
  PanelRightOpenIcon: () => import('./PanelRightOpenIcon.js'),
  PanelRightIcon: () => import('./PanelRightIcon.js'),
  PanelTopBottomDashedIcon: () => import('./PanelTopBottomDashedIcon.js'),
  Parabola01Icon: () => import('./Parabola01Icon.js'),
  Parabola02Icon: () => import('./Parabola02Icon.js'),
  Parabola03Icon: () => import('./Parabola03Icon.js'),
  ParaglidingIcon: () => import('./ParaglidingIcon.js'),
  ParagraphBulletsPoint01Icon: () => import('./ParagraphBulletsPoint01Icon.js'),
  ParagraphBulletsPoint02Icon: () => import('./ParagraphBulletsPoint02Icon.js'),
  ParagraphSpacingIcon: () => import('./ParagraphSpacingIcon.js'),
  ParagraphIcon: () => import('./ParagraphIcon.js'),
  ParallelogramIcon: () => import('./ParallelogramIcon.js'),
  ParkingAreaCircleIcon: () => import('./ParkingAreaCircleIcon.js'),
  ParkingAreaSquareIcon: () => import('./ParkingAreaSquareIcon.js'),
  ParkingMeterIcon: () => import('./ParkingMeterIcon.js'),
  PartyIcon: () => import('./PartyIcon.js'),
  Passport01Icon: () => import('./Passport01Icon.js'),
  PassportExpiredIcon: () => import('./PassportExpiredIcon.js'),
  PassportIcon: () => import('./PassportIcon.js'),
  PassportValidIcon: () => import('./PassportValidIcon.js'),
  PasswordValidationIcon: () => import('./PasswordValidationIcon.js'),
  PathIcon: () => import('./PathIcon.js'),
  PathfinderCropIcon: () => import('./PathfinderCropIcon.js'),
  PathfinderDivideIcon: () => import('./PathfinderDivideIcon.js'),
  PathfinderExcludeIcon: () => import('./PathfinderExcludeIcon.js'),
  PathfinderIntersectIcon: () => import('./PathfinderIntersectIcon.js'),
  PathfinderMergeIcon: () => import('./PathfinderMergeIcon.js'),
  PathfinderMinusBackIcon: () => import('./PathfinderMinusBackIcon.js'),
  PathfinderMinusFrontIcon: () => import('./PathfinderMinusFrontIcon.js'),
  PathfinderOutlineIcon: () => import('./PathfinderOutlineIcon.js'),
  PathfinderTrimIcon: () => import('./PathfinderTrimIcon.js'),
  PathfinderUniteIcon: () => import('./PathfinderUniteIcon.js'),
  PatientIcon: () => import('./PatientIcon.js'),
  PatioIcon: () => import('./PatioIcon.js'),
  PauseCircleIcon: () => import('./PauseCircleIcon.js'),
  PauseIcon: () => import('./PauseIcon.js'),
  PavilonIcon: () => import('./PavilonIcon.js'),
  PayByCheckIcon: () => import('./PayByCheckIcon.js'),
  Payment01Icon: () => import('./Payment01Icon.js'),
  Payment02Icon: () => import('./Payment02Icon.js'),
  PaymentSuccess01Icon: () => import('./PaymentSuccess01Icon.js'),
  PaymentSuccess02Icon: () => import('./PaymentSuccess02Icon.js'),
  PayoneerIcon: () => import('./PayoneerIcon.js'),
  PaypalIcon: () => import('./PaypalIcon.js'),
  PcCaseIcon: () => import('./PcCaseIcon.js'),
  Pdf01Icon: () => import('./Pdf01Icon.js'),
  Pdf02Icon: () => import('./Pdf02Icon.js'),
  PeerToPeer01Icon: () => import('./PeerToPeer01Icon.js'),
  PeerToPeer02Icon: () => import('./PeerToPeer02Icon.js'),
  PeerToPeer03Icon: () => import('./PeerToPeer03Icon.js'),
  Pen01Icon: () => import('./Pen01Icon.js'),
  Pen02Icon: () => import('./Pen02Icon.js'),
  PenConnectBluetoothIcon: () => import('./PenConnectBluetoothIcon.js'),
  PenConnectUsbIcon: () => import('./PenConnectUsbIcon.js'),
  PenConnectWifiIcon: () => import('./PenConnectWifiIcon.js'),
  PenTool01Icon: () => import('./PenTool01Icon.js'),
  PenTool02Icon: () => import('./PenTool02Icon.js'),
  PenTool03Icon: () => import('./PenTool03Icon.js'),
  PenToolAddIcon: () => import('./PenToolAddIcon.js'),
  PenToolMinusIcon: () => import('./PenToolMinusIcon.js'),
  PencilEdit01Icon: () => import('./PencilEdit01Icon.js'),
  PencilEdit02Icon: () => import('./PencilEdit02Icon.js'),
  PencilRulerIcon: () => import('./PencilRulerIcon.js'),
  PencilIcon: () => import('./PencilIcon.js'),
  PendulumIcon: () => import('./PendulumIcon.js'),
  PensiveIcon: () => import('./PensiveIcon.js'),
  Pentagon01Icon: () => import('./Pentagon01Icon.js'),
  PentagonIcon: () => import('./PentagonIcon.js'),
  PercentCircleIcon: () => import('./PercentCircleIcon.js'),
  PercentSquareIcon: () => import('./PercentSquareIcon.js'),
  PercentIcon: () => import('./PercentIcon.js'),
  PerfumeIcon: () => import('./PerfumeIcon.js'),
  PeriscopeIcon: () => import('./PeriscopeIcon.js'),
  PermanentJobIcon: () => import('./PermanentJobIcon.js'),
  PerplexityAiIcon: () => import('./PerplexityAiIcon.js'),
  PerspectiveIcon: () => import('./PerspectiveIcon.js'),
  PetrolPumpIcon: () => import('./PetrolPumpIcon.js'),
  PexelsIcon: () => import('./PexelsIcon.js'),
  PhilippinePesoIcon: () => import('./PhilippinePesoIcon.js'),
  PhoneArrowDownIcon: () => import('./PhoneArrowDownIcon.js'),
  PhoneArrowUpIcon: () => import('./PhoneArrowUpIcon.js'),
  PhoneCheckIcon: () => import('./PhoneCheckIcon.js'),
  PhoneDeveloperModeIcon: () => import('./PhoneDeveloperModeIcon.js'),
  PhoneEraseIcon: () => import('./PhoneEraseIcon.js'),
  PhoneLockIcon: () => import('./PhoneLockIcon.js'),
  PhoneOff01Icon: () => import('./PhoneOff01Icon.js'),
  PhoneOff02Icon: () => import('./PhoneOff02Icon.js'),
  PhpIcon: () => import('./PhpIcon.js'),
  PhysicsIcon: () => import('./PhysicsIcon.js'),
  PiCircleIcon: () => import('./PiCircleIcon.js'),
  PiSquareIcon: () => import('./PiSquareIcon.js'),
  PiIcon: () => import('./PiIcon.js'),
  PicasaIcon: () => import('./PicasaIcon.js'),
  Pickup01Icon: () => import('./Pickup01Icon.js'),
  Pickup02Icon: () => import('./Pickup02Icon.js'),
  PictureInPictureExitIcon: () => import('./PictureInPictureExitIcon.js'),
  PictureInPictureOnIcon: () => import('./PictureInPictureOnIcon.js'),
  PieChart01Icon: () => import('./PieChart01Icon.js'),
  PieChart02Icon: () => import('./PieChart02Icon.js'),
  PieChart03Icon: () => import('./PieChart03Icon.js'),
  PieChart04Icon: () => import('./PieChart04Icon.js'),
  PieChart05Icon: () => import('./PieChart05Icon.js'),
  PieChart06Icon: () => import('./PieChart06Icon.js'),
  PieChart07Icon: () => import('./PieChart07Icon.js'),
  PieChart08Icon: () => import('./PieChart08Icon.js'),
  PieChart09Icon: () => import('./PieChart09Icon.js'),
  PieChartSquareIcon: () => import('./PieChartSquareIcon.js'),
  PieChartIcon: () => import('./PieChartIcon.js'),
  PieIcon: () => import('./PieIcon.js'),
  PiggyBankIcon: () => import('./PiggyBankIcon.js'),
  PillBottleIcon: () => import('./PillBottleIcon.js'),
  PillOffIcon: () => import('./PillOffIcon.js'),
  PillIcon: () => import('./PillIcon.js'),
  PillsTabletIcon: () => import('./PillsTabletIcon.js'),
  Pin02Icon: () => import('./Pin02Icon.js'),
  PinCodeIcon: () => import('./PinCodeIcon.js'),
  PinLocation01Icon: () => import('./PinLocation01Icon.js'),
  PinLocation02Icon: () => import('./PinLocation02Icon.js'),
  PinLocation03Icon: () => import('./PinLocation03Icon.js'),
  PinOffIcon: () => import('./PinOffIcon.js'),
  PinIcon: () => import('./PinIcon.js'),
  PineTreeIcon: () => import('./PineTreeIcon.js'),
  PinterestIcon: () => import('./PinterestIcon.js'),
  PipelineIcon: () => import('./PipelineIcon.js'),
  PisaTowerIcon: () => import('./PisaTowerIcon.js'),
  PivotIcon: () => import('./PivotIcon.js'),
  Pizza01Icon: () => import('./Pizza01Icon.js'),
  Pizza02Icon: () => import('./Pizza02Icon.js'),
  Pizza03Icon: () => import('./Pizza03Icon.js'),
  Pizza04Icon: () => import('./Pizza04Icon.js'),
  PizzaCutterIcon: () => import('./PizzaCutterIcon.js'),
  PlaneIcon: () => import('./PlaneIcon.js'),
  Plant01Icon: () => import('./Plant01Icon.js'),
  Plant02Icon: () => import('./Plant02Icon.js'),
  Plant03Icon: () => import('./Plant03Icon.js'),
  Plant04Icon: () => import('./Plant04Icon.js'),
  PlateIcon: () => import('./PlateIcon.js'),
  PlaxoIcon: () => import('./PlaxoIcon.js'),
  PlayCircle02Icon: () => import('./PlayCircle02Icon.js'),
  PlayCircleIcon: () => import('./PlayCircleIcon.js'),
  PlayListAddIcon: () => import('./PlayListAddIcon.js'),
  PlayListFavourite01Icon: () => import('./PlayListFavourite01Icon.js'),
  PlayListFavourite02Icon: () => import('./PlayListFavourite02Icon.js'),
  PlayListMinusIcon: () => import('./PlayListMinusIcon.js'),
  PlayListRemoveIcon: () => import('./PlayListRemoveIcon.js'),
  PlayListIcon: () => import('./PlayListIcon.js'),
  PlaySquareIcon: () => import('./PlaySquareIcon.js'),
  PlayStoreIcon: () => import('./PlayStoreIcon.js'),
  PlayIcon: () => import('./PlayIcon.js'),
  Playlist01Icon: () => import('./Playlist01Icon.js'),
  Playlist02Icon: () => import('./Playlist02Icon.js'),
  Playlist03Icon: () => import('./Playlist03Icon.js'),
  PlazaIcon: () => import('./PlazaIcon.js'),
  Plug01Icon: () => import('./Plug01Icon.js'),
  Plug02Icon: () => import('./Plug02Icon.js'),
  PlugSocketIcon: () => import('./PlugSocketIcon.js'),
  PlusMinus01Icon: () => import('./PlusMinus01Icon.js'),
  PlusMinus02Icon: () => import('./PlusMinus02Icon.js'),
  PlusMinus03Icon: () => import('./PlusMinus03Icon.js'),
  PlusMinusCircle01Icon: () => import('./PlusMinusCircle01Icon.js'),
  PlusMinusCircle02Icon: () => import('./PlusMinusCircle02Icon.js'),
  PlusMinusSquare01Icon: () => import('./PlusMinusSquare01Icon.js'),
  PlusMinusSquare02Icon: () => import('./PlusMinusSquare02Icon.js'),
  PlusMinusIcon: () => import('./PlusMinusIcon.js'),
  PlusSignCircleIcon: () => import('./PlusSignCircleIcon.js'),
  PlusSignSquareIcon: () => import('./PlusSignSquareIcon.js'),
  PlusSignIcon: () => import('./PlusSignIcon.js'),
  Png01Icon: () => import('./Png01Icon.js'),
  Png02Icon: () => import('./Png02Icon.js'),
  PocketKnifeIcon: () => import('./PocketKnifeIcon.js'),
  PocketIcon: () => import('./PocketIcon.js'),
  PodcastIcon: () => import('./PodcastIcon.js'),
  PodiumIcon: () => import('./PodiumIcon.js'),
  PointingLeft01Icon: () => import('./PointingLeft01Icon.js'),
  PointingLeft02Icon: () => import('./PointingLeft02Icon.js'),
  PointingLeft03Icon: () => import('./PointingLeft03Icon.js'),
  PointingLeft04Icon: () => import('./PointingLeft04Icon.js'),
  PointingLeft05Icon: () => import('./PointingLeft05Icon.js'),
  PointingLeft06Icon: () => import('./PointingLeft06Icon.js'),
  PointingLeft07Icon: () => import('./PointingLeft07Icon.js'),
  PointingLeft08Icon: () => import('./PointingLeft08Icon.js'),
  PointingRight01Icon: () => import('./PointingRight01Icon.js'),
  PointingRight02Icon: () => import('./PointingRight02Icon.js'),
  PointingRight03Icon: () => import('./PointingRight03Icon.js'),
  PointingRight04Icon: () => import('./PointingRight04Icon.js'),
  PointingRight05Icon: () => import('./PointingRight05Icon.js'),
  PointingRight06Icon: () => import('./PointingRight06Icon.js'),
  PointingRight07Icon: () => import('./PointingRight07Icon.js'),
  PointingRight08Icon: () => import('./PointingRight08Icon.js'),
  PokeballIcon: () => import('./PokeballIcon.js'),
  PokemonIcon: () => import('./PokemonIcon.js'),
  PoliceBadgeIcon: () => import('./PoliceBadgeIcon.js'),
  PoliceCapIcon: () => import('./PoliceCapIcon.js'),
  PoliceCarIcon: () => import('./PoliceCarIcon.js'),
  PoliceStationIcon: () => import('./PoliceStationIcon.js'),
  PolicyIcon: () => import('./PolicyIcon.js'),
  PolyTankIcon: () => import('./PolyTankIcon.js'),
  PolygonIcon: () => import('./PolygonIcon.js'),
  PoolIcon: () => import('./PoolIcon.js'),
  PoolTableIcon: () => import('./PoolTableIcon.js'),
  PoopIcon: () => import('./PoopIcon.js'),
  PopcornIcon: () => import('./PopcornIcon.js'),
  PopsicleIcon: () => import('./PopsicleIcon.js'),
  Pot01Icon: () => import('./Pot01Icon.js'),
  Pot02Icon: () => import('./Pot02Icon.js'),
  PotionIcon: () => import('./PotionIcon.js'),
  PoundCircleIcon: () => import('./PoundCircleIcon.js'),
  PoundReceiveIcon: () => import('./PoundReceiveIcon.js'),
  PoundSendIcon: () => import('./PoundSendIcon.js'),
  PoundSquareIcon: () => import('./PoundSquareIcon.js'),
  PoundIcon: () => import('./PoundIcon.js'),
  PowerOffIcon: () => import('./PowerOffIcon.js'),
  PowerServiceIcon: () => import('./PowerServiceIcon.js'),
  PowerSocket01Icon: () => import('./PowerSocket01Icon.js'),
  PowerSocket02Icon: () => import('./PowerSocket02Icon.js'),
  PowerIcon: () => import('./PowerIcon.js'),
  Ppt01Icon: () => import('./Ppt01Icon.js'),
  Ppt02Icon: () => import('./Ppt02Icon.js'),
  PrawnIcon: () => import('./PrawnIcon.js'),
  PrayerRug01Icon: () => import('./PrayerRug01Icon.js'),
  PrayerRug02Icon: () => import('./PrayerRug02Icon.js'),
  PreferenceHorizontalIcon: () => import('./PreferenceHorizontalIcon.js'),
  PreferenceVerticalIcon: () => import('./PreferenceVerticalIcon.js'),
  PrescriptionIcon: () => import('./PrescriptionIcon.js'),
  PrescriptionsIcon: () => import('./PrescriptionsIcon.js'),
  Presentation01Icon: () => import('./Presentation01Icon.js'),
  Presentation02Icon: () => import('./Presentation02Icon.js'),
  Presentation03Icon: () => import('./Presentation03Icon.js'),
  Presentation04Icon: () => import('./Presentation04Icon.js'),
  Presentation05Icon: () => import('./Presentation05Icon.js'),
  Presentation06Icon: () => import('./Presentation06Icon.js'),
  Presentation07Icon: () => import('./Presentation07Icon.js'),
  PresentationBarChart01Icon: () => import('./PresentationBarChart01Icon.js'),
  PresentationBarChart02Icon: () => import('./PresentationBarChart02Icon.js'),
  PresentationLineChart01Icon: () => import('./PresentationLineChart01Icon.js'),
  PresentationLineChart02Icon: () => import('./PresentationLineChart02Icon.js'),
  PresentationOnlineIcon: () => import('./PresentationOnlineIcon.js'),
  PresentationPodiumIcon: () => import('./PresentationPodiumIcon.js'),
  PreviousIcon: () => import('./PreviousIcon.js'),
  PrinterThreeDIcon: () => import('./PrinterThreeDIcon.js'),
  PrinterCheckIcon: () => import('./PrinterCheckIcon.js'),
  PrinterOffIcon: () => import('./PrinterOffIcon.js'),
  PrinterIcon: () => import('./PrinterIcon.js'),
  PrinterXIcon: () => import('./PrinterXIcon.js'),
  Prism01Icon: () => import('./Prism01Icon.js'),
  PrismIcon: () => import('./PrismIcon.js'),
  PrisonGuardIcon: () => import('./PrisonGuardIcon.js'),
  PrisonIcon: () => import('./PrisonIcon.js'),
  PrisonerIcon: () => import('./PrisonerIcon.js'),
  ProductLoadingIcon: () => import('./ProductLoadingIcon.js'),
  Profile02Icon: () => import('./Profile02Icon.js'),
  ProfileIcon: () => import('./ProfileIcon.js'),
  ProfitIcon: () => import('./ProfitIcon.js'),
  ProgrammingFlagIcon: () => import('./ProgrammingFlagIcon.js'),
  Progress01Icon: () => import('./Progress01Icon.js'),
  Progress02Icon: () => import('./Progress02Icon.js'),
  Progress03Icon: () => import('./Progress03Icon.js'),
  Progress04Icon: () => import('./Progress04Icon.js'),
  ProgressIcon: () => import('./ProgressIcon.js'),
  Projector01Icon: () => import('./Projector01Icon.js'),
  ProjectorIcon: () => import('./ProjectorIcon.js'),
  PromotionIcon: () => import('./PromotionIcon.js'),
  PropertyAddIcon: () => import('./PropertyAddIcon.js'),
  PropertyDeleteIcon: () => import('./PropertyDeleteIcon.js'),
  PropertyEditIcon: () => import('./PropertyEditIcon.js'),
  PropertyNewIcon: () => import('./PropertyNewIcon.js'),
  PropertySearchIcon: () => import('./PropertySearchIcon.js'),
  PropertyViewIcon: () => import('./PropertyViewIcon.js'),
  ProportionsIcon: () => import('./ProportionsIcon.js'),
  ProtectionMaskIcon: () => import('./ProtectionMaskIcon.js'),
  PulleyIcon: () => import('./PulleyIcon.js'),
  Pulse01Icon: () => import('./Pulse01Icon.js'),
  Pulse02Icon: () => import('./Pulse02Icon.js'),
  PulseRectangle01Icon: () => import('./PulseRectangle01Icon.js'),
  PulseRectangle02Icon: () => import('./PulseRectangle02Icon.js'),
  PumpkinIcon: () => import('./PumpkinIcon.js'),
  PunchIcon: () => import('./PunchIcon.js'),
  PunchingBall01Icon: () => import('./PunchingBall01Icon.js'),
  PunchingBall02Icon: () => import('./PunchingBall02Icon.js'),
  Purse01Icon: () => import('./Purse01Icon.js'),
  PurseIcon: () => import('./PurseIcon.js'),
  PushUpBarIcon: () => import('./PushUpBarIcon.js'),
  PuzzleIcon: () => import('./PuzzleIcon.js'),
  PyramidMaslowoIcon: () => import('./PyramidMaslowoIcon.js'),
  PyramidIcon: () => import('./PyramidIcon.js'),
  PyramidStructure01Icon: () => import('./PyramidStructure01Icon.js'),
  PyramidStructure02Icon: () => import('./PyramidStructure02Icon.js'),
  PythonIcon: () => import('./PythonIcon.js'),
  QqPlotIcon: () => import('./QqPlotIcon.js'),
  QrCode01Icon: () => import('./QrCode01Icon.js'),
  QrCodeScanIcon: () => import('./QrCodeScanIcon.js'),
  QrCodeIcon: () => import('./QrCodeIcon.js'),
  QuestionIcon: () => import('./QuestionIcon.js'),
  Queue01Icon: () => import('./Queue01Icon.js'),
  Queue02Icon: () => import('./Queue02Icon.js'),
  QuillWrite01Icon: () => import('./QuillWrite01Icon.js'),
  QuillWrite02Icon: () => import('./QuillWrite02Icon.js'),
  Quiz01Icon: () => import('./Quiz01Icon.js'),
  Quiz02Icon: () => import('./Quiz02Icon.js'),
  Quiz03Icon: () => import('./Quiz03Icon.js'),
  Quiz04Icon: () => import('./Quiz04Icon.js'),
  Quiz05Icon: () => import('./Quiz05Icon.js'),
  QuoraIcon: () => import('./QuoraIcon.js'),
  QuoteDownCircleIcon: () => import('./QuoteDownCircleIcon.js'),
  QuoteDownSquareIcon: () => import('./QuoteDownSquareIcon.js'),
  QuoteDownIcon: () => import('./QuoteDownIcon.js'),
  QuoteUpCircleIcon: () => import('./QuoteUpCircleIcon.js'),
  QuoteUpSquareIcon: () => import('./QuoteUpSquareIcon.js'),
  QuoteUpIcon: () => import('./QuoteUpIcon.js'),
  QuotesIcon: () => import('./QuotesIcon.js'),
  Quran01Icon: () => import('./Quran01Icon.js'),
  Quran02Icon: () => import('./Quran02Icon.js'),
  Quran03Icon: () => import('./Quran03Icon.js'),
  QwenIcon: () => import('./QwenIcon.js'),
  RacingFlagIcon: () => import('./RacingFlagIcon.js'),
  Radar01Icon: () => import('./Radar01Icon.js'),
  Radar02Icon: () => import('./Radar02Icon.js'),
  Radar03Icon: () => import('./Radar03Icon.js'),
  RadialIcon: () => import('./RadialIcon.js'),
  RadiationIcon: () => import('./RadiationIcon.js'),
  Radio01Icon: () => import('./Radio01Icon.js'),
  Radio02Icon: () => import('./Radio02Icon.js'),
  RadioButtonIcon: () => import('./RadioButtonIcon.js'),
  RadioIcon: () => import('./RadioIcon.js'),
  RadioactiveAlertIcon: () => import('./RadioactiveAlertIcon.js'),
  RadiusIcon: () => import('./RadiusIcon.js'),
  RailSymbolIcon: () => import('./RailSymbolIcon.js'),
  RainDoubleDropIcon: () => import('./RainDoubleDropIcon.js'),
  RainDropIcon: () => import('./RainDropIcon.js'),
  RainIcon: () => import('./RainIcon.js'),
  RainbowIcon: () => import('./RainbowIcon.js'),
  RamMemoryIcon: () => import('./RamMemoryIcon.js'),
  Ramadhan01Icon: () => import('./Ramadhan01Icon.js'),
  Ramadhan02Icon: () => import('./Ramadhan02Icon.js'),
  RamadhanMonthIcon: () => import('./RamadhanMonthIcon.js'),
  RankingIcon: () => import('./RankingIcon.js'),
  Rar01Icon: () => import('./Rar01Icon.js'),
  Rar02Icon: () => import('./Rar02Icon.js'),
  RatioIcon: () => import('./RatioIcon.js'),
  Raw01Icon: () => import('./Raw01Icon.js'),
  Raw02Icon: () => import('./Raw02Icon.js'),
  ReIcon: () => import('./ReIcon.js'),
  ReactIcon: () => import('./ReactIcon.js'),
  RealEstate01Icon: () => import('./RealEstate01Icon.js'),
  RealEstate02Icon: () => import('./RealEstate02Icon.js'),
  ReceiptCentIcon: () => import('./ReceiptCentIcon.js'),
  ReceiptDollarIcon: () => import('./ReceiptDollarIcon.js'),
  ReceiptEuroIcon: () => import('./ReceiptEuroIcon.js'),
  ReceiptIndianRupeeIcon: () => import('./ReceiptIndianRupeeIcon.js'),
  ReceiptJapaneseYenIcon: () => import('./ReceiptJapaneseYenIcon.js'),
  ReceiptPoundSterlingIcon: () => import('./ReceiptPoundSterlingIcon.js'),
  ReceiptRussianRubleIcon: () => import('./ReceiptRussianRubleIcon.js'),
  ReceiptSwissFrancIcon: () => import('./ReceiptSwissFrancIcon.js'),
  ReceiptTextIcon: () => import('./ReceiptTextIcon.js'),
  ReceiptTurkishLiraIcon: () => import('./ReceiptTurkishLiraIcon.js'),
  RecordIcon: () => import('./RecordIcon.js'),
  RecoveryMailIcon: () => import('./RecoveryMailIcon.js'),
  RectangleCircleIcon: () => import('./RectangleCircleIcon.js'),
  Rectangular01Icon: () => import('./Rectangular01Icon.js'),
  RectangularIcon: () => import('./RectangularIcon.js'),
  Recycle01Icon: () => import('./Recycle01Icon.js'),
  Recycle02Icon: () => import('./Recycle02Icon.js'),
  Recycle03Icon: () => import('./Recycle03Icon.js'),
  RedditIcon: () => import('./RedditIcon.js'),
  Redo02Icon: () => import('./Redo02Icon.js'),
  Redo03Icon: () => import('./Redo03Icon.js'),
  RedoDotIcon: () => import('./RedoDotIcon.js'),
  RedoIcon: () => import('./RedoIcon.js'),
  ReduceParagraphIcon: () => import('./ReduceParagraphIcon.js'),
  ReflexIcon: () => import('./ReflexIcon.js'),
  Refresh01Icon: () => import('./Refresh01Icon.js'),
  Refresh03Icon: () => import('./Refresh03Icon.js'),
  Refresh04Icon: () => import('./Refresh04Icon.js'),
  RefreshCwOffIcon: () => import('./RefreshCwOffIcon.js'),
  RefreshDotIcon: () => import('./RefreshDotIcon.js'),
  RefreshIcon: () => import('./RefreshIcon.js'),
  RefrigeratorIcon: () => import('./RefrigeratorIcon.js'),
  RegexIcon: () => import('./RegexIcon.js'),
  RegisterIcon: () => import('./RegisterIcon.js'),
  RegisteredIcon: () => import('./RegisteredIcon.js'),
  Relieved01Icon: () => import('./Relieved01Icon.js'),
  Relieved02Icon: () => import('./Relieved02Icon.js'),
  ReloadIcon: () => import('./ReloadIcon.js'),
  ReminderIcon: () => import('./ReminderIcon.js'),
  RemoteControlIcon: () => import('./RemoteControlIcon.js'),
  Remove01Icon: () => import('./Remove01Icon.js'),
  Remove02Icon: () => import('./Remove02Icon.js'),
  RemoveCircleHalfDotIcon: () => import('./RemoveCircleHalfDotIcon.js'),
  RemoveCircleIcon: () => import('./RemoveCircleIcon.js'),
  RemoveFemaleIcon: () => import('./RemoveFemaleIcon.js'),
  RemoveMaleIcon: () => import('./RemoveMaleIcon.js'),
  RemovePiIcon: () => import('./RemovePiIcon.js'),
  RemoveSquareIcon: () => import('./RemoveSquareIcon.js'),
  RenewableEnergy01Icon: () => import('./RenewableEnergy01Icon.js'),
  RenewableEnergyIcon: () => import('./RenewableEnergyIcon.js'),
  RepairIcon: () => import('./RepairIcon.js'),
  RepeatOffIcon: () => import('./RepeatOffIcon.js'),
  RepeatOne01Icon: () => import('./RepeatOne01Icon.js'),
  RepeatOne02Icon: () => import('./RepeatOne02Icon.js'),
  RepeatIcon: () => import('./RepeatIcon.js'),
  ReplaceAllIcon: () => import('./ReplaceAllIcon.js'),
  ReplaceIcon: () => import('./ReplaceIcon.js'),
  ReplayIcon: () => import('./ReplayIcon.js'),
  ReplitIcon: () => import('./ReplitIcon.js'),
  RepositoryIcon: () => import('./RepositoryIcon.js'),
  RepostIcon: () => import('./RepostIcon.js'),
  ResetPasswordIcon: () => import('./ResetPasswordIcon.js'),
  Resize01Icon: () => import('./Resize01Icon.js'),
  Resize02Icon: () => import('./Resize02Icon.js'),
  ResizeFieldRectangleIcon: () => import('./ResizeFieldRectangleIcon.js'),
  ResizeFieldIcon: () => import('./ResizeFieldIcon.js'),
  ResourcesAddIcon: () => import('./ResourcesAddIcon.js'),
  ResourcesRemoveIcon: () => import('./ResourcesRemoveIcon.js'),
  Restaurant01Icon: () => import('./Restaurant01Icon.js'),
  Restaurant02Icon: () => import('./Restaurant02Icon.js'),
  Restaurant03Icon: () => import('./Restaurant03Icon.js'),
  RestaurantIcon: () => import('./RestaurantIcon.js'),
  RestaurantTableIcon: () => import('./RestaurantTableIcon.js'),
  RestoreBinIcon: () => import('./RestoreBinIcon.js'),
  ReturnRequestIcon: () => import('./ReturnRequestIcon.js'),
  ReverseWithdrawal01Icon: () => import('./ReverseWithdrawal01Icon.js'),
  ReverseWithdrawal02Icon: () => import('./ReverseWithdrawal02Icon.js'),
  Rhombus01Icon: () => import('./Rhombus01Icon.js'),
  RhombusIcon: () => import('./RhombusIcon.js'),
  RibbonIcon: () => import('./RibbonIcon.js'),
  RiceBowl01Icon: () => import('./RiceBowl01Icon.js'),
  RiceBowl02Icon: () => import('./RiceBowl02Icon.js'),
  RightAngleIcon: () => import('./RightAngleIcon.js'),
  RightToLeftBlockQuoteIcon: () => import('./RightToLeftBlockQuoteIcon.js'),
  RightToLeftListBulletIcon: () => import('./RightToLeftListBulletIcon.js'),
  RightToLeftListDashIcon: () => import('./RightToLeftListDashIcon.js'),
  RightToLeftListNumberIcon: () => import('./RightToLeftListNumberIcon.js'),
  RightToLeftListTriangleIcon: () => import('./RightToLeftListTriangleIcon.js'),
  RightTriangleIcon: () => import('./RightTriangleIcon.js'),
  RippleIcon: () => import('./RippleIcon.js'),
  RiyalRectangleIcon: () => import('./RiyalRectangleIcon.js'),
  RiyalIcon: () => import('./RiyalIcon.js'),
  Road01Icon: () => import('./Road01Icon.js'),
  Road02Icon: () => import('./Road02Icon.js'),
  RoadLocation01Icon: () => import('./RoadLocation01Icon.js'),
  RoadLocation02Icon: () => import('./RoadLocation02Icon.js'),
  RoadIcon: () => import('./RoadIcon.js'),
  RoadWaysideIcon: () => import('./RoadWaysideIcon.js'),
  Robot01Icon: () => import('./Robot01Icon.js'),
  Robot02Icon: () => import('./Robot02Icon.js'),
  RoboticIcon: () => import('./RoboticIcon.js'),
  Rocket01Icon: () => import('./Rocket01Icon.js'),
  Rocket02Icon: () => import('./Rocket02Icon.js'),
  RocketIcon: () => import('./RocketIcon.js'),
  RockingChairIcon: () => import('./RockingChairIcon.js'),
  RockingHorseIcon: () => import('./RockingHorseIcon.js'),
  RollerCoasterIcon: () => import('./RollerCoasterIcon.js'),
  RollerSkateIcon: () => import('./RollerSkateIcon.js'),
  RollingPinIcon: () => import('./RollingPinIcon.js'),
  Root01Icon: () => import('./Root01Icon.js'),
  Root02Icon: () => import('./Root02Icon.js'),
  RootFirstBracketIcon: () => import('./RootFirstBracketIcon.js'),
  RootSecondBracketIcon: () => import('./RootSecondBracketIcon.js'),
  RootThirdBracketIcon: () => import('./RootThirdBracketIcon.js'),
  RootCircleIcon: () => import('./RootCircleIcon.js'),
  RoseIcon: () => import('./RoseIcon.js'),
  Rotate01Icon: () => import('./Rotate01Icon.js'),
  Rotate02Icon: () => import('./Rotate02Icon.js'),
  Rotate360Icon: () => import('./Rotate360Icon.js'),
  RotateBottomLeftIcon: () => import('./RotateBottomLeftIcon.js'),
  RotateBottomRightIcon: () => import('./RotateBottomRightIcon.js'),
  RotateCcwSquareIcon: () => import('./RotateCcwSquareIcon.js'),
  RotateClockwiseIcon: () => import('./RotateClockwiseIcon.js'),
  RotateCropIcon: () => import('./RotateCropIcon.js'),
  RotateCwSquareIcon: () => import('./RotateCwSquareIcon.js'),
  RotateLeft01Icon: () => import('./RotateLeft01Icon.js'),
  RotateLeft02Icon: () => import('./RotateLeft02Icon.js'),
  RotateLeft03Icon: () => import('./RotateLeft03Icon.js'),
  RotateLeft04Icon: () => import('./RotateLeft04Icon.js'),
  RotateLeft05Icon: () => import('./RotateLeft05Icon.js'),
  RotateLeft06Icon: () => import('./RotateLeft06Icon.js'),
  RotateRight01Icon: () => import('./RotateRight01Icon.js'),
  RotateRight02Icon: () => import('./RotateRight02Icon.js'),
  RotateRight03Icon: () => import('./RotateRight03Icon.js'),
  RotateRight04Icon: () => import('./RotateRight04Icon.js'),
  RotateRight05Icon: () => import('./RotateRight05Icon.js'),
  RotateRight06Icon: () => import('./RotateRight06Icon.js'),
  RotateSquareIcon: () => import('./RotateSquareIcon.js'),
  RotateTopLeftIcon: () => import('./RotateTopLeftIcon.js'),
  RotateTopRightIcon: () => import('./RotateTopRightIcon.js'),
  Route01Icon: () => import('./Route01Icon.js'),
  Route02Icon: () => import('./Route02Icon.js'),
  Route03Icon: () => import('./Route03Icon.js'),
  RouteBlockIcon: () => import('./RouteBlockIcon.js'),
  Router01Icon: () => import('./Router01Icon.js'),
  Router02Icon: () => import('./Router02Icon.js'),
  RouterIcon: () => import('./RouterIcon.js'),
  RowDeleteIcon: () => import('./RowDeleteIcon.js'),
  RowInsertIcon: () => import('./RowInsertIcon.js'),
  RssConnected01Icon: () => import('./RssConnected01Icon.js'),
  RssConnected02Icon: () => import('./RssConnected02Icon.js'),
  RssErrorIcon: () => import('./RssErrorIcon.js'),
  RssLockedIcon: () => import('./RssLockedIcon.js'),
  RssIcon: () => import('./RssIcon.js'),
  RssUnlockedIcon: () => import('./RssUnlockedIcon.js'),
  RubElHizbIcon: () => import('./RubElHizbIcon.js'),
  RubberDuckIcon: () => import('./RubberDuckIcon.js'),
  RubiksCubeIcon: () => import('./RubiksCubeIcon.js'),
  RukuIcon: () => import('./RukuIcon.js'),
  RulerIcon: () => import('./RulerIcon.js'),
  RunningShoesIcon: () => import('./RunningShoesIcon.js'),
  RupeeCircleIcon: () => import('./RupeeCircleIcon.js'),
  RupeeShieldIcon: () => import('./RupeeShieldIcon.js'),
  RupeeSquareIcon: () => import('./RupeeSquareIcon.js'),
  RupeeIcon: () => import('./RupeeIcon.js'),
  RussianRubleIcon: () => import('./RussianRubleIcon.js'),
  Sad01Icon: () => import('./Sad01Icon.js'),
  Sad02Icon: () => import('./Sad02Icon.js'),
  SadDizzyIcon: () => import('./SadDizzyIcon.js'),
  SafariIcon: () => import('./SafariIcon.js'),
  SafeBoxIcon: () => import('./SafeBoxIcon.js'),
  SafeDelivery01Icon: () => import('./SafeDelivery01Icon.js'),
  SafeDelivery02Icon: () => import('./SafeDelivery02Icon.js'),
  SafeIcon: () => import('./SafeIcon.js'),
  SafetyPin01Icon: () => import('./SafetyPin01Icon.js'),
  SafetyPin02Icon: () => import('./SafetyPin02Icon.js'),
  SailboatCoastalIcon: () => import('./SailboatCoastalIcon.js'),
  SailboatOffshoreIcon: () => import('./SailboatOffshoreIcon.js'),
  SakuraIcon: () => import('./SakuraIcon.js'),
  SaladIcon: () => import('./SaladIcon.js'),
  SalahIcon: () => import('./SalahIcon.js'),
  SalahTimeIcon: () => import('./SalahTimeIcon.js'),
  SaleTag01Icon: () => import('./SaleTag01Icon.js'),
  SaleTag02Icon: () => import('./SaleTag02Icon.js'),
  SandalsIcon: () => import('./SandalsIcon.js'),
  Satellite01Icon: () => import('./Satellite01Icon.js'),
  Satellite02Icon: () => import('./Satellite02Icon.js'),
  Satellite03Icon: () => import('./Satellite03Icon.js'),
  SatelliteIcon: () => import('./SatelliteIcon.js'),
  Saturn01Icon: () => import('./Saturn01Icon.js'),
  Saturn02Icon: () => import('./Saturn02Icon.js'),
  SaturnIcon: () => import('./SaturnIcon.js'),
  SaudiRiyalIcon: () => import('./SaudiRiyalIcon.js'),
  SausageIcon: () => import('./SausageIcon.js'),
  SaveAllIcon: () => import('./SaveAllIcon.js'),
  SaveEnergy01Icon: () => import('./SaveEnergy01Icon.js'),
  SaveEnergy02Icon: () => import('./SaveEnergy02Icon.js'),
  SaveMoneyDollarIcon: () => import('./SaveMoneyDollarIcon.js'),
  SaveMoneyEuroIcon: () => import('./SaveMoneyEuroIcon.js'),
  SaveMoneyPoundIcon: () => import('./SaveMoneyPoundIcon.js'),
  SaveMoneyYenIcon: () => import('./SaveMoneyYenIcon.js'),
  SaveOffIcon: () => import('./SaveOffIcon.js'),
  SaveIcon: () => import('./SaveIcon.js'),
  SavingsIcon: () => import('./SavingsIcon.js'),
  ScanEyeIcon: () => import('./ScanEyeIcon.js'),
  ScanHeartIcon: () => import('./ScanHeartIcon.js'),
  ScanImageIcon: () => import('./ScanImageIcon.js'),
  ScanIcon: () => import('./ScanIcon.js'),
  SchemeIcon: () => import('./SchemeIcon.js'),
  School01Icon: () => import('./School01Icon.js'),
  SchoolBell01Icon: () => import('./SchoolBell01Icon.js'),
  SchoolBell02Icon: () => import('./SchoolBell02Icon.js'),
  SchoolBusIcon: () => import('./SchoolBusIcon.js'),
  SchoolReportCardIcon: () => import('./SchoolReportCardIcon.js'),
  SchoolIcon: () => import('./SchoolIcon.js'),
  SchoolTieIcon: () => import('./SchoolTieIcon.js'),
  Scissor01Icon: () => import('./Scissor01Icon.js'),
  ScissorRectangleIcon: () => import('./ScissorRectangleIcon.js'),
  ScissorIcon: () => import('./ScissorIcon.js'),
  Scooter01Icon: () => import('./Scooter01Icon.js'),
  Scooter02Icon: () => import('./Scooter02Icon.js'),
  Scooter03Icon: () => import('./Scooter03Icon.js'),
  Scooter04Icon: () => import('./Scooter04Icon.js'),
  ScooterElectricIcon: () => import('./ScooterElectricIcon.js'),
  ScratchCardIcon: () => import('./ScratchCardIcon.js'),
  ScreenAddToHome02Icon: () => import('./ScreenAddToHome02Icon.js'),
  ScreenAddToHomeIcon: () => import('./ScreenAddToHomeIcon.js'),
  ScreenLockRotationIcon: () => import('./ScreenLockRotationIcon.js'),
  ScreenRotationIcon: () => import('./ScreenRotationIcon.js'),
  ScribdIcon: () => import('./ScribdIcon.js'),
  Scroll01Icon: () => import('./Scroll01Icon.js'),
  ScrollHorizontalIcon: () => import('./ScrollHorizontalIcon.js'),
  ScrollIcon: () => import('./ScrollIcon.js'),
  ScrollVerticalIcon: () => import('./ScrollVerticalIcon.js'),
  SdCardIcon: () => import('./SdCardIcon.js'),
  SealIcon: () => import('./SealIcon.js'),
  Search01Icon: () => import('./Search01Icon.js'),
  Search02Icon: () => import('./Search02Icon.js'),
  SearchAddIcon: () => import('./SearchAddIcon.js'),
  SearchAreaIcon: () => import('./SearchAreaIcon.js'),
  SearchCircleIcon: () => import('./SearchCircleIcon.js'),
  SearchCodeIcon: () => import('./SearchCodeIcon.js'),
  SearchDollarIcon: () => import('./SearchDollarIcon.js'),
  SearchFocusIcon: () => import('./SearchFocusIcon.js'),
  SearchList01Icon: () => import('./SearchList01Icon.js'),
  SearchList02Icon: () => import('./SearchList02Icon.js'),
  SearchMinusIcon: () => import('./SearchMinusIcon.js'),
  SearchRemoveIcon: () => import('./SearchRemoveIcon.js'),
  SearchReplaceIcon: () => import('./SearchReplaceIcon.js'),
  SearchSquareIcon: () => import('./SearchSquareIcon.js'),
  SearchVisualIcon: () => import('./SearchVisualIcon.js'),
  SearchingIcon: () => import('./SearchingIcon.js'),
  SeatSelectorIcon: () => import('./SeatSelectorIcon.js'),
  SectionIcon: () => import('./SectionIcon.js'),
  SecuredNetworkIcon: () => import('./SecuredNetworkIcon.js'),
  SecurityBlockIcon: () => import('./SecurityBlockIcon.js'),
  SecurityCheckIcon: () => import('./SecurityCheckIcon.js'),
  SecurityKeyUsbIcon: () => import('./SecurityKeyUsbIcon.js'),
  SecurityLockIcon: () => import('./SecurityLockIcon.js'),
  SecurityPasswordIcon: () => import('./SecurityPasswordIcon.js'),
  SecurityIcon: () => import('./SecurityIcon.js'),
  SecurityValidationIcon: () => import('./SecurityValidationIcon.js'),
  SecurityWarningIcon: () => import('./SecurityWarningIcon.js'),
  SecurityWifiIcon: () => import('./SecurityWifiIcon.js'),
  SeesawIcon: () => import('./SeesawIcon.js'),
  SegmentIcon: () => import('./SegmentIcon.js'),
  Select01Icon: () => import('./Select01Icon.js'),
  Select02Icon: () => import('./Select02Icon.js'),
  SelfTransferIcon: () => import('./SelfTransferIcon.js'),
  SemiTruckIcon: () => import('./SemiTruckIcon.js'),
  SendToBackIcon: () => import('./SendToBackIcon.js'),
  SendToMobile02Icon: () => import('./SendToMobile02Icon.js'),
  SendToMobileIcon: () => import('./SendToMobileIcon.js'),
  SendingOrderIcon: () => import('./SendingOrderIcon.js'),
  SenselessIcon: () => import('./SenselessIcon.js'),
  Sent02Icon: () => import('./Sent02Icon.js'),
  SentIcon: () => import('./SentIcon.js'),
  SeoIcon: () => import('./SeoIcon.js'),
  SeparatorHorizontalIcon: () => import('./SeparatorHorizontalIcon.js'),
  ServerStack01Icon: () => import('./ServerStack01Icon.js'),
  ServerStack02Icon: () => import('./ServerStack02Icon.js'),
  ServerStack03Icon: () => import('./ServerStack03Icon.js'),
  ServiceIcon: () => import('./ServiceIcon.js'),
  ServingFoodIcon: () => import('./ServingFoodIcon.js'),
  Setting06Icon: () => import('./Setting06Icon.js'),
  Setting07Icon: () => import('./Setting07Icon.js'),
  SettingDone01Icon: () => import('./SettingDone01Icon.js'),
  SettingDone02Icon: () => import('./SettingDone02Icon.js'),
  SettingDone03Icon: () => import('./SettingDone03Icon.js'),
  SettingDone04Icon: () => import('./SettingDone04Icon.js'),
  SettingError03Icon: () => import('./SettingError03Icon.js'),
  SettingError04Icon: () => import('./SettingError04Icon.js'),
  Settings01Icon: () => import('./Settings01Icon.js'),
  Settings02Icon: () => import('./Settings02Icon.js'),
  Settings03Icon: () => import('./Settings03Icon.js'),
  Settings04Icon: () => import('./Settings04Icon.js'),
  Settings05Icon: () => import('./Settings05Icon.js'),
  SettingsError01Icon: () => import('./SettingsError01Icon.js'),
  SettingsError02Icon: () => import('./SettingsError02Icon.js'),
  Setup01Icon: () => import('./Setup01Icon.js'),
  Setup02Icon: () => import('./Setup02Icon.js'),
  SevenCircleIcon: () => import('./SevenCircleIcon.js'),
  SevenSquareIcon: () => import('./SevenSquareIcon.js'),
  ShadcnSquareIcon: () => import('./ShadcnSquareIcon.js'),
  ShadcnIcon: () => import('./ShadcnIcon.js'),
  Shaka01Icon: () => import('./Shaka01Icon.js'),
  Shaka02Icon: () => import('./Shaka02Icon.js'),
  Shaka03Icon: () => import('./Shaka03Icon.js'),
  Shaka04Icon: () => import('./Shaka04Icon.js'),
  ShampooIcon: () => import('./ShampooIcon.js'),
  ShapeCollectionIcon: () => import('./ShapeCollectionIcon.js'),
  Shapes01Icon: () => import('./Shapes01Icon.js'),
  ShapesIcon: () => import('./ShapesIcon.js'),
  Share01Icon: () => import('./Share01Icon.js'),
  Share02Icon: () => import('./Share02Icon.js'),
  Share03Icon: () => import('./Share03Icon.js'),
  Share04Icon: () => import('./Share04Icon.js'),
  Share05Icon: () => import('./Share05Icon.js'),
  Share06Icon: () => import('./Share06Icon.js'),
  Share07Icon: () => import('./Share07Icon.js'),
  Share08Icon: () => import('./Share08Icon.js'),
  ShareKnowledgeIcon: () => import('./ShareKnowledgeIcon.js'),
  ShareLocation01Icon: () => import('./ShareLocation01Icon.js'),
  ShareLocation02Icon: () => import('./ShareLocation02Icon.js'),
  SharedDriveIcon: () => import('./SharedDriveIcon.js'),
  SharedWifiIcon: () => import('./SharedWifiIcon.js'),
  SharingIcon: () => import('./SharingIcon.js'),
  ShellIcon: () => import('./ShellIcon.js'),
  ShellfishIcon: () => import('./ShellfishIcon.js'),
  Sheriff01Icon: () => import('./Sheriff01Icon.js'),
  Sheriff02Icon: () => import('./Sheriff02Icon.js'),
  Shield01Icon: () => import('./Shield01Icon.js'),
  Shield02Icon: () => import('./Shield02Icon.js'),
  ShieldBanIcon: () => import('./ShieldBanIcon.js'),
  ShieldBlockchainIcon: () => import('./ShieldBlockchainIcon.js'),
  ShieldEnergyIcon: () => import('./ShieldEnergyIcon.js'),
  ShieldHalfIcon: () => import('./ShieldHalfIcon.js'),
  ShieldKeyIcon: () => import('./ShieldKeyIcon.js'),
  ShieldMinusIcon: () => import('./ShieldMinusIcon.js'),
  ShieldPlusIcon: () => import('./ShieldPlusIcon.js'),
  ShieldQuestionMarkIcon: () => import('./ShieldQuestionMarkIcon.js'),
  ShieldUserIcon: () => import('./ShieldUserIcon.js'),
  ShipmentTrackingIcon: () => import('./ShipmentTrackingIcon.js'),
  ShippingCenterIcon: () => import('./ShippingCenterIcon.js'),
  ShippingLoadingIcon: () => import('./ShippingLoadingIcon.js'),
  ShippingTruck01Icon: () => import('./ShippingTruck01Icon.js'),
  ShippingTruck02Icon: () => import('./ShippingTruck02Icon.js'),
  Shirt01Icon: () => import('./Shirt01Icon.js'),
  ShockedIcon: () => import('./ShockedIcon.js'),
  ShopSignIcon: () => import('./ShopSignIcon.js'),
  ShopifyIcon: () => import('./ShopifyIcon.js'),
  ShoppingBag01Icon: () => import('./ShoppingBag01Icon.js'),
  ShoppingBag02Icon: () => import('./ShoppingBag02Icon.js'),
  ShoppingBag03Icon: () => import('./ShoppingBag03Icon.js'),
  ShoppingBagAddIcon: () => import('./ShoppingBagAddIcon.js'),
  ShoppingBagCheckIcon: () => import('./ShoppingBagCheckIcon.js'),
  ShoppingBagFavoriteIcon: () => import('./ShoppingBagFavoriteIcon.js'),
  ShoppingBagRemoveIcon: () => import('./ShoppingBagRemoveIcon.js'),
  ShoppingBasket01Icon: () => import('./ShoppingBasket01Icon.js'),
  ShoppingBasket02Icon: () => import('./ShoppingBasket02Icon.js'),
  ShoppingBasket03Icon: () => import('./ShoppingBasket03Icon.js'),
  ShoppingBasketAdd01Icon: () => import('./ShoppingBasketAdd01Icon.js'),
  ShoppingBasketAdd02Icon: () => import('./ShoppingBasketAdd02Icon.js'),
  ShoppingBasketAdd03Icon: () => import('./ShoppingBasketAdd03Icon.js'),
  ShoppingBasketCheckIn01Icon: () => import('./ShoppingBasketCheckIn01Icon.js'),
  ShoppingBasketCheckIn02Icon: () => import('./ShoppingBasketCheckIn02Icon.js'),
  ShoppingBasketCheckIn03Icon: () => import('./ShoppingBasketCheckIn03Icon.js'),
  ShoppingBasketCheckOut01Icon: () => import('./ShoppingBasketCheckOut01Icon.js'),
  ShoppingBasketCheckOut02Icon: () => import('./ShoppingBasketCheckOut02Icon.js'),
  ShoppingBasketCheckOut03Icon: () => import('./ShoppingBasketCheckOut03Icon.js'),
  ShoppingBasketDone01Icon: () => import('./ShoppingBasketDone01Icon.js'),
  ShoppingBasketDone02Icon: () => import('./ShoppingBasketDone02Icon.js'),
  ShoppingBasketDone03Icon: () => import('./ShoppingBasketDone03Icon.js'),
  ShoppingBasketFavorite01Icon: () => import('./ShoppingBasketFavorite01Icon.js'),
  ShoppingBasketFavorite02Icon: () => import('./ShoppingBasketFavorite02Icon.js'),
  ShoppingBasketFavorite03Icon: () => import('./ShoppingBasketFavorite03Icon.js'),
  ShoppingBasketRemove01Icon: () => import('./ShoppingBasketRemove01Icon.js'),
  ShoppingBasketRemove02Icon: () => import('./ShoppingBasketRemove02Icon.js'),
  ShoppingBasketRemove03Icon: () => import('./ShoppingBasketRemove03Icon.js'),
  ShoppingBasketSecure01Icon: () => import('./ShoppingBasketSecure01Icon.js'),
  ShoppingBasketSecure02Icon: () => import('./ShoppingBasketSecure02Icon.js'),
  ShoppingBasketSecure03Icon: () => import('./ShoppingBasketSecure03Icon.js'),
  ShoppingCart01Icon: () => import('./ShoppingCart01Icon.js'),
  ShoppingCart02Icon: () => import('./ShoppingCart02Icon.js'),
  ShoppingCartAdd01Icon: () => import('./ShoppingCartAdd01Icon.js'),
  ShoppingCartAdd02Icon: () => import('./ShoppingCartAdd02Icon.js'),
  ShoppingCartCheck01Icon: () => import('./ShoppingCartCheck01Icon.js'),
  ShoppingCartCheck02Icon: () => import('./ShoppingCartCheck02Icon.js'),
  ShoppingCartCheckIn01Icon: () => import('./ShoppingCartCheckIn01Icon.js'),
  ShoppingCartCheckIn02Icon: () => import('./ShoppingCartCheckIn02Icon.js'),
  ShoppingCartCheckOut01Icon: () => import('./ShoppingCartCheckOut01Icon.js'),
  ShoppingCartCheckOut02Icon: () => import('./ShoppingCartCheckOut02Icon.js'),
  ShoppingCartFavorite01Icon: () => import('./ShoppingCartFavorite01Icon.js'),
  ShoppingCartFavorite02Icon: () => import('./ShoppingCartFavorite02Icon.js'),
  ShoppingCartRemove01Icon: () => import('./ShoppingCartRemove01Icon.js'),
  ShoppingCartRemove02Icon: () => import('./ShoppingCartRemove02Icon.js'),
  ShortsPantsIcon: () => import('./ShortsPantsIcon.js'),
  ShoulderIcon: () => import('./ShoulderIcon.js'),
  ShovelIcon: () => import('./ShovelIcon.js'),
  ShowerHeadIcon: () => import('./ShowerHeadIcon.js'),
  ShrinkDotIcon: () => import('./ShrinkDotIcon.js'),
  ShuffleSquareIcon: () => import('./ShuffleSquareIcon.js'),
  ShuffleIcon: () => import('./ShuffleIcon.js'),
  ShutDownIcon: () => import('./ShutDownIcon.js'),
  ShutterstockIcon: () => import('./ShutterstockIcon.js'),
  SidebarBottomIcon: () => import('./SidebarBottomIcon.js'),
  SidebarLeft01Icon: () => import('./SidebarLeft01Icon.js'),
  SidebarLeftIcon: () => import('./SidebarLeftIcon.js'),
  SidebarRight01Icon: () => import('./SidebarRight01Icon.js'),
  SidebarRightIcon: () => import('./SidebarRightIcon.js'),
  SidebarTopIcon: () => import('./SidebarTopIcon.js'),
  SignLanguageCIcon: () => import('./SignLanguageCIcon.js'),
  SignalFull01Icon: () => import('./SignalFull01Icon.js'),
  SignalFull02Icon: () => import('./SignalFull02Icon.js'),
  SignalLow01Icon: () => import('./SignalLow01Icon.js'),
  SignalLow02Icon: () => import('./SignalLow02Icon.js'),
  SignalLowMediumIcon: () => import('./SignalLowMediumIcon.js'),
  SignalMedium01Icon: () => import('./SignalMedium01Icon.js'),
  SignalMedium02Icon: () => import('./SignalMedium02Icon.js'),
  SignalNo01Icon: () => import('./SignalNo01Icon.js'),
  SignalNo02Icon: () => import('./SignalNo02Icon.js'),
  SignalIcon: () => import('./SignalIcon.js'),
  SignatureIcon: () => import('./SignatureIcon.js'),
  SilenceIcon: () => import('./SilenceIcon.js'),
  Simcard01Icon: () => import('./Simcard01Icon.js'),
  Simcard02Icon: () => import('./Simcard02Icon.js'),
  SimcardDualIcon: () => import('./SimcardDualIcon.js'),
  SinIcon: () => import('./SinIcon.js'),
  Sine01Icon: () => import('./Sine01Icon.js'),
  Sine02Icon: () => import('./Sine02Icon.js'),
  SingLeftIcon: () => import('./SingLeftIcon.js'),
  SingRightIcon: () => import('./SingRightIcon.js'),
  Sink01Icon: () => import('./Sink01Icon.js'),
  Sink02Icon: () => import('./Sink02Icon.js'),
  SiriNewIcon: () => import('./SiriNewIcon.js'),
  SiriIcon: () => import('./SiriIcon.js'),
  SixCircleIcon: () => import('./SixCircleIcon.js'),
  SixSquareIcon: () => import('./SixSquareIcon.js'),
  SketchIcon: () => import('./SketchIcon.js'),
  SkewIcon: () => import('./SkewIcon.js'),
  SkiIcon: () => import('./SkiIcon.js'),
  SkippingRopeIcon: () => import('./SkippingRopeIcon.js'),
  SkoolIcon: () => import('./SkoolIcon.js'),
  SkullIcon: () => import('./SkullIcon.js'),
  SkypeIcon: () => import('./SkypeIcon.js'),
  SlackIcon: () => import('./SlackIcon.js'),
  SleepingIcon: () => import('./SleepingIcon.js'),
  SleevelessIcon: () => import('./SleevelessIcon.js'),
  SlideIcon: () => import('./SlideIcon.js'),
  SlidersHorizontalIcon: () => import('./SlidersHorizontalIcon.js'),
  SlidersVerticalIcon: () => import('./SlidersVerticalIcon.js'),
  SlideshareIcon: () => import('./SlideshareIcon.js'),
  SlowWindsIcon: () => import('./SlowWindsIcon.js'),
  SmartAcIcon: () => import('./SmartAcIcon.js'),
  SmartPhone01Icon: () => import('./SmartPhone01Icon.js'),
  SmartPhone02Icon: () => import('./SmartPhone02Icon.js'),
  SmartPhone03Icon: () => import('./SmartPhone03Icon.js'),
  SmartPhone04Icon: () => import('./SmartPhone04Icon.js'),
  SmartPhoneLandscape02Icon: () => import('./SmartPhoneLandscape02Icon.js'),
  SmartPhoneLandscapeIcon: () => import('./SmartPhoneLandscapeIcon.js'),
  SmartIcon: () => import('./SmartIcon.js'),
  SmartWatch01Icon: () => import('./SmartWatch01Icon.js'),
  SmartWatch02Icon: () => import('./SmartWatch02Icon.js'),
  SmartWatch03Icon: () => import('./SmartWatch03Icon.js'),
  SmartWatch04Icon: () => import('./SmartWatch04Icon.js'),
  SmartphoneChargingIcon: () => import('./SmartphoneChargingIcon.js'),
  SmartphoneLostWifiIcon: () => import('./SmartphoneLostWifiIcon.js'),
  SmartphoneNfcIcon: () => import('./SmartphoneNfcIcon.js'),
  SmartphoneWifiIcon: () => import('./SmartphoneWifiIcon.js'),
  SmileDizzyIcon: () => import('./SmileDizzyIcon.js'),
  SmilePlusIcon: () => import('./SmilePlusIcon.js'),
  SmileIcon: () => import('./SmileIcon.js'),
  SmsCodeIcon: () => import('./SmsCodeIcon.js'),
  SnailIcon: () => import('./SnailIcon.js'),
  SnapchatIcon: () => import('./SnapchatIcon.js'),
  SnowIcon: () => import('./SnowIcon.js'),
  SocksIcon: () => import('./SocksIcon.js'),
  SodaCanIcon: () => import('./SodaCanIcon.js'),
  Sofa01Icon: () => import('./Sofa01Icon.js'),
  Sofa02Icon: () => import('./Sofa02Icon.js'),
  Sofa03Icon: () => import('./Sofa03Icon.js'),
  SofaSingleIcon: () => import('./SofaSingleIcon.js'),
  SoftDrink01Icon: () => import('./SoftDrink01Icon.js'),
  SoftDrink02Icon: () => import('./SoftDrink02Icon.js'),
  SoftwareLicenseIcon: () => import('./SoftwareLicenseIcon.js'),
  SoftwareIcon: () => import('./SoftwareIcon.js'),
  SoftwareUninstallIcon: () => import('./SoftwareUninstallIcon.js'),
  SoilMoistureFieldIcon: () => import('./SoilMoistureFieldIcon.js'),
  SoilMoistureGlobalIcon: () => import('./SoilMoistureGlobalIcon.js'),
  SoilTemperatureFieldIcon: () => import('./SoilTemperatureFieldIcon.js'),
  SoilTemperatureGlobalIcon: () => import('./SoilTemperatureGlobalIcon.js'),
  SolarEnergyIcon: () => import('./SolarEnergyIcon.js'),
  SolarPanel01Icon: () => import('./SolarPanel01Icon.js'),
  SolarPanel02Icon: () => import('./SolarPanel02Icon.js'),
  SolarPanel03Icon: () => import('./SolarPanel03Icon.js'),
  SolarPanel04Icon: () => import('./SolarPanel04Icon.js'),
  SolarPanel05Icon: () => import('./SolarPanel05Icon.js'),
  SolarPowerIcon: () => import('./SolarPowerIcon.js'),
  SolarSystem01Icon: () => import('./SolarSystem01Icon.js'),
  SolarSystemIcon: () => import('./SolarSystemIcon.js'),
  SolidLine01Icon: () => import('./SolidLine01Icon.js'),
  SolidLine02Icon: () => import('./SolidLine02Icon.js'),
  SortByDown01Icon: () => import('./SortByDown01Icon.js'),
  SortByDown02Icon: () => import('./SortByDown02Icon.js'),
  SortByUp01Icon: () => import('./SortByUp01Icon.js'),
  SortByUp02Icon: () => import('./SortByUp02Icon.js'),
  SortDescendingIcon: () => import('./SortDescendingIcon.js'),
  Sorting01Icon: () => import('./Sorting01Icon.js'),
  Sorting02Icon: () => import('./Sorting02Icon.js'),
  Sorting03Icon: () => import('./Sorting03Icon.js'),
  Sorting04Icon: () => import('./Sorting04Icon.js'),
  Sorting05Icon: () => import('./Sorting05Icon.js'),
  SortingOneNineIcon: () => import('./SortingOneNineIcon.js'),
  SortingNineOneIcon: () => import('./SortingNineOneIcon.js'),
  SortingAZ01Icon: () => import('./SortingAZ01Icon.js'),
  SortingAZ02Icon: () => import('./SortingAZ02Icon.js'),
  SortingDownIcon: () => import('./SortingDownIcon.js'),
  SortingUpIcon: () => import('./SortingUpIcon.js'),
  SortingZA01Icon: () => import('./SortingZA01Icon.js'),
  SoundcloudIcon: () => import('./SoundcloudIcon.js'),
  SourceCodeCircleIcon: () => import('./SourceCodeCircleIcon.js'),
  SourceCodeSquareIcon: () => import('./SourceCodeSquareIcon.js'),
  SourceCodeIcon: () => import('./SourceCodeIcon.js'),
  SpaceshipIcon: () => import('./SpaceshipIcon.js'),
  SpadesIcon: () => import('./SpadesIcon.js'),
  SpaghettiIcon: () => import('./SpaghettiIcon.js'),
  SpamIcon: () => import('./SpamIcon.js'),
  SparklesIcon: () => import('./SparklesIcon.js'),
  SpartanHelmetIcon: () => import('./SpartanHelmetIcon.js'),
  SpatulaIcon: () => import('./SpatulaIcon.js'),
  Speaker01Icon: () => import('./Speaker01Icon.js'),
  SpeakerIcon: () => import('./SpeakerIcon.js'),
  SpeechIcon: () => import('./SpeechIcon.js'),
  SpeechToTextIcon: () => import('./SpeechToTextIcon.js'),
  SpeedTrain01Icon: () => import('./SpeedTrain01Icon.js'),
  SpeedTrain02Icon: () => import('./SpeedTrain02Icon.js'),
  SpermIcon: () => import('./SpermIcon.js'),
  SphereIcon: () => import('./SphereIcon.js'),
  SpiralsIcon: () => import('./SpiralsIcon.js'),
  SplinePointerIcon: () => import('./SplinePointerIcon.js'),
  SplitIcon: () => import('./SplitIcon.js'),
  SpoolIcon: () => import('./SpoolIcon.js'),
  SpoonAndForkIcon: () => import('./SpoonAndForkIcon.js'),
  SpoonAndKnifeIcon: () => import('./SpoonAndKnifeIcon.js'),
  SpoonIcon: () => import('./SpoonIcon.js'),
  SpotifyIcon: () => import('./SpotifyIcon.js'),
  SpotlightIcon: () => import('./SpotlightIcon.js'),
  SprayCanIcon: () => import('./SprayCanIcon.js'),
  SqlIcon: () => import('./SqlIcon.js'),
  Square01Icon: () => import('./Square01Icon.js'),
  SquareArrowDataTransferDiagonalIcon: () => import('./SquareArrowDataTransferDiagonalIcon.js'),
  SquareArrowDataTransferHorizontalIcon: () => import('./SquareArrowDataTransferHorizontalIcon.js'),
  SquareArrowDataTransferVerticalIcon: () => import('./SquareArrowDataTransferVerticalIcon.js'),
  SquareArrowDiagonal01Icon: () => import('./SquareArrowDiagonal01Icon.js'),
  SquareArrowDiagonal02Icon: () => import('./SquareArrowDiagonal02Icon.js'),
  SquareArrowDown01Icon: () => import('./SquareArrowDown01Icon.js'),
  SquareArrowDown02Icon: () => import('./SquareArrowDown02Icon.js'),
  SquareArrowDown03Icon: () => import('./SquareArrowDown03Icon.js'),
  SquareArrowDownDoubleIcon: () => import('./SquareArrowDownDoubleIcon.js'),
  SquareArrowDownLeftIcon: () => import('./SquareArrowDownLeftIcon.js'),
  SquareArrowDownRightIcon: () => import('./SquareArrowDownRightIcon.js'),
  SquareArrowExpand01Icon: () => import('./SquareArrowExpand01Icon.js'),
  SquareArrowExpand02Icon: () => import('./SquareArrowExpand02Icon.js'),
  SquareArrowHorizontalIcon: () => import('./SquareArrowHorizontalIcon.js'),
  SquareArrowLeft01Icon: () => import('./SquareArrowLeft01Icon.js'),
  SquareArrowLeft02Icon: () => import('./SquareArrowLeft02Icon.js'),
  SquareArrowLeft03Icon: () => import('./SquareArrowLeft03Icon.js'),
  SquareArrowLeftDoubleIcon: () => import('./SquareArrowLeftDoubleIcon.js'),
  SquareArrowLeftRightIcon: () => import('./SquareArrowLeftRightIcon.js'),
  SquareArrowMoveDownLeftIcon: () => import('./SquareArrowMoveDownLeftIcon.js'),
  SquareArrowMoveDownRightIcon: () => import('./SquareArrowMoveDownRightIcon.js'),
  SquareArrowMoveLeftDownIcon: () => import('./SquareArrowMoveLeftDownIcon.js'),
  SquareArrowMoveLeftUpIcon: () => import('./SquareArrowMoveLeftUpIcon.js'),
  SquareArrowMoveRightDownIcon: () => import('./SquareArrowMoveRightDownIcon.js'),
  SquareArrowMoveRightUpIcon: () => import('./SquareArrowMoveRightUpIcon.js'),
  SquareArrowReload01Icon: () => import('./SquareArrowReload01Icon.js'),
  SquareArrowReload02Icon: () => import('./SquareArrowReload02Icon.js'),
  SquareArrowRight01Icon: () => import('./SquareArrowRight01Icon.js'),
  SquareArrowRight02Icon: () => import('./SquareArrowRight02Icon.js'),
  SquareArrowRight03Icon: () => import('./SquareArrowRight03Icon.js'),
  SquareArrowRightDoubleIcon: () => import('./SquareArrowRightDoubleIcon.js'),
  SquareArrowShrink01Icon: () => import('./SquareArrowShrink01Icon.js'),
  SquareArrowShrink02Icon: () => import('./SquareArrowShrink02Icon.js'),
  SquareArrowUp01Icon: () => import('./SquareArrowUp01Icon.js'),
  SquareArrowUp02Icon: () => import('./SquareArrowUp02Icon.js'),
  SquareArrowUp03Icon: () => import('./SquareArrowUp03Icon.js'),
  SquareArrowUpDoubleIcon: () => import('./SquareArrowUpDoubleIcon.js'),
  SquareArrowUpDownIcon: () => import('./SquareArrowUpDownIcon.js'),
  SquareArrowUpLeftIcon: () => import('./SquareArrowUpLeftIcon.js'),
  SquareArrowUpRight02Icon: () => import('./SquareArrowUpRight02Icon.js'),
  SquareArrowUpRightIcon: () => import('./SquareArrowUpRightIcon.js'),
  SquareArrowVerticalIcon: () => import('./SquareArrowVerticalIcon.js'),
  SquareAsteriskIcon: () => import('./SquareAsteriskIcon.js'),
  SquareBottomDashedScissorsIcon: () => import('./SquareBottomDashedScissorsIcon.js'),
  SquareChartGanttIcon: () => import('./SquareChartGanttIcon.js'),
  SquareCircleIcon: () => import('./SquareCircleIcon.js'),
  SquareDashedKanbanIcon: () => import('./SquareDashedKanbanIcon.js'),
  SquareDashedTopSolidIcon: () => import('./SquareDashedTopSolidIcon.js'),
  SquareFIcon: () => import('./SquareFIcon.js'),
  SquareLock01Icon: () => import('./SquareLock01Icon.js'),
  SquareLock02Icon: () => import('./SquareLock02Icon.js'),
  SquareLockAdd01Icon: () => import('./SquareLockAdd01Icon.js'),
  SquareLockAdd02Icon: () => import('./SquareLockAdd02Icon.js'),
  SquareLockCheck01Icon: () => import('./SquareLockCheck01Icon.js'),
  SquareLockCheck02Icon: () => import('./SquareLockCheck02Icon.js'),
  SquareLockMinus01Icon: () => import('./SquareLockMinus01Icon.js'),
  SquareLockMinus02Icon: () => import('./SquareLockMinus02Icon.js'),
  SquareLockPasswordIcon: () => import('./SquareLockPasswordIcon.js'),
  SquareLockRemove01Icon: () => import('./SquareLockRemove01Icon.js'),
  SquareLockRemove02Icon: () => import('./SquareLockRemove02Icon.js'),
  SquareMIcon: () => import('./SquareMIcon.js'),
  SquareMousePointerIcon: () => import('./SquareMousePointerIcon.js'),
  SquareParkingOffIcon: () => import('./SquareParkingOffIcon.js'),
  SquarePowerIcon: () => import('./SquarePowerIcon.js'),
  SquareRootSquareIcon: () => import('./SquareRootSquareIcon.js'),
  SquareRoundCornerIcon: () => import('./SquareRoundCornerIcon.js'),
  SquareSquareIcon: () => import('./SquareSquareIcon.js'),
  SquareStackIcon: () => import('./SquareStackIcon.js'),
  SquareIcon: () => import('./SquareIcon.js'),
  SquareUnlock01Icon: () => import('./SquareUnlock01Icon.js'),
  SquareUnlock02Icon: () => import('./SquareUnlock02Icon.js'),
  SquaresExcludeIcon: () => import('./SquaresExcludeIcon.js'),
  StackStarIcon: () => import('./StackStarIcon.js'),
  Stairs01Icon: () => import('./Stairs01Icon.js'),
  Stairs02Icon: () => import('./Stairs02Icon.js'),
  Stairs03Icon: () => import('./Stairs03Icon.js'),
  Stairs04Icon: () => import('./Stairs04Icon.js'),
  StakeIcon: () => import('./StakeIcon.js'),
  Stamp01Icon: () => import('./Stamp01Icon.js'),
  Stamp02Icon: () => import('./Stamp02Icon.js'),
  StampIcon: () => import('./StampIcon.js'),
  StarAward01Icon: () => import('./StarAward01Icon.js'),
  StarAward02Icon: () => import('./StarAward02Icon.js'),
  StarCircleIcon: () => import('./StarCircleIcon.js'),
  StarFaceIcon: () => import('./StarFaceIcon.js'),
  StarHalfIcon: () => import('./StarHalfIcon.js'),
  StarOffIcon: () => import('./StarOffIcon.js'),
  StarSquareIcon: () => import('./StarSquareIcon.js'),
  StarIcon: () => import('./StarIcon.js'),
  StarsIcon: () => import('./StarsIcon.js'),
  StartUp01Icon: () => import('./StartUp01Icon.js'),
  StartUp02Icon: () => import('./StartUp02Icon.js'),
  StationeryIcon: () => import('./StationeryIcon.js'),
  StatusIcon: () => import('./StatusIcon.js'),
  SteakIcon: () => import('./SteakIcon.js'),
  SteeringIcon: () => import('./SteeringIcon.js'),
  StepIntoIcon: () => import('./StepIntoIcon.js'),
  StepOutIcon: () => import('./StepOutIcon.js'),
  StepOverIcon: () => import('./StepOverIcon.js'),
  Stethoscope02Icon: () => import('./Stethoscope02Icon.js'),
  StethoscopeIcon: () => import('./StethoscopeIcon.js'),
  StickerIcon: () => import('./StickerIcon.js'),
  StickyNote01Icon: () => import('./StickyNote01Icon.js'),
  StickyNote02Icon: () => import('./StickyNote02Icon.js'),
  StickyNote03Icon: () => import('./StickyNote03Icon.js'),
  StopCircleIcon: () => import('./StopCircleIcon.js'),
  StopLossOrderIcon: () => import('./StopLossOrderIcon.js'),
  StopIcon: () => import('./StopIcon.js'),
  StopWatchIcon: () => import('./StopWatchIcon.js'),
  Store01Icon: () => import('./Store01Icon.js'),
  Store02Icon: () => import('./Store02Icon.js'),
  Store03Icon: () => import('./Store03Icon.js'),
  Store04Icon: () => import('./Store04Icon.js'),
  StoreAdd01Icon: () => import('./StoreAdd01Icon.js'),
  StoreAdd02Icon: () => import('./StoreAdd02Icon.js'),
  StoreLocation01Icon: () => import('./StoreLocation01Icon.js'),
  StoreLocation02Icon: () => import('./StoreLocation02Icon.js'),
  StoreManagement01Icon: () => import('./StoreManagement01Icon.js'),
  StoreManagement02Icon: () => import('./StoreManagement02Icon.js'),
  StoreRemove01Icon: () => import('./StoreRemove01Icon.js'),
  StoreRemove02Icon: () => import('./StoreRemove02Icon.js'),
  StoreVerified01Icon: () => import('./StoreVerified01Icon.js'),
  StoreVerified02Icon: () => import('./StoreVerified02Icon.js'),
  StraightEdgeIcon: () => import('./StraightEdgeIcon.js'),
  StrategyIcon: () => import('./StrategyIcon.js'),
  StreeringWheelIcon: () => import('./StreeringWheelIcon.js'),
  StreetFoodIcon: () => import('./StreetFoodIcon.js'),
  StripeIcon: () => import('./StripeIcon.js'),
  StrokeBottomIcon: () => import('./StrokeBottomIcon.js'),
  StrokeCenterIcon: () => import('./StrokeCenterIcon.js'),
  StrokeInsideIcon: () => import('./StrokeInsideIcon.js'),
  StrokeLeftIcon: () => import('./StrokeLeftIcon.js'),
  StrokeOutsideIcon: () => import('./StrokeOutsideIcon.js'),
  StrokeRightIcon: () => import('./StrokeRightIcon.js'),
  StrokeTopIcon: () => import('./StrokeTopIcon.js'),
  Structure01Icon: () => import('./Structure01Icon.js'),
  Structure02Icon: () => import('./Structure02Icon.js'),
  Structure03Icon: () => import('./Structure03Icon.js'),
  Structure04Icon: () => import('./Structure04Icon.js'),
  Structure05Icon: () => import('./Structure05Icon.js'),
  StructureAddIcon: () => import('./StructureAddIcon.js'),
  StructureCheckIcon: () => import('./StructureCheckIcon.js'),
  StructureFailIcon: () => import('./StructureFailIcon.js'),
  StructureFolderCircleIcon: () => import('./StructureFolderCircleIcon.js'),
  StructureFolderIcon: () => import('./StructureFolderIcon.js'),
  StudentCardIcon: () => import('./StudentCardIcon.js'),
  StudentIcon: () => import('./StudentIcon.js'),
  StudentsIcon: () => import('./StudentsIcon.js'),
  StudyDeskIcon: () => import('./StudyDeskIcon.js'),
  StudyLampIcon: () => import('./StudyLampIcon.js'),
  StumbleuponIcon: () => import('./StumbleuponIcon.js'),
  SubmarineIcon: () => import('./SubmarineIcon.js'),
  SubmergeIcon: () => import('./SubmergeIcon.js'),
  SubnodeAddIcon: () => import('./SubnodeAddIcon.js'),
  SubnodeDeleteIcon: () => import('./SubnodeDeleteIcon.js'),
  SubpoenaIcon: () => import('./SubpoenaIcon.js'),
  SubtitleIcon: () => import('./SubtitleIcon.js'),
  Suit01Icon: () => import('./Suit01Icon.js'),
  Suit02Icon: () => import('./Suit02Icon.js'),
  SujoodIcon: () => import('./SujoodIcon.js'),
  Summation01Icon: () => import('./Summation01Icon.js'),
  Summation02Icon: () => import('./Summation02Icon.js'),
  SummationCircleIcon: () => import('./SummationCircleIcon.js'),
  SummationSquareIcon: () => import('./SummationSquareIcon.js'),
  Sun01Icon: () => import('./Sun01Icon.js'),
  Sun02Icon: () => import('./Sun02Icon.js'),
  Sun03Icon: () => import('./Sun03Icon.js'),
  SunCloud01Icon: () => import('./SunCloud01Icon.js'),
  SunCloud02Icon: () => import('./SunCloud02Icon.js'),
  SunCloudAngledRain01Icon: () => import('./SunCloudAngledRain01Icon.js'),
  SunCloudAngledRain02Icon: () => import('./SunCloudAngledRain02Icon.js'),
  SunCloudAngledRainZap01Icon: () => import('./SunCloudAngledRainZap01Icon.js'),
  SunCloudAngledRainZap02Icon: () => import('./SunCloudAngledRainZap02Icon.js'),
  SunCloudAngledZap01Icon: () => import('./SunCloudAngledZap01Icon.js'),
  SunCloudAngledZap02Icon: () => import('./SunCloudAngledZap02Icon.js'),
  SunCloudBigRain01Icon: () => import('./SunCloudBigRain01Icon.js'),
  SunCloudBigRain02Icon: () => import('./SunCloudBigRain02Icon.js'),
  SunCloudFastWind01Icon: () => import('./SunCloudFastWind01Icon.js'),
  SunCloudFastWind02Icon: () => import('./SunCloudFastWind02Icon.js'),
  SunCloudHailstone01Icon: () => import('./SunCloudHailstone01Icon.js'),
  SunCloudHailstone02Icon: () => import('./SunCloudHailstone02Icon.js'),
  SunCloudLittleRain01Icon: () => import('./SunCloudLittleRain01Icon.js'),
  SunCloudLittleRain02Icon: () => import('./SunCloudLittleRain02Icon.js'),
  SunCloudLittleSnow01Icon: () => import('./SunCloudLittleSnow01Icon.js'),
  SunCloudLittleSnow02Icon: () => import('./SunCloudLittleSnow02Icon.js'),
  SunCloudMidRain01Icon: () => import('./SunCloudMidRain01Icon.js'),
  SunCloudMidRain02Icon: () => import('./SunCloudMidRain02Icon.js'),
  SunCloudMidSnow01Icon: () => import('./SunCloudMidSnow01Icon.js'),
  SunCloudMidSnow02Icon: () => import('./SunCloudMidSnow02Icon.js'),
  SunCloudSlowWind01Icon: () => import('./SunCloudSlowWind01Icon.js'),
  SunCloudSlowWind02Icon: () => import('./SunCloudSlowWind02Icon.js'),
  SunCloudSnow01Icon: () => import('./SunCloudSnow01Icon.js'),
  SunCloudSnow02Icon: () => import('./SunCloudSnow02Icon.js'),
  SunglassesIcon: () => import('./SunglassesIcon.js'),
  SunriseIcon: () => import('./SunriseIcon.js'),
  SunsetIcon: () => import('./SunsetIcon.js'),
  SuperMarioIcon: () => import('./SuperMarioIcon.js'),
  SuperMarioToadIcon: () => import('./SuperMarioToadIcon.js'),
  SurfboardIcon: () => import('./SurfboardIcon.js'),
  SurpriseIcon: () => import('./SurpriseIcon.js'),
  Sushi01Icon: () => import('./Sushi01Icon.js'),
  Sushi02Icon: () => import('./Sushi02Icon.js'),
  Sushi03Icon: () => import('./Sushi03Icon.js'),
  SuspiciousIcon: () => import('./SuspiciousIcon.js'),
  SustainableEnergyIcon: () => import('./SustainableEnergyIcon.js'),
  Svg01Icon: () => import('./Svg01Icon.js'),
  Svg02Icon: () => import('./Svg02Icon.js'),
  SwarmIcon: () => import('./SwarmIcon.js'),
  SwatchIcon: () => import('./SwatchIcon.js'),
  SwimmingCapIcon: () => import('./SwimmingCapIcon.js'),
  SwimmingIcon: () => import('./SwimmingIcon.js'),
  SwipeDown01Icon: () => import('./SwipeDown01Icon.js'),
  SwipeDown02Icon: () => import('./SwipeDown02Icon.js'),
  SwipeDown03Icon: () => import('./SwipeDown03Icon.js'),
  SwipeDown04Icon: () => import('./SwipeDown04Icon.js'),
  SwipeDown05Icon: () => import('./SwipeDown05Icon.js'),
  SwipeDown06Icon: () => import('./SwipeDown06Icon.js'),
  SwipeDown07Icon: () => import('./SwipeDown07Icon.js'),
  SwipeDown08Icon: () => import('./SwipeDown08Icon.js'),
  SwipeLeft01Icon: () => import('./SwipeLeft01Icon.js'),
  SwipeLeft02Icon: () => import('./SwipeLeft02Icon.js'),
  SwipeLeft03Icon: () => import('./SwipeLeft03Icon.js'),
  SwipeLeft04Icon: () => import('./SwipeLeft04Icon.js'),
  SwipeLeft05Icon: () => import('./SwipeLeft05Icon.js'),
  SwipeLeft06Icon: () => import('./SwipeLeft06Icon.js'),
  SwipeLeft07Icon: () => import('./SwipeLeft07Icon.js'),
  SwipeLeft08Icon: () => import('./SwipeLeft08Icon.js'),
  SwipeLeft09Icon: () => import('./SwipeLeft09Icon.js'),
  SwipeRight01Icon: () => import('./SwipeRight01Icon.js'),
  SwipeRight02Icon: () => import('./SwipeRight02Icon.js'),
  SwipeRight03Icon: () => import('./SwipeRight03Icon.js'),
  SwipeRight04Icon: () => import('./SwipeRight04Icon.js'),
  SwipeRight05Icon: () => import('./SwipeRight05Icon.js'),
  SwipeRight06Icon: () => import('./SwipeRight06Icon.js'),
  SwipeRight07Icon: () => import('./SwipeRight07Icon.js'),
  SwipeRight08Icon: () => import('./SwipeRight08Icon.js'),
  SwipeRight09Icon: () => import('./SwipeRight09Icon.js'),
  SwipeUp01Icon: () => import('./SwipeUp01Icon.js'),
  SwipeUp02Icon: () => import('./SwipeUp02Icon.js'),
  SwipeUp03Icon: () => import('./SwipeUp03Icon.js'),
  SwipeUp04Icon: () => import('./SwipeUp04Icon.js'),
  SwipeUp05Icon: () => import('./SwipeUp05Icon.js'),
  SwipeUp06Icon: () => import('./SwipeUp06Icon.js'),
  SwipeUp07Icon: () => import('./SwipeUp07Icon.js'),
  SwipeUp08Icon: () => import('./SwipeUp08Icon.js'),
  SwissFrancIcon: () => import('./SwissFrancIcon.js'),
  SwitchCameraIcon: () => import('./SwitchCameraIcon.js'),
  Sword01Icon: () => import('./Sword01Icon.js'),
  Sword02Icon: () => import('./Sword02Icon.js'),
  Sword03Icon: () => import('./Sword03Icon.js'),
  SystemUpdate01Icon: () => import('./SystemUpdate01Icon.js'),
  SystemUpdate02Icon: () => import('./SystemUpdate02Icon.js'),
  TShirtIcon: () => import('./TShirtIcon.js'),
  Table01Icon: () => import('./Table01Icon.js'),
  Table02Icon: () => import('./Table02Icon.js'),
  Table03Icon: () => import('./Table03Icon.js'),
  TableColumnsSplitIcon: () => import('./TableColumnsSplitIcon.js'),
  TableLamp01Icon: () => import('./TableLamp01Icon.js'),
  TableLamp02Icon: () => import('./TableLamp02Icon.js'),
  TableRoundIcon: () => import('./TableRoundIcon.js'),
  TableRowsSplitIcon: () => import('./TableRowsSplitIcon.js'),
  TableIcon: () => import('./TableIcon.js'),
  TableTennisBatIcon: () => import('./TableTennisBatIcon.js'),
  Tablet01Icon: () => import('./Tablet01Icon.js'),
  Tablet02Icon: () => import('./Tablet02Icon.js'),
  TabletConnectedBluetoothIcon: () => import('./TabletConnectedBluetoothIcon.js'),
  TabletConnectedUsbIcon: () => import('./TabletConnectedUsbIcon.js'),
  TabletConnectedWifiIcon: () => import('./TabletConnectedWifiIcon.js'),
  TabletPenIcon: () => import('./TabletPenIcon.js'),
  TabletsIcon: () => import('./TabletsIcon.js'),
  Taco01Icon: () => import('./Taco01Icon.js'),
  Taco02Icon: () => import('./Taco02Icon.js'),
  Tag01Icon: () => import('./Tag01Icon.js'),
  Tag02Icon: () => import('./Tag02Icon.js'),
  TagsIcon: () => import('./TagsIcon.js'),
  TailwindcssIcon: () => import('./TailwindcssIcon.js'),
  TajMahalIcon: () => import('./TajMahalIcon.js'),
  TallyOneIcon: () => import('./TallyOneIcon.js'),
  TallyTwoIcon: () => import('./TallyTwoIcon.js'),
  TallyThreeIcon: () => import('./TallyThreeIcon.js'),
  TallyFourIcon: () => import('./TallyFourIcon.js'),
  TallyFiveIcon: () => import('./TallyFiveIcon.js'),
  TanIcon: () => import('./TanIcon.js'),
  TangentIcon: () => import('./TangentIcon.js'),
  TankTopIcon: () => import('./TankTopIcon.js'),
  TankerTruckIcon: () => import('./TankerTruckIcon.js'),
  Tap01Icon: () => import('./Tap01Icon.js'),
  Tap02Icon: () => import('./Tap02Icon.js'),
  Tap03Icon: () => import('./Tap03Icon.js'),
  Tap04Icon: () => import('./Tap04Icon.js'),
  Tap05Icon: () => import('./Tap05Icon.js'),
  Tap06Icon: () => import('./Tap06Icon.js'),
  Tap07Icon: () => import('./Tap07Icon.js'),
  Tap08Icon: () => import('./Tap08Icon.js'),
  TapeMeasureIcon: () => import('./TapeMeasureIcon.js'),
  Target01Icon: () => import('./Target01Icon.js'),
  Target02Icon: () => import('./Target02Icon.js'),
  Target03Icon: () => import('./Target03Icon.js'),
  TargetDollarIcon: () => import('./TargetDollarIcon.js'),
  TasbihIcon: () => import('./TasbihIcon.js'),
  Task01Icon: () => import('./Task01Icon.js'),
  Task02Icon: () => import('./Task02Icon.js'),
  TaskAdd01Icon: () => import('./TaskAdd01Icon.js'),
  TaskAdd02Icon: () => import('./TaskAdd02Icon.js'),
  TaskDaily01Icon: () => import('./TaskDaily01Icon.js'),
  TaskDaily02Icon: () => import('./TaskDaily02Icon.js'),
  TaskDone01Icon: () => import('./TaskDone01Icon.js'),
  TaskDone02Icon: () => import('./TaskDone02Icon.js'),
  TaskEdit01Icon: () => import('./TaskEdit01Icon.js'),
  TaskEdit02Icon: () => import('./TaskEdit02Icon.js'),
  TaskRemove01Icon: () => import('./TaskRemove01Icon.js'),
  TaskRemove02Icon: () => import('./TaskRemove02Icon.js'),
  TaxesIcon: () => import('./TaxesIcon.js'),
  Taxi02Icon: () => import('./Taxi02Icon.js'),
  TaxiIcon: () => import('./TaxiIcon.js'),
  TeaPodIcon: () => import('./TeaPodIcon.js'),
  TeaIcon: () => import('./TeaIcon.js'),
  TeacherIcon: () => import('./TeacherIcon.js'),
  TeachingIcon: () => import('./TeachingIcon.js'),
  TeamWorkIcon: () => import('./TeamWorkIcon.js'),
  TeamviewerIcon: () => import('./TeamviewerIcon.js'),
  TelegramIcon: () => import('./TelegramIcon.js'),
  TelephoneIcon: () => import('./TelephoneIcon.js'),
  Telescope01Icon: () => import('./Telescope01Icon.js'),
  Telescope02Icon: () => import('./Telescope02Icon.js'),
  TelevisionTableIcon: () => import('./TelevisionTableIcon.js'),
  TemperatureIcon: () => import('./TemperatureIcon.js'),
  TennisBallIcon: () => import('./TennisBallIcon.js'),
  TennisRacketIcon: () => import('./TennisRacketIcon.js'),
  TentIcon: () => import('./TentIcon.js'),
  TentTreeIcon: () => import('./TentTreeIcon.js'),
  TerminalIcon: () => import('./TerminalIcon.js'),
  TerraceIcon: () => import('./TerraceIcon.js'),
  TestTube01Icon: () => import('./TestTube01Icon.js'),
  TestTube02Icon: () => import('./TestTube02Icon.js'),
  TestTube03Icon: () => import('./TestTube03Icon.js'),
  TestTubeDiagonalIcon: () => import('./TestTubeDiagonalIcon.js'),
  TestTubeIcon: () => import('./TestTubeIcon.js'),
  TestTubesIcon: () => import('./TestTubesIcon.js'),
  TetrisIcon: () => import('./TetrisIcon.js'),
  TextAlignCenterIcon: () => import('./TextAlignCenterIcon.js'),
  TextAlignJustifyCenterIcon: () => import('./TextAlignJustifyCenterIcon.js'),
  TextAlignJustifyLeftIcon: () => import('./TextAlignJustifyLeftIcon.js'),
  TextAlignJustifyRightIcon: () => import('./TextAlignJustifyRightIcon.js'),
  TextAlignLeft01Icon: () => import('./TextAlignLeft01Icon.js'),
  TextAlignLeftIcon: () => import('./TextAlignLeftIcon.js'),
  TextAlignRight01Icon: () => import('./TextAlignRight01Icon.js'),
  TextAlignRightIcon: () => import('./TextAlignRightIcon.js'),
  TextAllCapsIcon: () => import('./TextAllCapsIcon.js'),
  TextBoldIcon: () => import('./TextBoldIcon.js'),
  TextCenterlineCenterTopIcon: () => import('./TextCenterlineCenterTopIcon.js'),
  TextCenterlineLeftIcon: () => import('./TextCenterlineLeftIcon.js'),
  TextCenterlineMiddleIcon: () => import('./TextCenterlineMiddleIcon.js'),
  TextCenterlineRightIcon: () => import('./TextCenterlineRightIcon.js'),
  TextCheckIcon: () => import('./TextCheckIcon.js'),
  TextCircleIcon: () => import('./TextCircleIcon.js'),
  TextClearIcon: () => import('./TextClearIcon.js'),
  TextColorIcon: () => import('./TextColorIcon.js'),
  TextCreationIcon: () => import('./TextCreationIcon.js'),
  TextFirstlineLeftIcon: () => import('./TextFirstlineLeftIcon.js'),
  TextFirstlineRightIcon: () => import('./TextFirstlineRightIcon.js'),
  TextFontIcon: () => import('./TextFontIcon.js'),
  TextFootnoteIcon: () => import('./TextFootnoteIcon.js'),
  TextIndent01Icon: () => import('./TextIndent01Icon.js'),
  TextIndentLessIcon: () => import('./TextIndentLessIcon.js'),
  TextIndentMoreIcon: () => import('./TextIndentMoreIcon.js'),
  TextIndentIcon: () => import('./TextIndentIcon.js'),
  TextItalicSlashIcon: () => import('./TextItalicSlashIcon.js'),
  TextItalicIcon: () => import('./TextItalicIcon.js'),
  TextKerningIcon: () => import('./TextKerningIcon.js'),
  TextNumberSignIcon: () => import('./TextNumberSignIcon.js'),
  TextSelectIcon: () => import('./TextSelectIcon.js'),
  TextSelectionIcon: () => import('./TextSelectionIcon.js'),
  TextSmallcapsIcon: () => import('./TextSmallcapsIcon.js'),
  TextSquareIcon: () => import('./TextSquareIcon.js'),
  TextStrikethroughIcon: () => import('./TextStrikethroughIcon.js'),
  TextIcon: () => import('./TextIcon.js'),
  TextSubscriptIcon: () => import('./TextSubscriptIcon.js'),
  TextSuperscriptIcon: () => import('./TextSuperscriptIcon.js'),
  TextTrackingIcon: () => import('./TextTrackingIcon.js'),
  TextUnderlineIcon: () => import('./TextUnderlineIcon.js'),
  TextVariableFrontIcon: () => import('./TextVariableFrontIcon.js'),
  TextVerticalAlignmentIcon: () => import('./TextVerticalAlignmentIcon.js'),
  TextWrapIcon: () => import('./TextWrapIcon.js'),
  TheProphetsMosqueIcon: () => import('./TheProphetsMosqueIcon.js'),
  TheaterIcon: () => import('./TheaterIcon.js'),
  ThermometerColdIcon: () => import('./ThermometerColdIcon.js'),
  ThermometerIcon: () => import('./ThermometerIcon.js'),
  ThermometerWarmIcon: () => import('./ThermometerWarmIcon.js'),
  ThreadIcon: () => import('./ThreadIcon.js'),
  ThreadsEllipseIcon: () => import('./ThreadsEllipseIcon.js'),
  ThreadsRectangleIcon: () => import('./ThreadsRectangleIcon.js'),
  ThreadsIcon: () => import('./ThreadsIcon.js'),
  ThreeCircleIcon: () => import('./ThreeCircleIcon.js'),
  ThreeFinger01Icon: () => import('./ThreeFinger01Icon.js'),
  ThreeFinger02Icon: () => import('./ThreeFinger02Icon.js'),
  ThreeFinger03Icon: () => import('./ThreeFinger03Icon.js'),
  ThreeFinger04Icon: () => import('./ThreeFinger04Icon.js'),
  ThreeFinger05Icon: () => import('./ThreeFinger05Icon.js'),
  ThreeSquareIcon: () => import('./ThreeSquareIcon.js'),
  ThumbsDownEllipseIcon: () => import('./ThumbsDownEllipseIcon.js'),
  ThumbsDownRectangleIcon: () => import('./ThumbsDownRectangleIcon.js'),
  ThumbsDownIcon: () => import('./ThumbsDownIcon.js'),
  ThumbsUpDownIcon: () => import('./ThumbsUpDownIcon.js'),
  ThumbsUpEllipseIcon: () => import('./ThumbsUpEllipseIcon.js'),
  ThumbsUpRectangleIcon: () => import('./ThumbsUpRectangleIcon.js'),
  ThumbsUpIcon: () => import('./ThumbsUpIcon.js'),
  TicTacToeIcon: () => import('./TicTacToeIcon.js'),
  Tick01Icon: () => import('./Tick01Icon.js'),
  Tick02Icon: () => import('./Tick02Icon.js'),
  Tick03Icon: () => import('./Tick03Icon.js'),
  Tick04Icon: () => import('./Tick04Icon.js'),
  TickDouble01Icon: () => import('./TickDouble01Icon.js'),
  TickDouble02Icon: () => import('./TickDouble02Icon.js'),
  TickDouble03Icon: () => import('./TickDouble03Icon.js'),
  TickDouble04Icon: () => import('./TickDouble04Icon.js'),
  Ticket01Icon: () => import('./Ticket01Icon.js'),
  Ticket02Icon: () => import('./Ticket02Icon.js'),
  Ticket03Icon: () => import('./Ticket03Icon.js'),
  TicketStarIcon: () => import('./TicketStarIcon.js'),
  TieIcon: () => import('./TieIcon.js'),
  Tiff01Icon: () => import('./Tiff01Icon.js'),
  Tiff02Icon: () => import('./Tiff02Icon.js'),
  TiktokIcon: () => import('./TiktokIcon.js'),
  TiltifyIcon: () => import('./TiltifyIcon.js'),
  Time01Icon: () => import('./Time01Icon.js'),
  Time02Icon: () => import('./Time02Icon.js'),
  Time03Icon: () => import('./Time03Icon.js'),
  Time04Icon: () => import('./Time04Icon.js'),
  TimeHalfPassIcon: () => import('./TimeHalfPassIcon.js'),
  TimeManagementCircleIcon: () => import('./TimeManagementCircleIcon.js'),
  TimeManagementIcon: () => import('./TimeManagementIcon.js'),
  TimeQuarter02Icon: () => import('./TimeQuarter02Icon.js'),
  TimeQuarterPassIcon: () => import('./TimeQuarterPassIcon.js'),
  TimeQuarterIcon: () => import('./TimeQuarterIcon.js'),
  TimeScheduleIcon: () => import('./TimeScheduleIcon.js'),
  TimeSetting01Icon: () => import('./TimeSetting01Icon.js'),
  TimeSetting02Icon: () => import('./TimeSetting02Icon.js'),
  TimeSetting03Icon: () => import('./TimeSetting03Icon.js'),
  TimeZoneIcon: () => import('./TimeZoneIcon.js'),
  TimelineEventIcon: () => import('./TimelineEventIcon.js'),
  TimelineListIcon: () => import('./TimelineListIcon.js'),
  TimelineIcon: () => import('./TimelineIcon.js'),
  Timer01Icon: () => import('./Timer01Icon.js'),
  Timer02Icon: () => import('./Timer02Icon.js'),
  TipsIcon: () => import('./TipsIcon.js'),
  TireIcon: () => import('./TireIcon.js'),
  Tired01Icon: () => import('./Tired01Icon.js'),
  Tired02Icon: () => import('./Tired02Icon.js'),
  TissuePaperIcon: () => import('./TissuePaperIcon.js'),
  ToggleOffIcon: () => import('./ToggleOffIcon.js'),
  ToggleOnIcon: () => import('./ToggleOnIcon.js'),
  Toilet01Icon: () => import('./Toilet01Icon.js'),
  Toilet02Icon: () => import('./Toilet02Icon.js'),
  TokenCircleIcon: () => import('./TokenCircleIcon.js'),
  TokenSquareIcon: () => import('./TokenSquareIcon.js'),
  Tongue01Icon: () => import('./Tongue01Icon.js'),
  TongueIcon: () => import('./TongueIcon.js'),
  TongueWinkLeftIcon: () => import('./TongueWinkLeftIcon.js'),
  TongueWinkRightIcon: () => import('./TongueWinkRightIcon.js'),
  ToolCaseIcon: () => import('./ToolCaseIcon.js'),
  ToolboxIcon: () => import('./ToolboxIcon.js'),
  ToolsIcon: () => import('./ToolsIcon.js'),
  Tornado01Icon: () => import('./Tornado01Icon.js'),
  Tornado02Icon: () => import('./Tornado02Icon.js'),
  TorriGateIcon: () => import('./TorriGateIcon.js'),
  TorusIcon: () => import('./TorusIcon.js'),
  Touch01Icon: () => import('./Touch01Icon.js'),
  Touch02Icon: () => import('./Touch02Icon.js'),
  Touch03Icon: () => import('./Touch03Icon.js'),
  Touch04Icon: () => import('./Touch04Icon.js'),
  Touch05Icon: () => import('./Touch05Icon.js'),
  Touch06Icon: () => import('./Touch06Icon.js'),
  Touch07Icon: () => import('./Touch07Icon.js'),
  Touch08Icon: () => import('./Touch08Icon.js'),
  Touch09Icon: () => import('./Touch09Icon.js'),
  Touch10Icon: () => import('./Touch10Icon.js'),
  TouchInteraction01Icon: () => import('./TouchInteraction01Icon.js'),
  TouchInteraction02Icon: () => import('./TouchInteraction02Icon.js'),
  TouchInteraction03Icon: () => import('./TouchInteraction03Icon.js'),
  TouchInteraction04Icon: () => import('./TouchInteraction04Icon.js'),
  TouchLocked01Icon: () => import('./TouchLocked01Icon.js'),
  TouchLocked02Icon: () => import('./TouchLocked02Icon.js'),
  TouchLocked03Icon: () => import('./TouchLocked03Icon.js'),
  TouchLocked04Icon: () => import('./TouchLocked04Icon.js'),
  Touchpad01Icon: () => import('./Touchpad01Icon.js'),
  Touchpad02Icon: () => import('./Touchpad02Icon.js'),
  Touchpad03Icon: () => import('./Touchpad03Icon.js'),
  Touchpad04Icon: () => import('./Touchpad04Icon.js'),
  TouchpadOffIcon: () => import('./TouchpadOffIcon.js'),
  TowTruckIcon: () => import('./TowTruckIcon.js'),
  TowelsIcon: () => import('./TowelsIcon.js'),
  ToyBrickIcon: () => import('./ToyBrickIcon.js'),
  ToyTrainIcon: () => import('./ToyTrainIcon.js'),
  TractorIcon: () => import('./TractorIcon.js'),
  TradeDownIcon: () => import('./TradeDownIcon.js'),
  TradeMarkIcon: () => import('./TradeMarkIcon.js'),
  TradeUpIcon: () => import('./TradeUpIcon.js'),
  TrafficIncidentIcon: () => import('./TrafficIncidentIcon.js'),
  TrafficJam01Icon: () => import('./TrafficJam01Icon.js'),
  TrafficJam02Icon: () => import('./TrafficJam02Icon.js'),
  TrafficLightIcon: () => import('./TrafficLightIcon.js'),
  Train01Icon: () => import('./Train01Icon.js'),
  Train02Icon: () => import('./Train02Icon.js'),
  TrainTrackIcon: () => import('./TrainTrackIcon.js'),
  TramIcon: () => import('./TramIcon.js'),
  TrampolineIcon: () => import('./TrampolineIcon.js'),
  TransactionHistoryIcon: () => import('./TransactionHistoryIcon.js'),
  TransactionIcon: () => import('./TransactionIcon.js'),
  TransitionBottomIcon: () => import('./TransitionBottomIcon.js'),
  TransitionLeftIcon: () => import('./TransitionLeftIcon.js'),
  TransitionRightIcon: () => import('./TransitionRightIcon.js'),
  TransitionTopIcon: () => import('./TransitionTopIcon.js'),
  TranslateIcon: () => import('./TranslateIcon.js'),
  TranslationIcon: () => import('./TranslationIcon.js'),
  TransmissionIcon: () => import('./TransmissionIcon.js'),
  TransparencyIcon: () => import('./TransparencyIcon.js'),
  TrapezoidLineHorizontalIcon: () => import('./TrapezoidLineHorizontalIcon.js'),
  TrapezoidLineVerticalIcon: () => import('./TrapezoidLineVerticalIcon.js'),
  TravelBagIcon: () => import('./TravelBagIcon.js'),
  Treadmill01Icon: () => import('./Treadmill01Icon.js'),
  Treadmill02Icon: () => import('./Treadmill02Icon.js'),
  TreatmentIcon: () => import('./TreatmentIcon.js'),
  Tree01Icon: () => import('./Tree01Icon.js'),
  Tree02Icon: () => import('./Tree02Icon.js'),
  Tree03Icon: () => import('./Tree03Icon.js'),
  Tree04Icon: () => import('./Tree04Icon.js'),
  Tree05Icon: () => import('./Tree05Icon.js'),
  Tree06Icon: () => import('./Tree06Icon.js'),
  Tree07Icon: () => import('./Tree07Icon.js'),
  TreePalmIcon: () => import('./TreePalmIcon.js'),
  TreesIcon: () => import('./TreesIcon.js'),
  TrelloIcon: () => import('./TrelloIcon.js'),
  TrendingUpDownIcon: () => import('./TrendingUpDownIcon.js'),
  Triangle01Icon: () => import('./Triangle01Icon.js'),
  Triangle02Icon: () => import('./Triangle02Icon.js'),
  Triangle03Icon: () => import('./Triangle03Icon.js'),
  TriangleDashIcon: () => import('./TriangleDashIcon.js'),
  TriangleRightIcon: () => import('./TriangleRightIcon.js'),
  TriangleIcon: () => import('./TriangleIcon.js'),
  Trolley01Icon: () => import('./Trolley01Icon.js'),
  Trolley02Icon: () => import('./Trolley02Icon.js'),
  TropicalStormIcon: () => import('./TropicalStormIcon.js'),
  TropicalStormTracks01Icon: () => import('./TropicalStormTracks01Icon.js'),
  TropicalStormTracks02Icon: () => import('./TropicalStormTracks02Icon.js'),
  TruckDeliveryIcon: () => import('./TruckDeliveryIcon.js'),
  TruckMonsterIcon: () => import('./TruckMonsterIcon.js'),
  TruckReturnIcon: () => import('./TruckReturnIcon.js'),
  TruckIcon: () => import('./TruckIcon.js'),
  TrulliIcon: () => import('./TrulliIcon.js'),
  TsunamiIcon: () => import('./TsunamiIcon.js'),
  TulipIcon: () => import('./TulipIcon.js'),
  TumblrIcon: () => import('./TumblrIcon.js'),
  TurkishLiraIcon: () => import('./TurkishLiraIcon.js'),
  TurtleNeckIcon: () => import('./TurtleNeckIcon.js'),
  Tv01Icon: () => import('./Tv01Icon.js'),
  Tv02Icon: () => import('./Tv02Icon.js'),
  TvFixIcon: () => import('./TvFixIcon.js'),
  TvIssueIcon: () => import('./TvIssueIcon.js'),
  TvSmartIcon: () => import('./TvSmartIcon.js'),
  TwinTowerIcon: () => import('./TwinTowerIcon.js'),
  TwitchIcon: () => import('./TwitchIcon.js'),
  TwitterSquareIcon: () => import('./TwitterSquareIcon.js'),
  TwitterIcon: () => import('./TwitterIcon.js'),
  TwoCircleIcon: () => import('./TwoCircleIcon.js'),
  TwoFactorAccessIcon: () => import('./TwoFactorAccessIcon.js'),
  TwoFinger01Icon: () => import('./TwoFinger01Icon.js'),
  TwoFinger02Icon: () => import('./TwoFinger02Icon.js'),
  TwoFinger03Icon: () => import('./TwoFinger03Icon.js'),
  TwoFinger04Icon: () => import('./TwoFinger04Icon.js'),
  TwoFinger05Icon: () => import('./TwoFinger05Icon.js'),
  TwoSquareIcon: () => import('./TwoSquareIcon.js'),
  Txt01Icon: () => import('./Txt01Icon.js'),
  Txt02Icon: () => import('./Txt02Icon.js'),
  TypeCursorIcon: () => import('./TypeCursorIcon.js'),
  Typescript01Icon: () => import('./Typescript01Icon.js'),
  Typescript02Icon: () => import('./Typescript02Icon.js'),
  Typescript03Icon: () => import('./Typescript03Icon.js'),
  UberIcon: () => import('./UberIcon.js'),
  Ufo01Icon: () => import('./Ufo01Icon.js'),
  UfoIcon: () => import('./UfoIcon.js'),
  UmbrellaDollarIcon: () => import('./UmbrellaDollarIcon.js'),
  UmbrellaOffIcon: () => import('./UmbrellaOffIcon.js'),
  UmbrellaIcon: () => import('./UmbrellaIcon.js'),
  UnamusedIcon: () => import('./UnamusedIcon.js'),
  Unarchive03Icon: () => import('./Unarchive03Icon.js'),
  UnavailableIcon: () => import('./UnavailableIcon.js'),
  Underpants01Icon: () => import('./Underpants01Icon.js'),
  Underpants02Icon: () => import('./Underpants02Icon.js'),
  Underpants03Icon: () => import('./Underpants03Icon.js'),
  Undo02Icon: () => import('./Undo02Icon.js'),
  Undo03Icon: () => import('./Undo03Icon.js'),
  UndoIcon: () => import('./UndoIcon.js'),
  UnfoldLessIcon: () => import('./UnfoldLessIcon.js'),
  UnfoldMoreDownIcon: () => import('./UnfoldMoreDownIcon.js'),
  UnfoldMoreIcon: () => import('./UnfoldMoreIcon.js'),
  UnfoldMoreUpIcon: () => import('./UnfoldMoreUpIcon.js'),
  UngroupItemsIcon: () => import('./UngroupItemsIcon.js'),
  UngroupLayersIcon: () => import('./UngroupLayersIcon.js'),
  UngroupIcon: () => import('./UngroupIcon.js'),
  UnhappyIcon: () => import('./UnhappyIcon.js'),
  UniversalAccessCircleIcon: () => import('./UniversalAccessCircleIcon.js'),
  UniversalAccessIcon: () => import('./UniversalAccessIcon.js'),
  UniversityIcon: () => import('./UniversityIcon.js'),
  Unlink01Icon: () => import('./Unlink01Icon.js'),
  Unlink02Icon: () => import('./Unlink02Icon.js'),
  Unlink03Icon: () => import('./Unlink03Icon.js'),
  Unlink04Icon: () => import('./Unlink04Icon.js'),
  Unlink05Icon: () => import('./Unlink05Icon.js'),
  Unlink06Icon: () => import('./Unlink06Icon.js'),
  UnsplashIcon: () => import('./UnsplashIcon.js'),
  Upload01Icon: () => import('./Upload01Icon.js'),
  Upload02Icon: () => import('./Upload02Icon.js'),
  Upload03Icon: () => import('./Upload03Icon.js'),
  Upload04Icon: () => import('./Upload04Icon.js'),
  Upload05Icon: () => import('./Upload05Icon.js'),
  Upload06Icon: () => import('./Upload06Icon.js'),
  UploadCircle01Icon: () => import('./UploadCircle01Icon.js'),
  UploadCircle02Icon: () => import('./UploadCircle02Icon.js'),
  UploadSquare01Icon: () => import('./UploadSquare01Icon.js'),
  UploadSquare02Icon: () => import('./UploadSquare02Icon.js'),
  UpworkIcon: () => import('./UpworkIcon.js'),
  UsbBugsIcon: () => import('./UsbBugsIcon.js'),
  UsbConnected01Icon: () => import('./UsbConnected01Icon.js'),
  UsbConnected02Icon: () => import('./UsbConnected02Icon.js'),
  UsbConnected03Icon: () => import('./UsbConnected03Icon.js'),
  UsbErrorIcon: () => import('./UsbErrorIcon.js'),
  UsbMemory01Icon: () => import('./UsbMemory01Icon.js'),
  UsbMemory02Icon: () => import('./UsbMemory02Icon.js'),
  UsbNotConnected01Icon: () => import('./UsbNotConnected01Icon.js'),
  UsbNotConnected02Icon: () => import('./UsbNotConnected02Icon.js'),
  UsbIcon: () => import('./UsbIcon.js'),
  UsdtIcon: () => import('./UsdtIcon.js'),
  User02Icon: () => import('./User02Icon.js'),
  User03Icon: () => import('./User03Icon.js'),
  UserAccountIcon: () => import('./UserAccountIcon.js'),
  UserAdd01Icon: () => import('./UserAdd01Icon.js'),
  UserAdd02Icon: () => import('./UserAdd02Icon.js'),
  UserAiIcon: () => import('./UserAiIcon.js'),
  UserArrowLeftRightIcon: () => import('./UserArrowLeftRightIcon.js'),
  UserBlock01Icon: () => import('./UserBlock01Icon.js'),
  UserBlock02Icon: () => import('./UserBlock02Icon.js'),
  UserCheck01Icon: () => import('./UserCheck01Icon.js'),
  UserCheck02Icon: () => import('./UserCheck02Icon.js'),
  UserCircle02Icon: () => import('./UserCircle02Icon.js'),
  UserCircleIcon: () => import('./UserCircleIcon.js'),
  UserDollarIcon: () => import('./UserDollarIcon.js'),
  UserEdit01Icon: () => import('./UserEdit01Icon.js'),
  UserFullViewIcon: () => import('./UserFullViewIcon.js'),
  UserGroup02Icon: () => import('./UserGroup02Icon.js'),
  UserGroup03Icon: () => import('./UserGroup03Icon.js'),
  UserGroupIcon: () => import('./UserGroupIcon.js'),
  UserIdVerificationIcon: () => import('./UserIdVerificationIcon.js'),
  UserListIcon: () => import('./UserListIcon.js'),
  UserLock01Icon: () => import('./UserLock01Icon.js'),
  UserLock02Icon: () => import('./UserLock02Icon.js'),
  UserLove01Icon: () => import('./UserLove01Icon.js'),
  UserLove02Icon: () => import('./UserLove02Icon.js'),
  UserMinus01Icon: () => import('./UserMinus01Icon.js'),
  UserMinus02Icon: () => import('./UserMinus02Icon.js'),
  UserMultiple02Icon: () => import('./UserMultiple02Icon.js'),
  UserMultiple03Icon: () => import('./UserMultiple03Icon.js'),
  UserMultipleIcon: () => import('./UserMultipleIcon.js'),
  UserQuestion01Icon: () => import('./UserQuestion01Icon.js'),
  UserQuestion02Icon: () => import('./UserQuestion02Icon.js'),
  UserRemove01Icon: () => import('./UserRemove01Icon.js'),
  UserRemove02Icon: () => import('./UserRemove02Icon.js'),
  UserRoadsideIcon: () => import('./UserRoadsideIcon.js'),
  UserSearch01Icon: () => import('./UserSearch01Icon.js'),
  UserSearch02Icon: () => import('./UserSearch02Icon.js'),
  UserSettings01Icon: () => import('./UserSettings01Icon.js'),
  UserSettings02Icon: () => import('./UserSettings02Icon.js'),
  UserSharingIcon: () => import('./UserSharingIcon.js'),
  UserShield01Icon: () => import('./UserShield01Icon.js'),
  UserShield02Icon: () => import('./UserShield02Icon.js'),
  UserSquareIcon: () => import('./UserSquareIcon.js'),
  UserStar01Icon: () => import('./UserStar01Icon.js'),
  UserStar02Icon: () => import('./UserStar02Icon.js'),
  UserStatusIcon: () => import('./UserStatusIcon.js'),
  UserStoryIcon: () => import('./UserStoryIcon.js'),
  UserIcon: () => import('./UserIcon.js'),
  UserSwitchIcon: () => import('./UserSwitchIcon.js'),
  UserTime01Icon: () => import('./UserTime01Icon.js'),
  UserTime02Icon: () => import('./UserTime02Icon.js'),
  UserTime03Icon: () => import('./UserTime03Icon.js'),
  UserUnlock01Icon: () => import('./UserUnlock01Icon.js'),
  UserUnlock02Icon: () => import('./UserUnlock02Icon.js'),
  UserWarning01Icon: () => import('./UserWarning01Icon.js'),
  UserWarning02Icon: () => import('./UserWarning02Icon.js'),
  UserWarning03Icon: () => import('./UserWarning03Icon.js'),
  UtilityPoleIcon: () => import('./UtilityPoleIcon.js'),
  Uv01Icon: () => import('./Uv01Icon.js'),
  Uv02Icon: () => import('./Uv02Icon.js'),
  Uv03Icon: () => import('./Uv03Icon.js'),
  VaccineIcon: () => import('./VaccineIcon.js'),
  VacuumCleanerIcon: () => import('./VacuumCleanerIcon.js'),
  ValidationApprovalIcon: () => import('./ValidationApprovalIcon.js'),
  ValidationIcon: () => import('./ValidationIcon.js'),
  VanIcon: () => import('./VanIcon.js'),
  VariableIcon: () => import('./VariableIcon.js'),
  VectorSquareIcon: () => import('./VectorSquareIcon.js'),
  VegetarianFoodIcon: () => import('./VegetarianFoodIcon.js'),
  VenetianMaskIcon: () => import('./VenetianMaskIcon.js'),
  VersusIcon: () => import('./VersusIcon.js'),
  VerticalResizeIcon: () => import('./VerticalResizeIcon.js'),
  VerticalScrollPointIcon: () => import('./VerticalScrollPointIcon.js'),
  VestIcon: () => import('./VestIcon.js'),
  ViberIcon: () => import('./ViberIcon.js'),
  VibrateIcon: () => import('./VibrateIcon.js'),
  VictoryFinger01Icon: () => import('./VictoryFinger01Icon.js'),
  VictoryFinger02Icon: () => import('./VictoryFinger02Icon.js'),
  VictoryFinger03Icon: () => import('./VictoryFinger03Icon.js'),
  Video01Icon: () => import('./Video01Icon.js'),
  Video02Icon: () => import('./Video02Icon.js'),
  VideoAiIcon: () => import('./VideoAiIcon.js'),
  VideoCameraAiIcon: () => import('./VideoCameraAiIcon.js'),
  VideoConsoleIcon: () => import('./VideoConsoleIcon.js'),
  VideoOffIcon: () => import('./VideoOffIcon.js'),
  VideoReplayIcon: () => import('./VideoReplayIcon.js'),
  VideotapeIcon: () => import('./VideotapeIcon.js'),
  ViewAgendaIcon: () => import('./ViewAgendaIcon.js'),
  ViewOffSlashIcon: () => import('./ViewOffSlashIcon.js'),
  ViewOffIcon: () => import('./ViewOffIcon.js'),
  ViewSidebarLeftIcon: () => import('./ViewSidebarLeftIcon.js'),
  ViewSidebarRightIcon: () => import('./ViewSidebarRightIcon.js'),
  ViewIcon: () => import('./ViewIcon.js'),
  Villa01Icon: () => import('./Villa01Icon.js'),
  Villa02Icon: () => import('./Villa02Icon.js'),
  VimeoIcon: () => import('./VimeoIcon.js'),
  VineSquareIcon: () => import('./VineSquareIcon.js'),
  VineIcon: () => import('./VineIcon.js'),
  VintageClockIcon: () => import('./VintageClockIcon.js'),
  VirtualRealityVr01Icon: () => import('./VirtualRealityVr01Icon.js'),
  VirtualRealityVr02Icon: () => import('./VirtualRealityVr02Icon.js'),
  VisionIcon: () => import('./VisionIcon.js'),
  VisualStudioCodeIcon: () => import('./VisualStudioCodeIcon.js'),
  VkSquareIcon: () => import('./VkSquareIcon.js'),
  VkIcon: () => import('./VkIcon.js'),
  VoiceIdIcon: () => import('./VoiceIdIcon.js'),
  VoiceIcon: () => import('./VoiceIcon.js'),
  VolleyballIcon: () => import('./VolleyballIcon.js'),
  VolumeHighIcon: () => import('./VolumeHighIcon.js'),
  VolumeLowIcon: () => import('./VolumeLowIcon.js'),
  VolumeMinusIcon: () => import('./VolumeMinusIcon.js'),
  VolumeMute01Icon: () => import('./VolumeMute01Icon.js'),
  VolumeMute02Icon: () => import('./VolumeMute02Icon.js'),
  VolumeOffIcon: () => import('./VolumeOffIcon.js'),
  VolumeUpIcon: () => import('./VolumeUpIcon.js'),
  VomitingIcon: () => import('./VomitingIcon.js'),
  VoucherIcon: () => import('./VoucherIcon.js'),
  VrGlassesIcon: () => import('./VrGlassesIcon.js'),
  VrIcon: () => import('./VrIcon.js'),
  Vynil01Icon: () => import('./Vynil01Icon.js'),
  Vynil02Icon: () => import('./Vynil02Icon.js'),
  Vynil03Icon: () => import('./Vynil03Icon.js'),
  WThreeSchoolsIcon: () => import('./WThreeSchoolsIcon.js'),
  WaiterIcon: () => import('./WaiterIcon.js'),
  WaitersIcon: () => import('./WaitersIcon.js'),
  WalkingIcon: () => import('./WalkingIcon.js'),
  WallLampIcon: () => import('./WallLampIcon.js'),
  Wallet01Icon: () => import('./Wallet01Icon.js'),
  Wallet02Icon: () => import('./Wallet02Icon.js'),
  Wallet03Icon: () => import('./Wallet03Icon.js'),
  Wallet04Icon: () => import('./Wallet04Icon.js'),
  Wallet05Icon: () => import('./Wallet05Icon.js'),
  WalletAdd01Icon: () => import('./WalletAdd01Icon.js'),
  WalletAdd02Icon: () => import('./WalletAdd02Icon.js'),
  WalletCardsIcon: () => import('./WalletCardsIcon.js'),
  WalletDone01Icon: () => import('./WalletDone01Icon.js'),
  WalletDone02Icon: () => import('./WalletDone02Icon.js'),
  WalletNotFound01Icon: () => import('./WalletNotFound01Icon.js'),
  WalletNotFound02Icon: () => import('./WalletNotFound02Icon.js'),
  WalletRemove01Icon: () => import('./WalletRemove01Icon.js'),
  WalletRemove02Icon: () => import('./WalletRemove02Icon.js'),
  WalpaperIcon: () => import('./WalpaperIcon.js'),
  WantedIcon: () => import('./WantedIcon.js'),
  Wardrobe01Icon: () => import('./Wardrobe01Icon.js'),
  Wardrobe02Icon: () => import('./Wardrobe02Icon.js'),
  Wardrobe03Icon: () => import('./Wardrobe03Icon.js'),
  Wardrobe04Icon: () => import('./Wardrobe04Icon.js'),
  WarehouseIcon: () => import('./WarehouseIcon.js'),
  WashingMachineIcon: () => import('./WashingMachineIcon.js'),
  WashingtonMonumentIcon: () => import('./WashingtonMonumentIcon.js'),
  WasteRestoreIcon: () => import('./WasteRestoreIcon.js'),
  WasteIcon: () => import('./WasteIcon.js'),
  Watch01Icon: () => import('./Watch01Icon.js'),
  Watch02Icon: () => import('./Watch02Icon.js'),
  WaterEnergyIcon: () => import('./WaterEnergyIcon.js'),
  WaterPoloIcon: () => import('./WaterPoloIcon.js'),
  WaterPumpIcon: () => import('./WaterPumpIcon.js'),
  WaterfallDown01Icon: () => import('./WaterfallDown01Icon.js'),
  WaterfallDown03Icon: () => import('./WaterfallDown03Icon.js'),
  WaterfallUp01Icon: () => import('./WaterfallUp01Icon.js'),
  WaterfallUp02Icon: () => import('./WaterfallUp02Icon.js'),
  WatermelonIcon: () => import('./WatermelonIcon.js'),
  WattpadSquareIcon: () => import('./WattpadSquareIcon.js'),
  WattpadIcon: () => import('./WattpadIcon.js'),
  Wav01Icon: () => import('./Wav01Icon.js'),
  Wav02Icon: () => import('./Wav02Icon.js'),
  WaveSquareIcon: () => import('./WaveSquareIcon.js'),
  WaveIcon: () => import('./WaveIcon.js'),
  WaveTriangleIcon: () => import('./WaveTriangleIcon.js'),
  WavingHand01Icon: () => import('./WavingHand01Icon.js'),
  WavingHand02Icon: () => import('./WavingHand02Icon.js'),
  WazeIcon: () => import('./WazeIcon.js'),
  WebDesign01Icon: () => import('./WebDesign01Icon.js'),
  WebDesign02Icon: () => import('./WebDesign02Icon.js'),
  WebProgrammingIcon: () => import('./WebProgrammingIcon.js'),
  WebProtectionIcon: () => import('./WebProtectionIcon.js'),
  WebSecurityIcon: () => import('./WebSecurityIcon.js'),
  WebValidationIcon: () => import('./WebValidationIcon.js'),
  WebflowEllipseIcon: () => import('./WebflowEllipseIcon.js'),
  WebflowRectangleIcon: () => import('./WebflowRectangleIcon.js'),
  WebflowIcon: () => import('./WebflowIcon.js'),
  WebhookOffIcon: () => import('./WebhookOffIcon.js'),
  WebhookIcon: () => import('./WebhookIcon.js'),
  WechatIcon: () => import('./WechatIcon.js'),
  WeddingIcon: () => import('./WeddingIcon.js'),
  WeightScale01Icon: () => import('./WeightScale01Icon.js'),
  WeightScaleIcon: () => import('./WeightScaleIcon.js'),
  WeightIcon: () => import('./WeightIcon.js'),
  WellnessIcon: () => import('./WellnessIcon.js'),
  WhatsappBusinessIcon: () => import('./WhatsappBusinessIcon.js'),
  WhatsappIcon: () => import('./WhatsappIcon.js'),
  WheatOffIcon: () => import('./WheatOffIcon.js'),
  WheatIcon: () => import('./WheatIcon.js'),
  WheelchairIcon: () => import('./WheelchairIcon.js'),
  WhiskIcon: () => import('./WhiskIcon.js'),
  WhistleIcon: () => import('./WhistleIcon.js'),
  WhiteboardIcon: () => import('./WhiteboardIcon.js'),
  Wifi01Icon: () => import('./Wifi01Icon.js'),
  Wifi02Icon: () => import('./Wifi02Icon.js'),
  WifiCircleIcon: () => import('./WifiCircleIcon.js'),
  WifiConnected01Icon: () => import('./WifiConnected01Icon.js'),
  WifiConnected02Icon: () => import('./WifiConnected02Icon.js'),
  WifiConnected03Icon: () => import('./WifiConnected03Icon.js'),
  WifiDisconnected01Icon: () => import('./WifiDisconnected01Icon.js'),
  WifiDisconnected02Icon: () => import('./WifiDisconnected02Icon.js'),
  WifiDisconnected03Icon: () => import('./WifiDisconnected03Icon.js'),
  WifiDisconnected04Icon: () => import('./WifiDisconnected04Icon.js'),
  WifiError01Icon: () => import('./WifiError01Icon.js'),
  WifiError02Icon: () => import('./WifiError02Icon.js'),
  WifiFullSignalIcon: () => import('./WifiFullSignalIcon.js'),
  WifiLocationIcon: () => import('./WifiLocationIcon.js'),
  WifiLockIcon: () => import('./WifiLockIcon.js'),
  WifiLowSignalIcon: () => import('./WifiLowSignalIcon.js'),
  WifiMediumSignalIcon: () => import('./WifiMediumSignalIcon.js'),
  WifiNoSignalIcon: () => import('./WifiNoSignalIcon.js'),
  WifiOff01Icon: () => import('./WifiOff01Icon.js'),
  WifiOff02Icon: () => import('./WifiOff02Icon.js'),
  WifiPenIcon: () => import('./WifiPenIcon.js'),
  WifiSquareIcon: () => import('./WifiSquareIcon.js'),
  WifiSyncIcon: () => import('./WifiSyncIcon.js'),
  WifiUnlockIcon: () => import('./WifiUnlockIcon.js'),
  WikipediaIcon: () => import('./WikipediaIcon.js'),
  WindPower01Icon: () => import('./WindPower01Icon.js'),
  WindPower02Icon: () => import('./WindPower02Icon.js'),
  WindPower03Icon: () => import('./WindPower03Icon.js'),
  WindPowerIcon: () => import('./WindPowerIcon.js'),
  WindSurfIcon: () => import('./WindSurfIcon.js'),
  WindTurbineIcon: () => import('./WindTurbineIcon.js'),
  WindowsNewIcon: () => import('./WindowsNewIcon.js'),
  WindowsOldIcon: () => import('./WindowsOldIcon.js'),
  WinkIcon: () => import('./WinkIcon.js'),
  WinkingIcon: () => import('./WinkingIcon.js'),
  WirelessCloudAccessIcon: () => import('./WirelessCloudAccessIcon.js'),
  WirelessIcon: () => import('./WirelessIcon.js'),
  WiseIcon: () => import('./WiseIcon.js'),
  WomanIcon: () => import('./WomanIcon.js'),
  WordpressIcon: () => import('./WordpressIcon.js'),
  WorkAlertIcon: () => import('./WorkAlertIcon.js'),
  WorkHistoryIcon: () => import('./WorkHistoryIcon.js'),
  WorkIcon: () => import('./WorkIcon.js'),
  WorkUpdateIcon: () => import('./WorkUpdateIcon.js'),
  WorkflowCircle01Icon: () => import('./WorkflowCircle01Icon.js'),
  WorkflowCircle02Icon: () => import('./WorkflowCircle02Icon.js'),
  WorkflowCircle03Icon: () => import('./WorkflowCircle03Icon.js'),
  WorkflowCircle04Icon: () => import('./WorkflowCircle04Icon.js'),
  WorkflowCircle05Icon: () => import('./WorkflowCircle05Icon.js'),
  WorkflowCircle06Icon: () => import('./WorkflowCircle06Icon.js'),
  WorkflowSquare01Icon: () => import('./WorkflowSquare01Icon.js'),
  WorkflowSquare02Icon: () => import('./WorkflowSquare02Icon.js'),
  WorkflowSquare03Icon: () => import('./WorkflowSquare03Icon.js'),
  WorkflowSquare04Icon: () => import('./WorkflowSquare04Icon.js'),
  WorkflowSquare05Icon: () => import('./WorkflowSquare05Icon.js'),
  WorkflowSquare06Icon: () => import('./WorkflowSquare06Icon.js'),
  WorkflowSquare07Icon: () => import('./WorkflowSquare07Icon.js'),
  WorkflowSquare08Icon: () => import('./WorkflowSquare08Icon.js'),
  WorkflowSquare09Icon: () => import('./WorkflowSquare09Icon.js'),
  WorkflowSquare10Icon: () => import('./WorkflowSquare10Icon.js'),
  WorkoutBattleRopesIcon: () => import('./WorkoutBattleRopesIcon.js'),
  WorkoutGymnasticsIcon: () => import('./WorkoutGymnasticsIcon.js'),
  WorkoutKickingIcon: () => import('./WorkoutKickingIcon.js'),
  WorkoutRunIcon: () => import('./WorkoutRunIcon.js'),
  WorkoutSportIcon: () => import('./WorkoutSportIcon.js'),
  WorkoutSquatsIcon: () => import('./WorkoutSquatsIcon.js'),
  WorkoutStretchingIcon: () => import('./WorkoutStretchingIcon.js'),
  WorkoutWarmUpIcon: () => import('./WorkoutWarmUpIcon.js'),
  WorryIcon: () => import('./WorryIcon.js'),
  WpsOfficeRectangleIcon: () => import('./WpsOfficeRectangleIcon.js'),
  WpsOfficeIcon: () => import('./WpsOfficeIcon.js'),
  Wrench01Icon: () => import('./Wrench01Icon.js'),
  Wrench02Icon: () => import('./Wrench02Icon.js'),
  WuduIcon: () => import('./WuduIcon.js'),
  XRayIcon: () => import('./XRayIcon.js'),
  XVariableCircleIcon: () => import('./XVariableCircleIcon.js'),
  XVariableSquareIcon: () => import('./XVariableSquareIcon.js'),
  XVariableIcon: () => import('./XVariableIcon.js'),
  XingIcon: () => import('./XingIcon.js'),
  Xls01Icon: () => import('./Xls01Icon.js'),
  Xls02Icon: () => import('./Xls02Icon.js'),
  Xml01Icon: () => import('./Xml01Icon.js'),
  Xml02Icon: () => import('./Xml02Icon.js'),
  Xsl01Icon: () => import('./Xsl01Icon.js'),
  Xsl02Icon: () => import('./Xsl02Icon.js'),
  YelpIcon: () => import('./YelpIcon.js'),
  YenCircleIcon: () => import('./YenCircleIcon.js'),
  YenReceiveIcon: () => import('./YenReceiveIcon.js'),
  YenSendIcon: () => import('./YenSendIcon.js'),
  YenSquareIcon: () => import('./YenSquareIcon.js'),
  YenIcon: () => import('./YenIcon.js'),
  Yoga01Icon: () => import('./Yoga01Icon.js'),
  Yoga02Icon: () => import('./Yoga02Icon.js'),
  Yoga03Icon: () => import('./Yoga03Icon.js'),
  YogaBallIcon: () => import('./YogaBallIcon.js'),
  YogaMatIcon: () => import('./YogaMatIcon.js'),
  YogurtIcon: () => import('./YogurtIcon.js'),
  YoutubeIcon: () => import('./YoutubeIcon.js'),
  YurtIcon: () => import('./YurtIcon.js'),
  ZakatIcon: () => import('./ZakatIcon.js'),
  ZapIcon: () => import('./ZapIcon.js'),
  ZeppelinIcon: () => import('./ZeppelinIcon.js'),
  ZeroCircleIcon: () => import('./ZeroCircleIcon.js'),
  ZeroSquareIcon: () => import('./ZeroSquareIcon.js'),
  Zip01Icon: () => import('./Zip01Icon.js'),
  Zip02Icon: () => import('./Zip02Icon.js'),
  ZoomCircleIcon: () => import('./ZoomCircleIcon.js'),
  ZoomInAreaIcon: () => import('./ZoomInAreaIcon.js'),
  ZoomOutAreaIcon: () => import('./ZoomOutAreaIcon.js'),
  ZoomSquareIcon: () => import('./ZoomSquareIcon.js'),
  ZoomIcon: () => import('./ZoomIcon.js'),
  ZshIcon: () => import('./ZshIcon.js'),
  ZzzIcon: () => import('./ZzzIcon.js'),
};

// In-memory cache
const iconCache = new Map<string, IconSvgObject>();

export async function loadIcon(iconName: string): Promise<IconSvgObject> {
  if (iconCache.has(iconName)) return iconCache.get(iconName)!;
  const fn = imports[iconName];
  if (!fn) throw new Error(`Icon not found: ${iconName}`);
  const mod = await fn();
  const iconData = mod.default || mod;
  iconCache.set(iconName, iconData);
  return iconData;
}

export async function loadIcons(iconNames: string[]): Promise<IconSvgObject[]> {
  return Promise.all(iconNames.map(loadIcon));
}

export function iconExists(iconName: string): boolean {
  return !!imports[iconName];
}

export function clearIconCache(): void {
  iconCache.clear();
}

export function getCacheSize(): number {
  return iconCache.size;
}

// Style-specific aliases for multi-package usage
export const loadIconStrokeRounded = loadIcon;
export const loadIconsStrokeRounded = loadIcons;

export default { loadIcon, loadIcons, iconExists, clearIconCache, getCacheSize };
